export type Language = 'en' | 'de';

export const translations: Record<Language, Record<string, string>> = {
  en: {
    'publisherCalendar.subscribe': 'Subscribe',
    'publisherCalendar.title': 'Personal calendar',
    'publisherCalendar.link': 'Subscription link',
    'publisherCalendar.loading': 'Loading calendar link…',
    'publisherCalendar.create': 'Create subscription link',
    'publisherCalendar.copy': 'Copy link',
    'publisherCalendar.copied': 'Link copied',
    'publisherCalendar.copyFailed': 'The link could not be copied. Select and copy it below.',
    'publisherCalendar.rotate': 'Regenerate link',
    'publisherCalendar.revoke': 'Revoke link',
    'publisherCalendar.confirmRotate':
      'Regenerate this link? Existing subscriptions will stop updating. Share the new link to subscribe again.',
    'publisherCalendar.confirmRevoke':
      'Revoke this link? Existing subscriptions will stop updating.',
    'publisherCalendar.confirm': 'Confirm',
    'publisherCalendar.cancel': 'Cancel',
    'publisherCalendar.empty': 'No subscription link has been created.',
    'publisherCalendar.revoked': 'This subscription link has been revoked.',
    'publisherCalendar.failed':
      'The calendar link could not be loaded or changed. Reload it and try again.',
    'publisherCalendar.retry': 'Reload link',
    'publisherCalendar.guidance':
      'Includes published LaMM, service and field service conducting assignments. Anyone with this link can read the calendar. Your calendar app controls when updates appear.',
    'fieldService.meetingType': 'Meeting type',
    'fieldService.ordinary': 'Congregation field service meeting',
    'fieldService.groups': 'Field Service Groups',
    'fieldService.notes': 'Notes (optional)',
    'fieldService.addNotes': 'Notes',
    'fieldService.notesPresent': 'Notes present',
    'fieldService.invalidMeeting':
      'Enter a valid start time for each ordinary meeting and limit notes to 500 characters.',

    'absenceConflicts.title': 'Conflicts',
    'absenceConflicts.countOne': '1 affected person',
    'absenceConflicts.count': '{{count}} affected people',
    'absenceConflicts.loading': 'Loading conflicts…',
    'absenceConflicts.empty': 'No overlapping absences.',
    'absenceConflicts.loadError': 'Conflicts could not be loaded. Please try again.',
    'absenceConflicts.edit': 'Edit absence for {{name}}, {{dates}}',
    'absenceConflicts.delete': 'Delete absence for {{name}}, {{dates}}',
    'absenceConflicts.deleteConfirm':
      'Delete the absence for {{name}} ({{dates}})? This cannot be undone.',
    'fieldService.assignmentRemoved': 'This conductor assignment will no longer be active.',
    'fieldService.weekCountOne': '1 meeting · {{assigned}} assigned',
    'fieldService.title': 'Field service meetings',
    'fieldService.reviewPeriod': 'Review period',
    'fieldService.releasePeriod': 'Release period',
    'fieldService.unlockPeriod': 'Unlock period',
    'fieldService.releasedPeriod': 'Period released',
    'fieldService.draftPeriod': 'Period in draft',
    'fieldService.revision': 'Revision {{revision}}',
    'fieldService.saveBeforeRelease': 'Save or discard changes before reviewing the period.',
    'fieldService.releaseFailed': 'The release could not be completed. Please try again.',
    'fieldService.releaseStale': 'The period changed. Reload and review it again.',
    'fieldService.releaseOverlap':
      'A shared week differs from another released period. Unlock that period first.',
    'fieldService.releaseLocked':
      'This week belongs to a released period. Unlock its release before editing.',
    'fieldService.unlockConfirm':
      'Unlock this period for editing? Its public weeks are withdrawn unless another released period still owns them.',
    'fieldService.reviewCount': '{{count}} weeks in this period',
    'fieldService.saveWeekFirst': 'Save this week before releasing the period.',
    'fieldService.reviewInvalidWeek': 'Check the saved assignments for this week.',
    'fieldService.missingConductor': 'Conductor not selected',
    'fieldService.missingPlace': 'Place not selected',
    'fieldService.releaseConfirm':
      'Release this reviewed period, including any unassigned meetings?',
    'fieldService.help': 'Plan meetings and manually choose conductors week by week.',
    'fieldService.eligible': 'Eligible to conduct field service meetings',
    'fieldService.blueprint': 'Field service weekly blueprint',
    'fieldService.placeNotSelected': 'Place not selected',
    'fieldService.blueprintHelp':
      'Define weekdays and times here. Choose the place and conductor for each dated meeting in Field service. Planned weeks retain their saved choices.',
    'fieldService.noBlueprint': 'No recurring field service meetings configured.',
    'fieldService.place': 'Place',
    'fieldService.searchPlaces': 'Search places',
    'fieldService.selectPlace': 'Select a place',
    'fieldService.selectedPlace': 'Selected place',
    'fieldService.addPlace': 'Add place',
    'fieldService.noMatches': 'No matching places.',
    'fieldService.noRecentPlaces': 'No recently used places.',
    'fieldService.noPlaces': 'No saved places.',
    'fieldService.name': 'Place name',
    'fieldService.address': 'Address (optional)',
    'fieldService.directions': 'Note (optional)',
    'fieldService.latitude': 'Latitude (optional)',
    'fieldService.longitude': 'Longitude (optional)',
    'fieldService.optionalPlaceData':
      'Only the place name is required. Coordinates must be entered as a pair.',
    'fieldService.reusePlace': 'Use {{name}}',
    'fieldService.duplicatePlace':
      'This place already exists. Search for and select the existing place.',
    'fieldService.coordinatesInvalid': 'Enter both latitude and longitude, or leave both empty.',
    'fieldService.loadFailed': 'Could not load field service data. Please retry.',
    'fieldService.saveFailed':
      'Could not save. Your changes are still here; please check the entries and retry.',
    'fieldService.stale':
      'The week or defaults changed. Refresh, then explicitly load the saved week before reviewing your changes again.',
    'fieldService.unavailable':
      'The conductor is no longer eligible or available on this date. Please review or clear the choice.',
    'fieldService.saved': 'Week saved.',
    'fieldService.noMeetings': 'No field service meetings this week.',
    'fieldService.weekCount': '{{count}} meetings · {{assigned}} assigned',
    'fieldService.conductor': 'Conductor',
    'fieldService.chooseConductor': 'Choose conductor',
    'fieldService.selectedConductor': 'Selected conductor',
    'fieldService.unavailableShort': 'Unavailable',
    'fieldService.history': 'Assignment history',
    'fieldService.placesFailed': 'Places unavailable.',
    'fieldService.useConductor': 'Use selected conductor',
    'fieldService.clear': 'Clear conductor',
    'fieldService.date': 'Date',
    'fieldService.addMeeting': 'Add meeting',
    'fieldService.removeMeeting': 'Remove meeting',
    'fieldService.cancelMeeting': 'Cancel meeting',
    'fieldService.restoreMeeting': 'Restore meeting',
    'fieldService.cancelled': 'Cancelled',
    'fieldService.configureWeek': 'Week configuration',
    'fieldService.saveWeek': 'Save week',
    'fieldService.applyBlueprint': 'Apply current blueprint',
    'fieldService.previewHelp':
      'Review the changes before saving. Local additions and cancellations are retained. Conductors are never selected automatically.',
    'fieldService.historyFailed': 'Assignment history unavailable.',
    'fieldService.periodCount': '{{count}} assignments in this period',
    'fieldService.conditional': 'Conditional availability · contact first.',
    'fieldService.noCandidates': 'No eligible, available conductors on this date.',
    'fieldService.noHistory': 'No other assignments.',
    'fieldService.before': 'Before',
    'fieldService.after': 'After',
    'fieldService.keepMeeting': 'Keep this meeting active',
    'fieldService.acknowledgeLoss':
      'I acknowledge that cancelling these meetings removes their active conductor assignments.',
    'fieldService.usePreview': 'Use preview in week draft',
    'fieldService.useRefreshedWeek': 'Load refreshed saved week (discard draft)',
    'fieldService.timezone': 'Local times: {{timezone}}',
    'nav.fieldService': 'Field service',
    'appRole.read_only': 'Read only',
    'weeklyCopy.settings': 'Copy settings',
    'weeklyCopy.format': 'Weekly summary format',
    'weeklyCopy.rtf': 'RTF (rich text)',
    'weeklyCopy.markdown': 'Markdown',
    'weeklyCopy.whatsapp': 'WhatsApp',
    'weeklyCopy.formatHelp':
      'Used for the LaMM and Services copy buttons. Saved for your account across devices.',
    'weeklyCopy.richHelp':
      'Rich text keeps bold labels when supported by your browser and the app you paste into.',
    'weeklyCopy.loadFailed': 'Your copy format could not be loaded. Please try again.',
    'weeklyCopy.saveFailed':
      'Your copy format could not be saved. Your choice is still here; please try again.',
    'weeklyCopy.copy': 'Copy {{section}} summary',
    'weeklyCopy.button': 'Copy',
    'weeklyCopy.copied': 'Copied',
    'weeklyCopy.failed': 'Copy failed. Select the summary below and copy it manually.',
    'weeklyCopy.plainFallback': 'This is plain text. Bold formatting is unavailable here.',
    'weeklyCopy.summary': 'Summary to copy',

    'message.settings': 'Message template',
    'message.language': 'Message language',
    'message.german': 'German',
    'message.english': 'English',
    'message.languageHelp':
      'Used for dates and responsibility names. Your wording stays unchanged.',
    'message.template': 'Your message',
    'message.variables': 'Insert a variable',
    'message.variable.firstname': 'First name',
    'message.variable.assignment': 'Assignment',
    'message.variable.date': 'Date',
    'message.preview': 'Example preview',
    'message.previewUnavailable': 'Enter a valid template to see a preview.',
    'message.invalid':
      'Enter 1–2000 characters. Supported variables: {{firstname}}, {{assignment}}, {{date}}. Check for incomplete or unknown variables.',
    'message.saveFailed':
      'The template could not be saved. Your changes are still here; please try again.',
    'message.reset': 'Reset to default',
    'message.cancel': 'Cancel',
    'message.save': 'Save',
    'message.saving': 'Saving…',
    'message.loading': 'Loading your message template…',
    'message.loadFailed':
      'Your message template could not be loaded. Retry before preparing a message.',
    'message.retry': 'Retry',
    'message.prepare': 'Prepare a message',
    'message.recipient': 'Message to',
    'message.choosePerson': 'Choose a person',
    'message.share': 'Share message',
    'message.selectFor': 'Select {{name}}',
    'message.copyFor': 'Copy message for {{name}}',
    'message.shareFor': 'Share message for {{name}}',
    'message.copy': 'Copy message',
    'message.copied': 'Copied',
    'message.shareFailed': 'Sharing was unavailable. You can copy the message instead.',
    'message.copyFailed': 'Copying was unavailable. Select and copy this message manually.',
    'message.cannotPrepare':
      'The message cannot be prepared. Check the template and the candidate’s first name.',
    'message.default':
      'Hello {{firstname}},\ncould you take over {{assignment}} on {{date}}?\nBest regards\n{{sender}}',
    'loading.refreshing': 'Refreshing…',
    'loading.stale': 'Displayed information is out of date. Retry before editing.',
    'loading.codeFailed':
      'This part of the application could not be loaded. Retry without leaving your work.',

    'assignment.open': 'Assign someone',
    'assignment.title': 'Assign someone',
    'assignment.confirm': 'Assign: {{name}}',
    'assignment.undo': 'Undo assignment',
    'assignment.empty': 'No eligible, available people were found for this responsibility.',
    'assignment.saveFailed':
      'The assignment could not be saved. Reload the comparison to check the current assignment before trying again.',
    'assignment.messageDefault':
      'Hello {{firstname}},\ncould you handle {{assignment}} on {{date}}?\nBest regards\n{{sender}}',
    'replacement.confirm': 'Confirm replacement: {{name}}',
    'replacement.undo': 'Undo latest replacement',
    'replacement.saving': 'Saving…',
    'replacement.saveFailed':
      'The replacement could not be confirmed. Reload the comparison to check the current assignment before trying again.',
    'replacement.conditionalAvailability':
      '{{name}} is conditionally available. Contact them before assigning this responsibility.',
    'replacement.replacedOnDate':
      '{{name}} was replaced in another assignment on this date. They may still be available for this task.',
    'replacement.undoBeforeUnlock': 'Undo the recorded replacements before unlocking this plan.',
    'replacement.name': 'Name',
    'replacement.score': 'Score',
    'replacement.scoreValue': '{{score}} out of 100 points',
    'replacement.caution': 'Main caution',
    'replacement.candidates': 'Available candidates',
    'replacement.caution.sameDay': 'Same day: {{task}}',
    'replacement.caution.sameTask.past': 'Same task {{days}} days before',
    'replacement.caution.sameTask.future': 'Same task {{days}} days after',
    'replacement.caution.otherTask.past': 'Assignment {{days}} days before',
    'replacement.caution.otherTask.future': 'Assignment {{days}} days after',
    'replacement.caution.workload': '{{count}} assignments · ±90 days',
    'replacement.caution.noHistory': 'No recorded assignments',
    'replacement.caution.noNearby': 'No nearby assignments',
    'replacement.caution.sameTask.past.one': 'Same task 1 day before',
    'replacement.caution.sameTask.future.one': 'Same task 1 day after',
    'replacement.caution.otherTask.past.one': 'Assignment 1 day before',
    'replacement.caution.otherTask.future.one': 'Assignment 1 day after',
    'replacement.caution.workload.one': '1 assignment · ±90 days',
    'replacement.open': 'Compare replacements for {{name}}',
    'replacement.title': 'Compare replacements',
    'replacement.forPerson': 'For {{name}}',
    'replacement.loadingTarget': 'Loading the released assignment…',
    'replacement.loading': 'Finding available candidates and assignment history…',
    'replacement.search': 'Find a person',
    'replacement.searchPlaceholder': 'Find a person',
    'replacement.count': '{{count}} available',
    'replacement.empty': 'No eligible, available replacements were found for this responsibility.',
    'replacement.noMatches': 'No names match your search.',
    'replacement.refresh': 'Refresh',
    'replacement.close': 'Close',
    'replacement.reload': 'Reload plan',
    'replacement.failed': 'Comparison could not be loaded completely. Please retry.',
    'replacement.conflicting':
      'Overlapping released plans disagree. Review those plans before retrying comparison.',
    'replacement.unavailable':
      'This released assignment has insufficient retained context for comparison. Existing plan information remains available.',
    'replacement.stale':
      'The plan was unlocked or its released version changed. Reload the plan to compare the current release.',
    'replacement.forbidden': 'You do not have permission to compare replacements for this plan.',
    'replacement.domain.service': 'Service responsibility',
    'replacement.domain.lamm': 'LaMM responsibility',
    'replacement.source.draft': 'Draft',
    'planning.cycle.period1': 'Jan/Feb',
    'planning.cycle.period2': 'Mar/Apr',
    'planning.cycle.period3': 'May/Jun',
    'planning.cycle.period4': 'Jul/Aug',
    'planning.cycle.period5': 'Sep/Oct',
    'planning.cycle.period6': 'Nov/Dec',
    'planning.cycle.label': 'Planning cycle',
    'planning.cycle.select': 'Select planning cycle',
    'planning.cycle.open': 'Open planning cycle',
    'planning.cycle.frozen': 'Frozen · Version {{revision}}',
    'planning.cycle.error': 'Planning cycles could not be loaded.',
    'planning.cycle.empty': 'No active planning cycles.',

    'midweekPlanning.releasedPrayer.opening': 'Opening prayer',
    'midweekPlanning.releasedPrayer.closing': 'Closing prayer',
    'midweekPlanning.releasedAssignments': 'Released assignments',
    'midweekPlanning.releasedEmpty': 'No assignable parts.',
    'midweekPlanning.releasedCancelled': 'No midweek meeting',
    'midweekPlanning.releasedPartner': 'Partner',
    'midweekPlanning.releasedPartnerUnassigned': 'Partner unassigned',
    'midweekPlanning.releasedUnavailable':
      'This older release has no captured assignment overview. Existing plan files remain available in Plan cycles.',
    'midweekPlanning.releasedFailed':
      'The released assignments could not be loaded. Retry to load this revision.',
    'absenceForm.addPerson': 'Add person',
    'absenceForm.removePerson': 'Remove person {{count}}',
    'absenceForm.additionalPerson': 'Person {{count}}',
    'absenceForm.peopleLimit': 'Up to 20 people can be saved together.',
    'absenceForm.appliesToPeople': 'Applies to all {{count}} people',
    'absenceForm.savePeople': 'Save for {{count}} people',
    'absenceForm.saveMoreDates': 'Save and enter more dates',
    'absenceForm.savedPeople':
      'Absences saved for {{count}} people. Enter more dates for the same people.',
    'absenceForm.saveUncertain':
      'The save result could not be confirmed. Your entries are locked to prevent duplicates. Retry saving to check and complete this request.',
    'absenceForm.overlappingDates': 'Overlapping dates',
    'absenceResponses.title': 'Responses',
    'absenceResponses.cycle': 'Planning cycle',
    'absenceResponses.loading': 'Loading responses…',
    'absenceResponses.loadError': 'Responses could not be loaded. Please retry.',
    'absenceResponses.retry': 'Retry',
    'absenceResponses.noCycles': 'No imported planning cycles are available.',
    'absenceResponses.unavailable':
      'The previous cycle’s actual dates are unavailable. Import its workbook before using this checklist.',
    'absenceResponses.readOnly': 'This checklist is read-only.',
    'absenceResponses.noPublishers': 'No active LaMM participants.',
    'absenceResponses.progress': '{{completed}}/{{total}} received · {{outstanding}} outstanding',
    'absenceResponses.complete': 'All responses received.',
    'absenceResponses.completed': 'Received responses',
    'absenceResponses.absence_recorded': 'Absence recorded',
    'absenceResponses.no_absences': 'No absences confirmed',
    'absenceResponses.confirm': 'No absences',
    'absenceResponses.confirmFor': 'Confirm no absences for {{name}}',
    'absenceResponses.undo': 'Undo confirmation',
    'absenceResponses.undoFor': 'Undo no-absence confirmation for {{name}}',
    'absenceResponses.saving': 'Saving…',
    'absenceResponses.saveError': 'The response could not be saved. Please try again.',

    'cycles.lifecycle.current': 'Open current planning cycle',
    'cycles.lifecycle.loading': 'Loading cycle status…',
    'cycles.lifecycle.error': 'Cycle status could not be loaded. Editing is unavailable.',
    'cycles.lifecycle.retry': 'Retry',
    'cycles.lifecycle.archived':
      'Archived cycle: read-only. Existing files remain available until retention cleanup.',
    'cycles.lifecycle.unavailable': 'This cycle is no longer available under the retention policy.',
    'cycles.lifecycle.empty': 'No active cycles in this year',

    'participation.progress': '{{assigned}} of {{total}} parts assigned',
    'participation.remaining': '{{count}} unassigned parts',
    'participation.complete': 'All parts assigned',
    'participation.noParts': 'No parts to assign in this cycle.',
    'participation.progressUnavailable': 'Assignment progress could not be loaded.',
    'participation.cycle': 'Workbook cycle {{year}} · {{workbook}}',
    'midweekPlanning.decisionContext.partnerHistory': 'Recent tasks as partner',
    'midweekPlanning.decisionContext.noPartnerHistory': 'No previous tasks as partner.',
    'midweekPlanning.decisionContext.partnerHistoryUnavailable': 'Partner history is unavailable.',
    'nav.congregation': 'Congregation',
    'publisherForm.paused': 'Paused',
    'publisherForm.weight.inactive': 'Not active',
    'publisherForm.weight.stronglyReduced': 'Strongly reduced',
    'publisherForm.weight.reduced': 'Reduced',
    'publisherForm.weight.slightlyReduced': 'Slightly reduced',
    'publisherForm.weight.standard': 'Standard',
    'publisherForm.group.opening': 'Opening',
    'publisherForm.group.treasures': 'Treasures from God’s Word',
    'publisherForm.group.ministry': 'Apply Yourself to the Field Ministry',
    'publisherForm.group.living': 'Living as Christians',
    'publisherForm.gender': 'Brother / Sister',
    'publisherForm.gender.unknown': 'Not specified',
    'publisherForm.gender.male': 'Brother',
    'publisherForm.gender.female': 'Sister',
    'participation.title': 'Assignments this cycle',
    'participation.explanation':
      'Colour compares counts within each group. Includes partners, chairman and prayers.',
    'participation.elder': 'Elders',
    'participation.ministerial_servant': 'Ministerial Servants',
    'participation.brothers': 'Other Brothers',
    'participation.sisters': 'Sisters',
    'participation.unknown': 'Classification needed',
    'participation.classify': 'Set Brother / Sister in publisher management.',
    'participation.sort': 'Sort participants',
    'participation.alphabetical': 'Alphabetical',
    'participation.fewest': 'Fewest first',
    'participation.range': '0–{{max}} parts',
    'participation.emptyGroup': 'No participants',
    'participation.empty': 'No active LaMM participants.',
    'participation.loading': 'Loading cycle assignments…',
    'participation.error': 'Cycle assignments could not be loaded.',
    'participation.tile': '{{name}}: {{count}} parts. Show assignments.',
    'participation.details': '{{name}} · {{count}} parts',
    'participation.none': 'No assignments in this cycle.',
    'participation.assignee': 'Assigned part',
    'participation.partner': 'Partner',
    'participation.close': 'Close details',

    'settings.location': 'Meeting location',
    'settings.locationHelp':
      'Used for new LaMM and service plan releases and their calendars. Previously released plans keep their location.',
    'settings.invalidLocation': 'Enter a location on one line, up to 500 characters.',
    'settings.timezone': 'Meeting timezone',
    'timezone.city.Pacific/Honolulu': 'Honolulu',
    'timezone.city.America/Anchorage': 'Anchorage',
    'timezone.city.America/Los_Angeles': 'Los Angeles',
    'timezone.city.America/Denver': 'Denver',
    'timezone.city.America/Phoenix': 'Phoenix',
    'timezone.city.America/Chicago': 'Chicago',
    'timezone.city.America/New_York': 'New York',
    'timezone.city.America/Halifax': 'Halifax',
    'timezone.city.America/St_Johns': 'St. John’s',
    'timezone.city.America/Sao_Paulo': 'São Paulo',
    'timezone.city.America/Argentina/Buenos_Aires': 'Buenos Aires',
    'timezone.city.Atlantic/Azores': 'Azores',
    'timezone.city.UTC': 'UTC',
    'timezone.city.Europe/London': 'London',
    'timezone.city.Europe/Berlin': 'Berlin',
    'timezone.city.Europe/Helsinki': 'Helsinki',
    'timezone.city.Europe/Moscow': 'Moscow',
    'timezone.city.Africa/Cairo': 'Cairo',
    'timezone.city.Africa/Johannesburg': 'Johannesburg',
    'timezone.city.Asia/Dubai': 'Dubai',
    'timezone.city.Asia/Karachi': 'Karachi',
    'timezone.city.Asia/Kolkata': 'Kolkata',
    'timezone.city.Asia/Kathmandu': 'Kathmandu',
    'timezone.city.Asia/Dhaka': 'Dhaka',
    'timezone.city.Asia/Bangkok': 'Bangkok',
    'timezone.city.Asia/Shanghai': 'Shanghai',
    'timezone.city.Asia/Tokyo': 'Tokyo',
    'timezone.city.Australia/Perth': 'Perth',
    'timezone.city.Australia/Adelaide': 'Adelaide',
    'timezone.city.Australia/Sydney': 'Sydney',
    'timezone.city.Pacific/Auckland': 'Auckland',
    'settings.timezoneHelp':
      'Choose a timezone. Local meeting dates and start times stay the same; frozen plans keep their saved timezone.',
    'settings.invalidTimezone': 'Choose another timezone and try again.',
    'nav.congregationSettings': 'Settings',
    'planAccess.title': 'Weekly plan password',
    'planAccess.loading': 'Loading plan access…',
    'planAccess.configured': 'A password is configured.',
    'planAccess.unconfigured': 'No password configured. The weekly plan stays locked.',
    'planAccess.password': 'New password',
    'planAccess.confirmation': 'Confirm password',
    'planAccess.help':
      '8–128 characters. Changing the password signs out everyone viewing the plan.',
    'planAccess.save': 'Save plan password',
    'planAccess.saved': 'Password saved. Previous plan sessions have been signed out.',
    'planAccess.mismatch': 'The passwords do not match.',
    'planAccess.invalid': 'Use 8–128 characters, including at least one non-space character.',
    'planAccess.stale': 'The password was changed elsewhere. Reload before saving again.',
    'planAccess.loadFailed': 'Could not load plan access. Reload to try again.',
    'settings.title': 'Congregation settings',
    'settings.help':
      'Set defaults for new weeks. Existing schedules stay as planned. Edit official meetings in Plan cycles and field service meetings in their own tab.',
    'settings.adminOnly': 'Administrator access required.',
    'settings.loadFailed': 'Could not load congregation settings. Please retry.',
    'settings.saveFailed': 'Could not save. Check the values and try again.',
    'settings.stale':
      'These defaults changed. Reload the saved values, review your draft and save again.',
    'settings.reload': 'Reload saved values',
    'settings.save': 'Save defaults',
    'settings.saving': 'Saving…',
    'settings.saved': 'Defaults saved.',
    'settings.unsaved': 'Unsaved changes',
    'settings.unavailableConductor':
      'The saved conductor is no longer an active elder. Retain this value or choose a replacement.',
    'settings.useDefault': 'Use congregation default',
    'settings.resetPreview': '{{before}} → {{after}}. Save to apply this change.',

    'watchtower.defaultsTitle': 'Weekend meeting defaults',
    'watchtower.defaultHelp':
      'Weeks without an override use the primary conductor, or the deputy if the primary is unavailable.',
    'watchtower.defaultConductor': 'Watchtower Study default conductor',
    'watchtower.deputyConductor': 'Watchtower Study deputy conductor',
    'watchtower.reason.BOTH_UNAVAILABLE':
      'Neither the primary conductor nor the deputy is available on this date. Choose a conductor for this week.',
    'watchtower.conductor': 'Watchtower Study conductor',
    'watchtower.part': 'Watchtower Study',
    'watchtower.useDefaultName': 'Use default ({{name}})',
    'watchtower.useDefault': 'Use default',
    'watchtower.saveDefault': 'Save default',
    'watchtower.saveConductor': 'Save conductor',
    'watchtower.clear': 'Clear conductor',
    'watchtower.loadFailed': 'Could not load Watchtower Study configuration. Retry loading.',
    'watchtower.saveFailed': 'Could not save the conductor. Check availability and try again.',
    'watchtower.stale': 'The default has changed. Reload before saving.',
    'watchtower.noElders': 'No active elders are available for selection.',
    'watchtower.noAvailableElders': 'No active elders are available on this meeting date.',
    'watchtower.unavailableChoice': '{{name}} (unavailable)',
    'watchtower.setupRequired':
      'Save the planning scope before choosing a conductor for this week.',
    'watchtower.source.default': 'Default',
    'watchtower.source.override': 'Override',
    'watchtower.source.unassigned': 'Unassigned',
    'watchtower.reason.NO_DEFAULT':
      'No default conductor is configured. Choose a conductor for this week.',
    'watchtower.reason.INELIGIBLE':
      'The conductor is no longer an active elder. Choose a replacement.',
    'watchtower.reason.ABSENT':
      'The conductor is absent on this date. Choose a replacement for this week.',
    'publisherForm.service.WatchtowerStudy': 'Watchtower Study',
    'cycles.unifiedDescription':
      'Load the workbook, prepare each week, verify assignments, release files and publish by email.',
    'cycles.release': 'Cycle release',
    'cycles.verify': 'Verify',
    'cycles.publish': 'Publish',
    'cycles.publishEmails': 'Prepare emails',
    'cycles.verifiedFrozen': 'Checks are available before releasing the cycle.',
    'cycles.releaseHelp':
      'Verify and acknowledge the plans, then release the cycle to generate and download files.',
    'cycles.publishHelp': 'Release the cycle and generate its files before preparing emails.',
    'cycles.weekOverview': 'Weekly overview',
    'cycles.checkAll': 'Check all plans',
    'cycles.review.lamm': '1. Review LaMM',
    'cycles.review.service': '2. Review services',
    'cycles.reviewed': 'Reviewed',
    'cycles.freeze': 'Freeze planning cycle',
    'cycles.frozen': 'Planning cycle frozen',
    'cycles.unlock': 'Unlock planning cycle',
    'cycles.freezeConfirm':
      'Freeze LaMM and services together? Files and email delivery remain separate actions.',
    'cycles.unlockConfirm': 'Unlock both plans for editing? Published revisions remain available.',
    'cycles.weekType': 'Configuration for {{date}}',
    'cycles.editWeek': 'Edit week {{date}}',
    'cycles.saveWeek': 'Save week {{date}}',
    'cycles.removeService': 'Remove {{role}} on {{date}}',
    'cycles.assignmentProgress': '{{assigned}} of {{total}} parts assigned',
    'cycles.serviceAssignmentProgress': '{{assigned}} of {{total}} services assigned',
    'cycles.missingParts': 'Not yet assigned',
    'cycles.allPartsAssigned': 'All parts are assigned.',
    'cycles.noAssignableParts': 'No assignable parts in this week.',
    'cycles.noWorkbookWeeks': 'No workbook weeks imported for this cycle.',
    'cycles.serviceOverview': 'Services',
    'cycles.weekdayMeeting': 'Weekday meeting',
    'cycles.weekendMeeting': 'Weekend meeting',
    'cycles.otherMeeting': 'Other date',
    'cycles.additionalDates': 'Additional dates outside the workbook weeks',
    'cycles.meetingDisabled': 'This meeting is disabled.',
    'cycles.noMeetingServices': 'No services configured for this meeting.',
    'cycles.configureServices': 'Configure services',
    'cycles.closeServiceSetup': 'Close service setup',
    'planning.files.plan.lamm': 'LaMM plan',
    'planning.files.createLamm': 'Create LaMM plan and S-89 forms',
    'planning.files.createService': 'Create Service plan',
    'planning.files.calendarStep': 'Personal calendar',
    'planning.files.createCalendar': 'Create personal calendar',
    'planning.files.calendarReadyNotice':
      'The combined calendars are ready in Personal files below.',
    'planning.files.calendarNeedsPlans': 'Create both plans before creating personal calendars',
    'planning.files.plan.service': 'Service plan',
    'planning.files.identity': 'Cycle {{scope}} · Revision {{revision}}',
    'planning.files.personalTitle': 'Personal files',
    'planning.files.personalHelp':
      'People with assignments in these frozen plans. After both plans are ready, create one combined calendar per person. S-89 forms apply only to eligible LaMM parts.',
    'planning.files.unknownPerson': 'Participant name unavailable',
    'planning.files.inventoryUnknown':
      'The full personal-file inventory could not be verified. Some files or people may still be missing.',
    'planning.files.noPeople': 'No personal files are required for these plans.',
    'planning.files.peopleCount': '{{count}} people',
    'planning.files.personName': 'Name',
    'planning.files.calendarHeader': 'ICS',
    'planning.files.s89Header': 'S-89',
    'planning.files.pendingPlans': 'Files not yet ready: {{plans}}',
    'planning.files.downloadIcs': 'Download ICS for {{name}}',
    'planning.files.downloadS89': 'Download S-89 for {{name}}',
    'planning.files.notReady': 'The required file is not yet ready.',
    'planning.files.notRequired': 'No file of this kind is required for this person.',
    'planning.files.expectationUnknown': 'S-89 eligibility could not be verified.',
    'planning.files.downloadHtml': 'Download {{plan}} as HTML',
    'planning.files.downloadPdf': 'Download {{plan}} as PDF',
    'planning.files.preview': 'Preview {{plan}}',
    'planning.files.failedStage': 'Failed phase: {{stage}}',
    'planning.files.job': 'Generation reference',
    'planning.files.inputIssue': 'Captured plan validation',
    'planning.files.stage.prepare': 'Plan preparation and HTML',
    'planning.files.stage.pdf': 'Plan PDF',
    'planning.files.stage.personal': 'Personal calendars and S-89 forms',
    'planning.files.stage.commit': 'Final output validation',
    'planning.files.stage.unknown': 'Not recorded for this older attempt',
    'planning.files.error.ARTIFACT_GENERATION_FAILED':
      'Generation failed. Retry or use the reference to investigate the affected plan.',
    'planning.files.error.ARTIFACT_GENERATION_TIMEOUT': 'Generation exceeded its time limit.',
    'planning.files.error.S89_MAPPING_MISSING':
      'A LaMM part has no supported S-89 type mapping. Correct the source assignment type and freeze a new revision.',
    'planning.files.error.S89_PART_NUMBER_MISSING':
      'An S-89 part is missing its part number in the frozen plan.',
    'planning.files.error.CAPTURE_PARTICIPANT_MISSING':
      'A participant is missing from the frozen plan context.',
    'planning.files.error.S89_TEMPLATE_MISSING': 'The S-89 form template is unavailable.',
    'planning.files.error.S89_TEMPLATE_FIELDS_MISSING':
      'The S-89 template does not contain the required fields.',
    'planning.files.error.INVALID_RENDER_CONTEXT':
      'The frozen rendering context is incomplete or invalid.',
    'planning.files.error.ARTIFACT_REVISION_MISMATCH':
      'The generation request does not match the frozen revision.',
    'planning.files.error.ARTIFACT_TEMPLATE_UNAVAILABLE':
      'The requested plan template is unavailable.',
    'planning.files.error.ARTIFACT_SET_INCOMPLETE': 'Not all required output files were produced.',
    'planning.files.error.ARTIFACT_CHECKSUM_MISMATCH':
      'An output file did not pass its integrity check.',
    'planning.files.error.CAPTURE_MEETING_DATE_INVALID': 'A captured meeting date is invalid.',
    'cycles.title': 'Plan cycles',
    'cycles.description':
      'Prepare scopes, configure shared weeks, check assignments and manage frozen plans.',
    'cycles.domains': 'Planning domains',
    'cycles.serviceSetup': 'Service range and slots',
    'cycles.slots': 'Services',
    'cycles.addSlot': 'Add slot',
    'cycles.removeSlot': 'Remove slot',
    'cycles.saveSlots': 'Save slots',
    'cycles.saved': 'Slots saved.',
    'cycles.filled': 'Filled',
    'cycles.legacyPreview':
      '{{count}} earlier assignment(s) are proposed for adoption. Saving keeps their publishers unchanged.',
    'cycles.noServiceSlots': 'There are no enabled meetings in this range.',
    'cycles.invalidSlot': 'Choose a date within the range and a valid slot identifier.',
    'cycles.duplicateSlot': 'This date, role and slot already exists.',
    'cycles.staleScope': 'The range changed elsewhere. Reload it before saving.',
    'cycles.saveFailed': 'The slots could not be saved.',
    'cycles.loadFailed': 'The selected scope could not be loaded.',
    'cycles.loadScope': 'Load range',
    'cycles.retry': 'Retry',
    'cycles.overlapNotice':
      'Overlapping ranges share assignments with the same date, role and slot.',
    'cycles.sharedWeeks': 'Week configuration',
    'cycles.openSetup': 'Open in Plan cycles',
    'cycles.fillService': 'Fill service assignments',
    'cycles.fillLamm': 'Fill LaMM assignments',
    'planning.issue.SERVICE_SCOPE_MISSING':
      'Save the service slot structure before checking assignments.',
    'planning.issue.MEETING_DATE_UNCLASSIFIED': 'This slot date has no configured meeting time.',
    'planning.distribution.lamm': 'Midweek meeting',
    'planning.distribution.service': 'Service plan',
    'planning.distribution.title': 'Send reviewed plans',
    'planning.distribution.instructions':
      'Choose ready plan sets and recipients, then review every attachment before sending.',
    'planning.distribution.domain': 'Plan type',
    'planning.distribution.scope': 'Scope',
    'planning.distribution.revision': 'Revision',
    'planning.distribution.find': 'Find ready sets',
    'planning.distribution.add': 'Add {{locale}} set for revision {{revision}}',
    'planning.distribution.remove': 'Remove',
    'planning.distribution.recipients': 'Recipients',
    'planning.distribution.recipientCount':
      'Add or change recipients ({{selected}} of {{total}} selected)',
    'planning.distribution.defaultExplanation':
      'Recommended recipients are active LaMM participants and active publishers assigned in either selected plan. You can add other active publishers below.',
    'planning.distribution.restoreRecommended': 'Use recommended recipients',
    'planning.distribution.emptyDefault':
      'No recommended recipients for these plans. You can select other active publishers.',
    'planning.distribution.manualRecipients':
      'Automatic recipient selection is unavailable for these older plans. Select recipients manually.',
    'planning.distribution.peopleError':
      'The complete recipient list could not be loaded. Try again before reviewing delivery.',
    'planning.distribution.retryPeople': 'Reload recipients',
    'planning.distribution.selectAll': 'Select all',
    'planning.distribution.clearAll': 'Clear all',
    'planning.distribution.noRecipients': 'No active publishers available.',
    'planning.distribution.emailLocale': 'Email language',
    'planning.distribution.review': 'Review delivery',
    'planning.distribution.sender': 'Sender',
    'planning.distribution.expires': 'Preview expires',
    'planning.distribution.confirm': 'I have reviewed the recipients, plans and attachments.',
    'planning.distribution.send': 'Send reviewed email',
    'planning.distribution.batch': 'Delivery status',
    'planning.distribution.batchSummary': '{{sent}} of {{total}} accepted by email provider',
    'planning.distribution.queued': 'Queued',
    'planning.distribution.sending': 'Sending',
    'planning.distribution.sent': 'Accepted by email provider',
    'planning.distribution.failed': 'Failed',
    'planning.distribution.unknown': 'Acceptance unknown — do not resend without checking',
    'planning.distribution.selectRetry': 'Select for retry',
    'planning.distribution.retry': 'Retry selected failed recipients',
    'planning.distribution.sendUncertain':
      'The send result is unclear. Do not send again until the delivery batch has been checked.',
    'planning.distribution.disabled': 'Email sending is disabled.',
    'planning.distribution.stale': 'Preview expired or changed. Review again.',
    'planning.distribution.denied': 'You do not have permission to send these plans.',
    'planning.distribution.error': 'Unable to review or send. Check the selections and try again.',
    'planning.artifacts.title': 'Plan files',
    'planning.artifacts.locale': 'Document language',
    'planning.artifacts.generate': 'Generate files',
    'planning.artifacts.submitting': 'Starting file generation…',
    'planning.artifacts.accepted':
      'Generation request accepted. The status will appear here shortly.',
    'planning.artifacts.readyNotice': 'The files are ready below.',
    'planning.artifacts.regenerate': 'Recreate files',
    'planning.artifacts.regenerateHelp':
      'Create new files from this frozen revision. Existing files remain available.',
    'planning.artifacts.fileSet': 'File set',
    'planning.artifacts.empty': 'No files generated for this revision.',
    'planning.artifacts.legacy':
      'Freeze a new revision to capture the data required for these files.',
    'planning.artifacts.queued': 'Queued',
    'planning.artifacts.running': 'Generating',
    'planning.artifacts.ready': 'Ready',
    'planning.artifacts.failed': 'Generation failed',
    'planning.artifacts.retry': 'Retry generation',
    'planning.artifacts.download': 'Download',
    'planning.artifacts.preview': 'Preview plan',
    'planning.artifacts.finalPlanActions': 'Final plan formats',
    'planning.artifacts.viewHtml': 'View final plan (HTML)',
    'planning.artifacts.downloadPdf': 'Download final plan (PDF)',
    'planning.artifacts.closePreview': 'Close preview',
    'planning.artifacts.error':
      'Unable to load or generate files. Check the configuration and try again.',
    'planning.artifacts.denied': 'You do not have permission to access these files.',
    'planning.artifacts.disabled': 'Artifact generation is currently disabled.',
    'planning.artifacts.kind.html': 'Plan HTML',
    'planning.artifacts.kind.pdf': 'Plan PDF',
    'planning.artifacts.kind.s89': 'Personal S-89',
    'planning.artifacts.kind.ics': 'Personal calendar',
    'common.appName': 'CongPlanner',
    'common.loading': 'Loading...',
    'common.cancel': 'Cancel',
    'common.retry': 'Retry',
    'common.closeDialog': 'Close dialog',
    'common.confirm': 'Confirm',
    'common.processing': 'Processing...',
    'common.error': 'Error',
    'common.dismiss': 'Dismiss',
    'common.save': 'Save',
    'common.language': 'Language',
    'common.english': 'English',
    'common.german': 'German',
    'common.refresh': 'Refresh',
    'common.from': 'From',
    'common.to': 'To',
    'common.type': 'Type',
    'common.status': 'Status',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.created': 'Created',
    'common.completed': 'Completed',
    'common.filters': 'Filters',
    'common.noFilters': 'No filters applied',
    'common.row': 'Row',
    'nav.dashboard': 'Dashboard',
    'nav.planCycles': 'Plan cycles',
    'nav.servicePlanning': 'Services',
    'nav.midweekMeetingPlanning': 'LaMM',
    'nav.publishers': 'Publishers',
    'nav.absences': 'Absences',
    'nav.export': 'Export',
    'nav.userManagement': 'Users',
    'nav.accountMenu': 'Account menu',
    'nav.account': 'Account',
    'nav.logout': 'Logout',
    'nav.openMenu': 'Open navigation menu',
    'nav.closeMenu': 'Close navigation menu',
    'login.title': 'Sign in to CongPlanner',
    'login.subtitle': 'Congregation Planning System',
    'login.mockAuthEnabled': 'Mock auth is enabled for local development.',
    'login.mockAuthUse': 'Use {{email}} / {{password}}',
    'login.email': 'Email address',
    'login.emailPlaceholder': 'Enter your email address',
    'login.password': 'Password',
    'login.passwordPlaceholder': 'Enter your password',
    'login.submit': 'Sign in',
    'login.submitting': 'Signing in...',
    'login.support': "For technical support, contact your congregation's IT administrator",
    'login.error.default': 'Login failed. Please try again.',
    'login.error.invalidCredentials':
      'Invalid email or password. Please check your credentials and try again.',
    'login.error.tooManyAttempts':
      'Too many login attempts. Please wait a few minutes before trying again.',
    'login.error.server': 'Server error. Please try again in a few moments.',
    'login.error.network': 'Network error. Please check your connection and try again.',
    'login.validation.emailRequired': 'Email is required',
    'login.validation.emailInvalid': 'Please enter a valid email address',
    'login.validation.passwordRequired': 'Password is required',
    'login.validation.passwordLength': 'Password must be at least 6 characters',
    'login.firstLogin.title': 'Choose a permanent password',
    'login.firstLogin.subtitle': 'Complete your first sign-in',
    'login.firstLogin.instructions':
      'Replace the temporary password with a permanent password. Use at least 12 characters with uppercase, lowercase, a number, and a symbol.',
    'login.firstLogin.newPassword': 'New password',
    'login.firstLogin.confirmPassword': 'Confirm new password',
    'login.firstLogin.submit': 'Set password and sign in',
    'login.firstLogin.back': 'Back to sign in',
    'login.firstLogin.error':
      'The password could not be changed. Request a new invitation if the temporary password has expired.',
    'login.firstLogin.validation.policy':
      'Use at least 12 characters with uppercase, lowercase, a number, and a symbol.',
    'login.firstLogin.validation.mismatch': 'The passwords do not match.',
    'login.recovery.forgotPassword': 'Forgot password?',
    'login.recovery.title': 'Reset your password',
    'login.recovery.subtitle': 'Recover access to your account',
    'login.recovery.requestInstructions':
      'Enter your email address. If it can be used to recover an account, Cognito will send a reset code. If no code arrives, contact your administrator.',
    'login.recovery.sendCode': 'Send reset code',
    'login.recovery.codeSent':
      'If the account can be recovered, a code has been sent to its verified email address. Check your inbox and spam folder.',
    'login.recovery.code': 'Reset code',
    'login.recovery.codeRequired': 'Enter the reset code from your email.',
    'login.recovery.resend': 'Send a new code',
    'login.recovery.resetPassword': 'Set new password',
    'login.recovery.success':
      'Your password has been reset. You can now sign in with your new password.',
    'login.recovery.backToSignIn': 'Back to sign in',
    'login.recovery.invalidCode':
      'That code is invalid or expired. Request a new code and try again.',
    'login.recovery.requestError':
      'We could not start password recovery. Please try again later or contact your administrator.',
    'login.recovery.confirmError':
      'We could not reset your password. Check the code and password, then try again.',
    'login.firstLogin.profileError': 'Required profile information is missing or invalid.',
    'login.firstLogin.givenNameRequired': 'First name is required.',
    'login.firstLogin.familyNameRequired': 'Last name is required.',
    'protected.refreshing': 'Refreshing session...',
    'protected.sessionUnavailable': 'Unable to refresh your session',
    'protected.sessionRetryDescription':
      'Your saved login is still available. Check your connection and try again.',
    'protected.retrySession': 'Try again',
    'protected.accessDenied': 'Access Denied',
    'protected.permissionDenied': 'Your current role does not have permission to view this page.',
    'protected.requiresRole':
      'This page requires {{role}} access. Your current role is {{currentRole}}.',
    'protected.goBack': 'Go Back',
    'role.admin': 'Administrator',
    'role.publisher': 'Publisher',
    'appRole.admin': 'Admin',
    'appRole.absence_management': 'Absence Management',
    'appRole.service_management': 'Service Management',
    'appRole.lamm_overseer': 'LaMM Overseer',
    'appRole.planner': 'Field service planner',
    'appRole.overseer': 'Overseer',
    'dashboard.title': 'Dashboard',
    'dashboard.totalPublishers': 'Total Publishers',
    'dashboard.upcomingAbsences': 'Upcoming Absences',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.shortcuts': 'Quick Access',
    'home.thisWeek': 'This week',
    'home.openPublicPlan': 'Open public weekly plan (opens in a new tab)',
    'home.loadFailed': 'This week could not be loaded.',
    'home.namesFailed': 'Publisher names could not be loaded.',
    'home.notReleased': 'No released plan for this week. Open the module to view planning.',
    'home.noAssignments': 'No assignments this week.',
    'home.noAbsences': 'No absences this week.',
    'dashboard.shortcutsEmpty': 'No modules are available for your account.',
    'dashboard.openShortcut': 'Open module',
    'dashboard.servicePlanning': 'Service Planning',
    'dashboard.servicePlanningDesc':
      'Trigger the existing planning Lambda workflow for service planning cycles.',
    'dashboard.midweekMeetingPlanning': 'LaMM Planning',
    'dashboard.midweekMeetingPlanningDesc':
      'Load one workbook cycle for LaMM and prepare future assignments.',
    'dashboard.activity.absenceUpdated': 'Absence updated for {{name}}',
    'dashboard.activity.publisherUpdated': 'Publisher {{name}} updated',
    'dashboard.activity.exportGenerated': 'Data export generated for {{period}}',
    'dashboard.activity.empty': 'No recent activity yet.',
    'userManagement.title': 'User Management',
    'userManagement.subtitle':
      'Create users, enable or disable access, and assign application roles.',
    'userManagement.addUser': 'Add user',
    'userManagement.createTitle': 'Create User',
    'userManagement.usersTitle': 'Users',
    'userManagement.email': 'Email',
    'userManagement.givenName': 'First Name',
    'userManagement.familyName': 'Last Name',
    'userManagement.congregation': 'Congregation',
    'userManagement.role': 'Role',
    'userManagement.status': 'Status',
    'userManagement.accountSetup': 'Account setup',
    'userManagement.setupRequired': 'Temporary password: initial setup required',
    'userManagement.setupComplete': 'Initial setup complete',
    'userManagement.setupUnknown': 'Setup status unavailable',
    'userManagement.actions': 'Actions',
    'userManagement.user': 'User',
    'userManagement.create': 'Create',
    'userManagement.enable': 'Enable',
    'userManagement.disable': 'Disable',
    'userManagement.enabled': 'Enabled',
    'userManagement.disabled': 'Disabled',
    'userManagement.loadFailed': 'Failed to load users.',
    'userManagement.createFailed': 'Failed to create user.',
    'userManagement.updateFailed': 'Failed to update user.',
    'userManagement.roleUpdateFailed': 'Failed to update user role.',
    'userManagement.empty': 'No users found.',
    'publishers.title': 'Publishers',
    'publishers.subtitle': 'Manage publisher registry and information ({{count}} publishers)',
    'publishers.subtitleIdle': 'Manage publisher registry and information',
    'publishers.dashboard.total': 'Total Publishers',
    'publishers.dashboard.active': 'Active Publishers',
    'publishers.dashboard.elders': 'Elders',
    'publishers.dashboard.ministerialServants': 'Ministerial Servants',
    'publishers.add': 'Add Publisher',
    'publishers.import': 'Import CSV',
    'publishers.importing': 'Importing...',
    'publishers.search': 'Search publishers',
    'publishers.noMatches': 'No publishers match your search.',
    'publishers.searchPlaceholder': 'Search publishers by name or email...',
    'publishers.clear': 'Clear',
    'publishers.noResults': 'No publishers found matching your criteria.',
    'publishers.empty': 'No publishers found. Add your first publisher to get started.',
    'publishers.unloaded': 'Publisher records are loaded only when you request them.',
    'publishers.load': 'Load active publishers',
    'publishers.loading': 'Loading active publishers...',
    'publishers.retry': 'Retry loading publishers',
    'publishers.registry': 'Publisher',
    'publishers.contact': 'Contact',
    'publishers.typeRole': 'Type & Role',
    'publishers.baptismDate': 'Baptism Date',
    'publishers.actions': 'Actions',
    'publishers.edit': 'Edit publisher',
    'publishers.delete': 'Delete publisher',
    'publishers.deleteTitle': 'Delete Publisher',
    'publishers.deleteMessage':
      'Are you sure you want to delete {{name}}? This action cannot be undone.',
    'publishers.filters.publisherType': 'Publisher Type',
    'publishers.filters.congregationRole': 'Congregation Role',
    'publishers.filters.state': 'Status',
    'publishers.filters.allTypes': 'All Types',
    'publishers.filters.allRoles': 'All Roles',
    'publishers.filters.all': 'All',
    'publishers.importFormatTitle': 'CSV Import Format',
    'publishers.importFormatRequired': 'Required columns: {{columns}}',
    'publishers.importFormatOptional':
      'Optional columns: {{columns}}. Use | or ; to separate multiple list values.',
    'publishers.importLastResult': 'Last Import Result',
    'publishers.importSummary':
      '{{created}} created, {{updated}} updated, {{unchanged}} unchanged, {{failed}} failed ({{total}} total).',
    'publishers.importSelectJson': 'Please select a JSON file.',
    'publishers.importMalformed': 'The selected file is not valid JSON.',
    'publishers.importEmpty': 'The document contains no publishers.',
    'publishers.importUnsupported': 'Only publisher import schema version 1 is supported.',
    'publishers.importExample': 'Example JSON',
    'publishers.importErrors': 'Rows with errors',
    'publishers.importErrorsShowing': 'Showing first 10 errors of {{count}}.',
    'publishers.importSelectCsv': 'Please select a CSV file.',
    'publishers.importFailed': 'Failed to import publishers.',
    'publishers.loadFailed': 'Failed to load publishers',
    'publishers.deleteFailed': 'Failed to delete publisher',
    'publishers.queryPrompt': 'Use search or filters to find publishers.',
    'publisherType.publisher': 'Publisher',
    'publisherType.pioneer': 'Pioneer',
    'congregationRole.none': 'None',
    'congregationRole.ministerial_servant': 'Ministerial Servant',
    'congregationRole.elder': 'Elder',
    'publisherForm.addTitle': 'Add New Publisher',
    'publisherForm.editTitle': 'Edit Publisher',
    'publisherForm.firstName': 'First Name',
    'publisherForm.lastName': 'Last Name',
    'publisherForm.birthDate': 'Birth Date',
    'publisherForm.baptismDate': 'Baptism Date',
    'publisherForm.address': 'Address',
    'publisherForm.street': 'Street Address',
    'publisherForm.city': 'City',
    'publisherForm.state': 'State',
    'publisherForm.zipCode': 'ZIP Code',
    'publisherForm.phoneNumber': 'Phone Number',
    'publisherForm.emailAddress': 'Email Address',
    'publisherForm.appointedRole': 'Appointed role',
    'publisherForm.serviceEligibility': 'Service eligibility',
    'publisherForm.frequency': 'Assignment weight',
    'publisherForm.frequencyHelp':
      '0 excludes this service. Positive values control score growth: 0.5 grows at half the standard rate, 1 at the standard rate. This is not a share of meetings.',
    'publisherForm.weekdays': 'Weekdays',
    'publisherForm.weekends': 'Weekends',
    'publisherForm.qualified': 'Yes',
    'publisherForm.notQualified': 'No',
    'publisherForm.temporarilyUnavailable': 'Temporarily paused',
    'publisherForm.part.chairman': 'Chairman',
    'publisherForm.part.prayer': 'Prayer',
    'publisherForm.part.treasuresTalk': 'Treasures talk',
    'publisherForm.part.spiritualGems': 'Spiritual gems',
    'publisherForm.part.bibleReading': 'Bible reading',
    'publisherForm.part.initialCall': 'Starting a conversation',
    'publisherForm.part.returnVisit': 'Following up',
    'publisherForm.part.makeDisciples': 'Making disciples',
    'publisherForm.part.ayttfmTalk': 'Student talk',
    'publisherForm.part.partner': 'Partner',
    'publisherForm.part.livingAsChristians': 'Living as Christians',
    'publisherForm.part.congregationBibleStudy': 'Congregation Bible Study',
    'publisherForm.service.ChairmanWEM': 'Chairman',
    'publisherForm.service.Reader': 'Reader',
    'publisherForm.service.Video': 'Video',
    'publisherForm.service.Audio': 'Audio',
    'publisherForm.service.AttendantHall': 'Hall attendant',
    'publisherForm.service.AttendantEntrance': 'Entrance attendant',
    'publisherForm.service.AttendantZoom': 'Zoom attendant',
    'publisherForm.service.Stage': 'Stage',
    'publisherForm.service.Microphone': 'Microphone',
    'publisherForm.publisherType': 'Publisher Type',
    'publisherForm.congregationRole': 'Congregation Role',
    'publisherForm.lammEligibility': 'LaMM Eligibility',
    'publisherForm.lammEligibilityDescription':
      'Maintain participation and eligibility for each LaMM assignment type.',
    'publisherForm.lammParticipant': 'Participant',
    'publisherForm.privileges': 'Privileges',
    'publisherForm.assignments': 'Assignments',
    'publisherForm.privilegePlaceholder': 'Add privilege...',
    'publisherForm.assignmentPlaceholder': 'Add assignment...',
    'publisherForm.addItem': 'Add',
    'publisherForm.create': 'Create Publisher',
    'publisherForm.update': 'Update Publisher',
    'publisherForm.saving': 'Saving...',
    'publisherForm.saveFailed':
      'The publisher could not be saved. Your changes are retained. Please try again.',
    'publisherForm.privilege.sound': 'Sound',
    'publisherForm.privilege.platform': 'Platform',
    'publisherForm.privilege.microphones': 'Microphones',
    'publisherForm.privilege.literature': 'Literature',
    'publisherForm.privilege.accounts': 'Accounts',
    'publisherForm.privilege.territories': 'Territories',
    'publisherForm.privilege.schoolOverseer': 'School Overseer',
    'publisherForm.privilege.serviceOverseer': 'Service Overseer',
    'publisherForm.privilege.secretary': 'Secretary',
    'publisherForm.privilege.coordinator': 'Coordinator',
    'publisherForm.assignment.watchtowerStudy': 'Watchtower Study',
    'publisherForm.assignment.theocraticMinistrySchool': 'Theocratic Ministry School',
    'publisherForm.assignment.serviceMeeting': 'Service Meeting',
    'publisherForm.assignment.publicTalk': 'Public Talk',
    'publisherForm.assignment.circuitAssembly': 'Circuit Assembly',
    'publisherForm.assignment.regionalConvention': 'Regional Convention',
    'publisherForm.validation.firstName': 'First name is required',
    'publisherForm.validation.lastName': 'Last name is required',
    'publisherForm.validation.birthDate': 'Birth date is required',
    'publisherForm.validation.baptismDate': 'Baptism date is required',
    'publisherForm.validation.street': 'Street address is required',
    'publisherForm.validation.city': 'City is required',
    'publisherForm.validation.state': 'State is required',
    'publisherForm.validation.zipCode': 'ZIP code is required',
    'publisherForm.validation.phoneRequired': 'Phone number is required',
    'publisherForm.validation.phoneInvalid': 'Please enter a valid phone number',
    'publisherForm.validation.emailRequired': 'Email address is required',
    'publisherForm.validation.emailInvalid': 'Please enter a valid email address',
    'absences.title': 'Absences',
    'absences.add': 'Add Absence',
    'absences.import': 'Import CSV',
    'absences.importing': 'Importing...',
    'absences.total': 'Total Absences',
    'absences.absentPublishers': 'Absent Publishers',
    'absences.absentTypeCount': 'Absent Entries',
    'absences.conditionalCount': 'Conditional Entries',
    'absences.importFormatTitle': 'CSV Import Format',
    'absences.importFormatRequired': 'Required columns: {{columns}}',
    'absences.importFormatIdentifier':
      'Include at least one publisher identifier column: {{columns}}',
    'absences.importFormatOptional': 'Optional columns: {{columns}}',
    'absences.importLastResult': 'Last Import Result',
    'absences.importSummary': 'Imported {{imported}} of {{total}} rows. {{failed}} failed.',
    'absences.importErrors': 'Rows with errors',
    'absences.importErrorsShowing': 'Showing first 10 errors of {{count}}.',
    'absences.importSelectCsv': 'Please select a CSV file.',
    'absences.importFailed': 'Failed to import absences.',
    'absences.loadFailed': 'Failed to load data',
    'absences.loadAbsencesFailed': 'Failed to load absences',
    'absences.createFailed': 'Failed to create absence',
    'absences.createOverlapWarning':
      'An absence already exists in this timeframe. Please clarify, resolve, or manually merge the records.',
    'absences.updateFailed': 'Failed to update absence',
    'absences.deleteFailed': 'Failed to delete absence',
    'absences.queryPrompt': 'Use filters to find absences.',
    'absenceCalendar.title': 'Absence calendar',
    'absenceCalendar.month': 'Month',
    'absenceCalendar.empty': 'No absences this month.',
    'absenceCalendar.loadFailed': 'Absences could not be loaded.',
    'absenceCalendar.retry': 'Try again',
    'absenceCalendar.barDescription': '{{name}}: {{start}} – {{end}}',
    'absenceType.selectOptional': 'Select type (optional)',
    'absenceSubscriptions.title': 'Subscribe to calendar',
    'absenceSubscriptions.guidance':
      'Anyone with a link can read its calendar. Copy the HTTPS link into your calendar app to subscribe. Changes are grouped for about one hour before publication; the app decides when to fetch updates.',
    'absenceSubscriptions.appointed': 'Elders and ministerial servants',
    'absenceSubscriptions.all': 'Everyone',
    'absenceSubscriptions.elders': 'Elders',
    'absenceSubscriptions.ministerialServants': 'Ministerial servants',
    'absenceSubscriptions.otherBrothers': 'Other brothers',
    'absenceSubscriptions.sisters': 'Sisters',
    'absenceSubscriptions.copy': 'Copy link',
    'absenceSubscriptions.subscribe': 'Subscribe',
    'absenceSubscriptions.copied': 'Link copied.',
    'absenceSubscriptions.copyFailed': 'Copy failed. Select and copy the link above.',
    'absenceSubscriptions.unavailable': 'Calendar subscriptions are not available yet.',
    'absenceType.talk': 'Talk',
    'absenceType.absent': 'Absent',
    'absenceType.conditional': 'Conditional',
    'absenceType.do_not_schedule': 'Do Not Schedule',
    'absenceForm.addTitle': 'Add New Absence',
    'absenceForm.editTitle': 'Edit Absence',
    'absenceForm.publisher': 'Publisher',
    'absenceForm.selectPublisher': 'Select a publisher',
    'absenceForm.absenceType': 'Absence Type',
    'absenceForm.startDate': 'Start Date',
    'absenceForm.endDate': 'End Date',
    'absenceForm.endDateHint': 'Leave empty for single-day absence',
    'absenceForm.create': 'Save',
    'absenceForm.saveAnother': 'Save and add another',
    'absenceForm.savedOverlapWarning':
      'The absence was saved, but an absence already exists in that timeframe. Please clarify, resolve, or manually merge the records.',
    'absenceForm.savedAnother': 'Absence saved. Add another for the same publisher.',
    'absenceForm.saveFailed': 'The absence could not be saved. Please try again.',
    'absenceForm.previousMonth': 'Previous month',
    'absenceForm.nextMonth': 'Next month',
    'absenceForm.chooseDate': 'Choose',
    'absenceForm.unavailableDate': 'Unavailable',
    'absenceForm.update': 'Update Absence',
    'absenceForm.saving': 'Saving...',
    'absenceForm.validation.publisher': 'Publisher is required',
    'absenceForm.validation.startDate': 'Start date is required',
    'absenceForm.validation.invalidDate': 'Enter a valid date.',
    'absenceForm.validation.endDateAfter': 'End date must be after start date',
    'absenceForm.validation.pastLimit': 'Start date cannot be more than 2 years in the past',
    'absenceForm.validation.futureLimit': 'Start date cannot be more than 1 year in the future',
    'absenceForm.upcomingTitle': 'Upcoming absences',
    'absenceForm.upcomingLoading': 'Loading upcoming absences…',
    'absenceForm.upcomingEmpty': 'No upcoming absences.',
    'absenceForm.upcomingError':
      'Upcoming absences could not be loaded. You can still create this absence.',
    'absenceForm.overlapWarning':
      'An absence already exists in this timeframe. Please clarify, resolve, or manually merge the records.',
    'absenceList.filters': 'Filters',
    'absenceList.clearFilters': 'Clear filters',
    'absenceList.publisher': 'Publisher',
    'absenceList.publisherGroups': 'Publisher groups',
    'absenceList.allGroups': 'All groups',
    'absenceGroup.elder': 'Elders',
    'absenceGroup.ministerial_servant': 'Ministerial servants',
    'absenceGroup.brothers': 'Brothers',
    'absenceGroup.sisters': 'Sisters',
    'absenceList.allPublishers': 'All publishers',
    'absenceList.allTypes': 'All types',
    'absenceList.dateRange': 'Date Range',
    'absenceList.startDate': 'Start date',
    'absenceList.endDate': 'End date',
    'absenceList.loading': 'Loading absences...',
    'absenceList.noMatch': 'No absences match your filters',
    'absenceList.empty': 'No absences found',
    'absenceList.unknownPublisher': 'Unknown Publisher',
    'absenceList.edit': 'Edit absence',
    'absenceList.delete': 'Delete absence',
    'absenceList.deleteTitle': 'Delete Absence',
    'absenceList.deleteMessage':
      'Are you sure you want to delete this absence for {{name}}? This action cannot be undone.',
    'absenceList.pageIndicator': 'Page {{page}}',
    'absenceList.previousPage': 'Previous',
    'absenceList.nextPage': 'Next',
    'export.title': 'Data Export',
    'export.subtitle': 'Export absence data in various formats for external use',
    'export.new': 'New Export',
    'export.loadFailed': 'Failed to load export history',
    'export.failed': 'Export failed. Please try again.',
    'export.downloadFailed': 'Download failed. Please try again.',
    'export.dialog.title': 'Export Data',
    'export.dialog.subtitle': 'Configure your data export settings',
    'export.dialog.format': 'Export Format',
    'export.dialog.filters': 'Filters',
    'export.dialog.startDate': 'Start Date',
    'export.dialog.endDate': 'End Date',
    'export.dialog.publisher': 'Publisher',
    'export.dialog.allPublishers': 'All Publishers',
    'export.dialog.absenceType': 'Absence Type',
    'export.dialog.cancel': 'Cancel',
    'export.dialog.generate': 'Generate Export',
    'export.dialog.generating': 'Generating Export...',
    'export.format.csvDesc': 'Comma-separated values for spreadsheet applications',
    'export.format.jsonDesc': 'JavaScript Object Notation for programmatic use',
    'export.format.excelDesc': 'Microsoft Excel format with formatted sheets',
    'export.history.title': 'Export History',
    'export.history.loading': 'Loading export history...',
    'export.history.emptyTitle': 'No exports yet',
    'export.history.emptySubtitle': 'Create your first data export to see it here.',
    'export.history.pending': 'Pending',
    'export.history.processing': 'Processing',
    'export.history.completed': 'Completed',
    'export.history.failed': 'Failed',
    'export.history.unknown': 'Unknown',
    'export.history.unknownSize': 'Unknown size',
    'export.history.format': 'Format',
    'export.history.size': 'Size',
    'export.history.created': 'Created',
    'export.history.completedAt': 'Completed',
    'export.history.filters': 'Filters',
    'export.history.error': 'Error',
    'export.history.download': 'Download',
    'servicePlanning.edit': 'Edit assignment',
    'serviceAuto.undoDescription':
      'Reset clears automatic and manual service assignments. Input scores stay unchanged.',
    'serviceAuto.undo': 'Reset service assignments',
    'serviceAuto.recoverUndo': 'Retry undo',
    'servicePlanning.weekCounts': '{{assigned}} assigned · {{open}} open',
    'servicePlanning.title': 'Service Planning',
    'servicePlanning.subtitle': 'Start the existing service planning workflow from the web tool.',
    'servicePlanning.description': 'Assign the cycle automatically and review manual corrections.',
    'servicePlanning.blueprint':
      'Each meeting inherits the standard services. Change meeting dates or cancellations in Plan cycles → week configuration.',
    'servicePlanning.noConfigurationWeeks':
      'No week configurations are available. Retry loading this period.',
    'servicePlanning.noMeetings': 'There are no enabled meetings in this range.',
    'servicePlanning.trigger': 'Start Service Planning',
    'servicePlanning.triggering': 'Starting...',
    'servicePlanning.triggered':
      'Service planning workflow trigger prepared. Backend wiring can be connected to the existing Lambda next.',
    'servicePlanning.nextStepTitle': 'Current scope',
    'servicePlanning.nextStepBody':
      'The category and trigger action are now in place. The next step is to connect this button to the existing workflow endpoint.',
    'planning.progressLabel': 'Planning progress',
    'planning.step.scope': 'Select scope',
    'planning.step.assign': 'Assign roles',
    'planning.step.review': 'Check assignments',
    'planning.step.publish': 'Freeze plan',
    'planning.step.complete': 'View ready plan',
    'planning.step.import': 'Import workbook',
    'planning.step.manualAssignments': 'Make manual assignments',
    'planning.step.configureWeeks': 'Configure weeks',
    'planning.step.autoAssign': 'Auto-assign',
    'serviceAuto.description':
      'Fill this cycle using its saved starting scores. Manual choices remain unchanged.',
    'serviceAuto.recover': 'Recover interrupted assignment',
    'serviceAuto.pendingAssignments':
      'Save or discard pending assignment changes before running automatic assignment.',
    'serviceAuto.planningNotReady':
      'Load the current planning data before running automatic assignment.',
    'serviceAuto.run': 'Assign services automatically',
    'serviceAuto.running': 'Updating service assignments…',
    'serviceAuto.refresh': 'Use corrected previous-cycle scores',
    'serviceAuto.refreshConfirm':
      'This replaces the input reference, keeps manual choices, and requires a new automatic pass and review.',
    'serviceAuto.input': 'Input scores: {{cycle}}',
    'serviceAuto.completed':
      'Assigned: {{assigned}}. Open slots: {{gaps}}. Review the services before freezing.',
    'serviceAuto.loadFailed':
      'Assignment state could not be loaded. Retry to get the current scores.',
    'serviceAuto.frozen':
      'This cycle is frozen. Unlock it in Plan cycles before changing assignments.',
    'serviceAuto.error.SCORE_INPUT_MISSING':
      'Starting scores are missing. Finalize the preceding cycle or import the reviewed starting state.',
    'serviceAuto.error.SCORE_PREDECESSOR_STALE':
      'The preceding cycle has changed. Finalize it, then explicitly refresh this cycle’s starting scores.',
    'serviceAuto.error.UNSUPPORTED_AUTO_SCOPE':
      'Automatic assignment uses standard two-month cycles. This range remains available for manual planning.',
    'serviceAuto.error.SERVICE_SCOPE_MISSING':
      'Set up and save the service slots here in Services before assigning them.',
    'serviceAuto.error.EMPTY_AUTO_SCOPE': 'There are no enabled service slots to assign.',
    'serviceAuto.error.AUTO_CONTEXT_STALE':
      'The planning data changed. Reload and review the current state before retrying.',
    'serviceAuto.error.AUTO_ASSIGNMENT_PENDING':
      'A previous automatic pass was interrupted. Recover it before editing services.',
    'serviceAuto.error.SCORE_RUN_REQUIRED':
      'Run automatic assignment again before reviewing this cycle.',
    'serviceAuto.error.AUTO_ASSIGNMENT_FOREIGN_OWNER':
      'An overlapping range owns automatic assignments here. Resolve that range before running this cycle.',
    'serviceAuto.context': 'Show assignment context',
    'serviceAuto.score': 'Score',
    'serviceAuto.weight': 'Weight',
    'serviceAuto.manualEmpty': 'Kept empty manually',
    'serviceAuto.return': 'Return to automatic selection',
    'serviceAuto.conflict':
      'This person already has a responsibility on this date. Confirm only if you intend to double-book them.',
    'serviceAuto.confirmConflict': 'Confirm same-day assignment',
    'serviceAuto.contextFailed':
      'Candidate context could not be loaded. Retry before making a decision.',
    'serviceAuto.reason.INACTIVE': 'Inactive',
    'serviceAuto.reason.NOT_ELIGIBLE': 'Not eligible',
    'serviceAuto.reason.DAY_INELIGIBLE': 'Not eligible for this meeting day',
    'serviceAuto.reason.ABSENT': 'Absent or not to be scheduled',
    'serviceAuto.reason.CONDITIONAL': 'Conditional availability; personal contact required',
    'serviceAuto.reason.SAME_DATE_CONFLICT': 'Already assigned on this date',
    'serviceAuto.available': 'Available',
    'serviceAuto.noCandidates': 'No publisher candidates are available.',
    'planning.step.manualGaps': 'Fill manual gaps',
    'planning.readiness.loading': 'Checking plan readiness…',
    'planning.readiness.notReviewed': 'Review readiness when the assignment work is complete.',
    'planning.readiness.ready': 'Ready to publish',
    'planning.readiness.readyWithUnassigned': 'Ready to freeze with unassigned parts',
    'planning.readiness.blocked': 'This plan still has blocking assignments',
    'planning.readiness.summary': '{{complete}} of {{total}} assignments complete.',
    'planning.issue.EMPTY_SCOPE': 'No planning assignments are stored in this scope.',
    'planning.issue.SCORE_PREDECESSOR_STALE':
      'Previous-cycle scores changed. Refresh this cycle’s input, rerun automatic assignment, and review again.',
    'planning.issue.SCORE_RUN_REQUIRED':
      'Run automatic assignment again after refreshing the input scores.',
    'planning.issue.SCORE_ASSIGNMENT_INVALID':
      'A saved service assignment is no longer eligible or available. Resolve it before freezing.',
    'planning.issue.AUTO_ASSIGNMENT_PENDING':
      'Automatic assignment is pending recovery. Retry the same pass before reviewing.',
    'planning.issue.MISSING_ASSIGNEE': 'An assignment still needs a publisher.',
    'planning.issue.INVALID_STATUS': 'An assignment has a status that cannot be published.',
    'planning.issue.open': 'Open',
    'planning.publication.none': 'No revision has been published for this scope.',
    'planning.publication.revision': 'Published revision {{revision}}',
    'planning.publication.unpublishedChanges': 'Unpublished changes',
    'planning.publication.review': 'Check assignments',
    'planning.publication.publish': 'Freeze assignment plan',
    'planning.publication.publishing': 'Publishing…',
    'planning.publication.confirmTitle': 'Freeze this assignment plan?',
    'planning.publication.confirmBody':
      'This creates an immutable snapshot of every assignment in the complete scope. Unlock it before making later edits.',
    'planning.publication.success': 'Revision {{revision}} was frozen.',
    'planning.publication.stale':
      'The draft changed after review. Review readiness again before publishing.',
    'planning.publication.error':
      'The plan could not be published. Your draft is unchanged; please try again.',
    'planning.frozen.title': 'Plan frozen',
    'planning.unlock.action': 'Unlock plan',
    'planning.unlock.confirmTitle': 'Unlock this plan?',
    'planning.unlock.confirmBody':
      'The frozen revision remains unchanged. Current draft changes become editable again.',
    'planning.autoAssign.unavailable':
      'Automatic assignment is not available yet. Continue by filling any manual gaps.',
    'servicePlanning.scopeTitle': 'Select a planning period',
    'servicePlanning.year': 'Year',
    'servicePlanning.period': 'Planning period',
    'servicePlanning.selectPeriod': 'Select a planning period',
    'servicePlanning.invalidPeriod': 'Select a valid year and planning period.',
    'servicePlanning.period1': 'Jan/Feb',
    'servicePlanning.period2': 'Mar/Apr',
    'servicePlanning.period3': 'May/Jun',
    'servicePlanning.period4': 'Jul/Aug',
    'servicePlanning.period5': 'Sep/Oct',
    'servicePlanning.period6': 'Nov/Dec',
    'servicePlanning.startDate': 'Start date',
    'servicePlanning.endDate': 'End date',
    'servicePlanning.loadRange': 'Load assignments',
    'servicePlanning.invalidRange': 'Choose an end date on or after the start date.',
    'servicePlanning.emptyTitle': 'No service assignments in this range',
    'servicePlanning.emptyBody': 'Add the first date and role to begin planning this range.',
    'servicePlanning.addRow': 'Add assignment row',
    'servicePlanning.date': 'Date',
    'servicePlanning.role': 'Service role',
    'servicePlanning.slot': 'Slot',
    'servicePlanning.publisher': 'Publisher',
    'servicePlanning.selectPublisher': 'Select an eligible publisher',
    'servicePlanning.save': 'Save assignment',
    'servicePlanning.unassign': 'Unassign',
    'servicePlanning.readOnly': 'You have read-only access to this plan.',
    'servicePlanning.loadFailed': 'The service assignments could not be loaded.',
    'servicePlanning.noSlotsForMeeting': 'No assignments configured for this meeting.',
    'servicePlanning.saveShort': 'Save',
    'servicePlanning.saveFailed': 'The assignment could not be saved.',
    'servicePlanning.selectionChanged':
      'This person is no longer available for this assignment. Please refresh the list.',
    'servicePlanning.optionsLoading': 'Loading available people…',
    'servicePlanning.optionsFailed': 'Could not load available people. Retry',
    'servicePlanning.noOptions': 'No available people for this service.',
    'servicePlanning.lammTask': 'Midweek meeting',
    'servicePlanning.assistant': 'assistant',
    'servicePlanning.automaticSection': 'Automatic assignment',
    'servicePlanning.sourceBusy': 'Planning is busy. Please try saving this assignment again.',
    'midweekPlanning.title': 'LaMM Planning',
    'midweekPlanning.subtitle': 'LaMM planning for one workbook cycle.',
    'midweekPlanning.description':
      'Use this category to load the workbook data for one planning cycle. Assignment of brothers and students can be added in the next phase.',
    'midweekPlanning.loadCycle': 'Fetch workbook data',
    'midweekPlanning.loadingCycle': 'Loading workbook...',
    'midweekPlanning.contextTitle': 'Workbook',
    'midweekPlanning.importOptionsTitle': 'Import Options',
    'midweekPlanning.year': 'Year',
    'midweekPlanning.workbook': 'Workbook',
    'midweekPlanning.meetingWeekday': 'Weekday',
    'midweekPlanning.weekendWeekday': 'Weekend',
    'midweekPlanning.workbookOption1': '1 - Jan/Feb',
    'midweekPlanning.workbookOption2': '2 - Mar/Apr',
    'midweekPlanning.workbookOption3': '3 - May/Jun',
    'midweekPlanning.workbookOption4': '4 - Jul/Aug',
    'midweekPlanning.workbookOption5': '5 - Sep/Oct',
    'midweekPlanning.workbookOption6': '6 - Nov/Dec',
    'midweekPlanning.weekday.monday': 'Monday',
    'midweekPlanning.weekday.tuesday': 'Tuesday',
    'midweekPlanning.weekday.wednesday': 'Wednesday',
    'midweekPlanning.weekday.thursday': 'Thursday',
    'midweekPlanning.weekday.friday': 'Friday',
    'midweekPlanning.weekday.saturday': 'Saturday',
    'midweekPlanning.weekday.sunday': 'Sunday',
    'midweekPlanning.loaded': 'Workbook cycle imported successfully. {{count}} weeks were stored.',
    'midweekPlanning.loadedAt': 'Last prepared action: {{time}}',
    'workbookReview.type.explainingBeliefs': 'Explaining your beliefs',
    'workbookReview.type.unknown': 'Unknown part type',
    'workbookReview.refreshSummary': 'Refresh programme structure in {{count}} weeks',
    'workbookReview.refreshIntro':
      'No assignments are saved in this cycle. Confirmation replaces the reviewed programme structure. Week settings and published plans are preserved.',
    'workbookReview.refresh': 'Refresh unassigned programme',
    'workbookReview.structure.added': 'Added programme item',
    'workbookReview.structure.removed': 'Removed programme item',
    'workbookReview.structure.replaced': 'Replaced programme item',
    'workbookReview.field.programOrder': 'Programme position',
    'workbookReview.field.sectionCode': 'Meeting section',
    'workbookReview.capacity':
      'This workbook is too large for a safe refresh. No changes were applied.',
    'workbookReview.refreshFailed':
      'The refresh could not be completed. Some complete weeks may already be updated. Fetch the workbook again to review the remaining changes.',
    'workbookReview.title': 'Review workbook updates',
    'workbookReview.summary': '{{weeks}} weeks checked; {{count}} rows have workbook changes.',
    'workbookReview.noChanges': 'No workbook changes. Saved assignments stay unchanged.',
    'workbookReview.preserve':
      'Assignees and partners stay assigned. Nothing changes until you apply these updates.',
    'workbookReview.blocked':
      'These changes cannot be applied. Existing entries and assignments are preserved.',
    'workbookReview.conflict.added': 'The source contains an additional part.',
    'workbookReview.conflict.removed': 'An existing part is missing from the source.',
    'workbookReview.conflict.identity': 'The part identity or program order changed.',
    'workbookReview.conflict.assignment':
      'A saved assignment is incompatible with the changed part.',
    'workbookReview.empty': 'None',
    'workbookReview.week': 'Week details',
    'workbookReview.before': 'Before',
    'workbookReview.after': 'After',
    'workbookReview.apply': 'Apply reviewed updates',
    'workbookReview.applying': 'Applying updates…',
    'workbookReview.cancel': 'Close review',
    'workbookReview.stale':
      'The workbook or saved plan changed. Fetch the workbook again to review the current changes.',
    'workbookReview.updateConflict':
      'The update is blocked. Check the cycle state and fetch the workbook again.',
    'workbookReview.applied':
      'Workbook updates applied to {{count}} entries. Saved assignments were preserved.',
    'workbookReview.song.opening': 'Opening',
    'workbookReview.song.middle': 'Middle',
    'workbookReview.song.closing': 'Closing',
    'workbookReview.field.weekStartDate': 'Week start',
    'workbookReview.field.weekEndDate': 'Week end',
    'workbookReview.field.weekendMeetingDate': 'Weekend meeting',
    'workbookReview.field.workbookWeekNumber': 'Workbook week',
    'workbookReview.field.language': 'Source language',
    'workbookReview.field.sourceUrl': 'Source',
    'workbookReview.field.jwLibraryUrl': 'JW Library reference',
    'workbookReview.field.songs': 'Songs',
    'workbookReview.field.readingProgram': 'Bible reading',
    'workbookReview.field.itemCount': 'Program items',
    'workbookReview.field.title': 'Title',
    'workbookReview.field.titleClean': 'Part title',
    'workbookReview.field.number': 'Part number',
    'workbookReview.field.durationMinutes': 'Duration (minutes)',
    'workbookReview.field.lesson': 'Lesson reference',
    'workbookReview.field.assignmentTypeCode': 'Part type',
    'workbookReview.field.songNumber': 'Song number',
    'midweekPlanning.importSummaryTitle': 'Imported workbook cycle',
    'midweekPlanning.importSummaryWeeks': '{{count}} meeting weeks stored.',
    'midweekPlanning.importSummaryRange': 'Date range: {{from}} to {{to}}',
    'midweekPlanning.importedTitle': 'Workbook import completed',
    'midweekPlanning.importedBody':
      'Workbook {{workbook}} for {{year}} is now available in the planning store with {{count}} weeks.',
    'midweekPlanning.errorDefault': 'The workbook import failed.',
    'midweekPlanning.sourceError':
      'The workbook source could not be loaded. Please try again later.',
    'midweekPlanning.weeksTitle': 'Workbook Weeks',
    'midweekPlanning.activeWorkbookLabel': 'Current workbook: {{workbook}} {{year}}',
    'midweekPlanning.loadingWeeks': 'Loading stored weeks...',
    'midweekPlanning.weeksEmpty': 'No weeks are stored yet for this workbook.',
    'midweekPlanning.weekLabel': 'Week {{number}}',
    'midweekPlanning.jwLibraryReference': 'JW Library',
    'midweekPlanning.jwLibraryReferenceLabel':
      'JW Library reference for week {{number}} (opens JW Library)',
    'midweekPlanning.jwWebsiteReference': 'jw.org',
    'midweekPlanning.jwWebsiteReferenceLabel':
      'jw.org reference for week {{number}} (opens in a new tab)',
    'midweekPlanning.weekRange': '{{from}}-{{to}}',
    'midweekPlanning.weekendMeetingDateLabel': 'Weekend meeting: {{date}}',
    'schedule.day': 'Day',
    'schedule.time': 'Start time',
    'schedule.duration': 'Duration: {{minutes}} minutes',
    'schedule.timezone': 'Times in {{timezone}}. Existing services move with the meeting.',
    'schedule.error.SCHEDULE_INVALID_DATE': 'Choose a valid date in this week.',
    'schedule.error.SCHEDULE_INVALID_DAY':
      'Choose Monday–Friday for the weekday meeting or Saturday–Sunday for the weekend meeting.',
    'schedule.error.SCHEDULE_INVALID_TIME':
      'Choose a valid local start time. Ambiguous or missing daylight-saving times are unavailable.',
    'schedule.error.SCHEDULE_INVALID_CYCLE': 'Reload this cycle before changing its schedule.',
    'schedule.error.SCHEDULE_INVALID_REQUEST': 'Reload the week before saving its schedule.',
    'schedule.error.SCHEDULE_STALE':
      'The week or its services changed. Reload before saving; your draft is retained.',
    'schedule.error.SCHEDULE_FROZEN':
      'Unlock the selected plan and affected service plans before moving this meeting.',
    'schedule.error.SCHEDULE_SERVICE_PERMISSION':
      'Moving existing services requires service write permission.',
    'schedule.error.SCHEDULE_ASSIGNEE_UNAVAILABLE':
      'An assigned person is unavailable on the new date. Resolve their assignment before moving the meeting.',
    'schedule.error.SCHEDULE_SLOT_CONFLICT':
      'The new date already has a conflicting service slot. Resolve it before moving the meeting.',
    'schedule.error.SCHEDULE_RANGE_CONFLICT':
      'The new date is outside a saved service range. Adjust that range before moving the meeting.',
    'schedule.error.SCHEDULE_TOO_LARGE':
      'This meeting has too many linked services to move in one save. Contact an administrator.',
    'schedule.error.SCHEDULE_DEFAULTS_UNAVAILABLE':
      'Meeting defaults could not be loaded. Retry loading this week.',
    'midweekPlanning.weekConfiguration.title': 'Week Configuration',
    'midweekPlanning.weekConfiguration.description':
      'This congregation week is shared by LaMM and Services. Edit it in Plan cycles.',
    'midweekPlanning.weekConfiguration.typeLabel': 'Configuration',
    'midweekPlanning.weekConfiguration.notesLabel': 'Notes',
    'midweekPlanning.weekConfiguration.notesPlaceholder': 'Optional note for this week',
    'midweekPlanning.weekConfiguration.sharedOwnerHint':
      'Shared planning data for LaMM and Services.',
    'midweekPlanning.weekConfiguration.save': 'Save Week Configuration',
    'midweekPlanning.weekConfiguration.saving': 'Saving...',
    'midweekPlanning.weekConfiguration.saved': 'Week configuration saved: {{type}}',
    'midweekPlanning.weekConfiguration.loadFailed': 'Failed to load week configuration',
    'midweekPlanning.weekConfiguration.saveFailed': 'Failed to save week configuration',
    'midweekPlanning.weekConfiguration.weekdayEnabled': 'Weekday meeting active',
    'midweekPlanning.weekConfiguration.weekdayDisabled': 'No weekday meeting',
    'midweekPlanning.weekConfiguration.weekendEnabled': 'Weekend meeting active',
    'midweekPlanning.weekConfiguration.weekendDisabled': 'No weekend meeting',
    'midweekPlanning.weekConfiguration.noWeekdayMeetingHint':
      'This week is configured with no weekday meeting. LaMM assignments are disabled for this week.',
    'midweekPlanning.section.tfgw': "Treasures from God's Word",
    'midweekPlanning.section.ayttfm': 'Apply Yourself to the Field Ministry',
    'midweekPlanning.section.lac': 'Living as Christians',
    'midweekPlanning.weekConfiguration.type.standard': 'Standard Week',
    'midweekPlanning.weekConfiguration.type.circuit_overseer_visit': 'Circuit Overseer Visit',
    'midweekPlanning.weekConfiguration.type.no_weekday_meeting': 'No Weekday Meeting',
    'midweekPlanning.weekConfiguration.type.no_weekend_meeting': 'No Weekend Meeting',
    'midweekPlanning.weekConfiguration.type.no_meetings': 'No meetings',
    'midweekPlanning.itemsStored': '{{count}} program items',
    'midweekPlanning.loadingWeekDetail': 'Loading meeting details...',
    'midweekPlanning.loadWeeksFailed': 'The stored workbook weeks could not be loaded.',
    'midweekPlanning.loadWeekFailed': 'The selected meeting week could not be loaded.',
    'midweekPlanning.itemType.song': 'Song',
    'midweekPlanning.itemType.prayer': 'Prayer',
    'midweekPlanning.itemType.chairman': 'Chairman',
    'midweekPlanning.durationMinutes': '{{count}} min',
    'midweekPlanning.lessonLabel': 'Lesson: {{lesson}}',
    'midweekPlanning.assigned': 'Assigned',
    'midweekPlanning.unassigned': 'Unassigned',
    'midweekPlanning.notAssignable': 'N/A',
    'midweekPlanning.assignmentLabel': 'Assignment',
    'midweekPlanning.mainAssigneeLabel': 'Assignment',
    'midweekPlanning.candidateWithCycleCount': '{{name}} ({{count}})',
    'midweekPlanning.conditionalAvailability.title': 'Conditional availability',
    'midweekPlanning.conditionalAvailability.contact': 'Contact the person before assigning.',
    'midweekPlanning.partnerLabel': 'Partner',
    'midweekPlanning.assistantLabel': 'Partner',
    'midweekPlanning.noAssigneeOption': 'Not assigned',
    'midweekPlanning.noAssistantOption': 'No partner',
    'midweekPlanning.noPartnerOption': 'No partner',
    'midweekPlanning.noAssignmentSelected': 'No assignment selected yet.',
    'midweekPlanning.noEligibleCandidates': 'No available eligible people.',
    'midweekPlanning.naOption': 'N/A',
    'midweekPlanning.savedAssignment': 'Saved assignment: {{assignee}}',
    'midweekPlanning.savedAssignmentWithPartner':
      'Saved assignment: {{assignee}} · Partner: {{assistant}}',
    'midweekPlanning.unknownPublisher': 'Unknown participant',
    'midweekPlanning.saveAssignment': 'Save assignment',
    'midweekPlanning.savingAssignment': 'Saving...',
    'midweekPlanning.saveAssignmentFailed': 'The meeting item assignment could not be saved.',
    'midweekPlanning.loadingPublishers': 'Loading publishers...',
    'midweekPlanning.loadPublishersFailed': 'The active publishers could not be loaded.',
    'midweekPlanning.loadingCandidates': 'Loading candidates...',
    'midweekPlanning.loadCandidatesFailed': 'The candidate list could not be loaded.',
    'midweekPlanning.decisionContext.title': 'Assignment context for {{name}}',
    'midweekPlanning.decisionContext.previousAssignments': 'Recent LaMM assignments',
    'midweekPlanning.decisionContext.thisWeek': 'This week',
    'midweekPlanning.decisionContext.assignmentDescription': '{{date}} · {{description}}',
    'midweekPlanning.decisionContext.loading': 'Loading assignment context...',
    'midweekPlanning.decisionContext.noPreviousAssignments': 'No recorded assignment.',
    'midweekPlanning.decisionContext.loadFailed': 'Assignment context could not be loaded.',
    'midweekPlanning.nextStepTitle': 'Planned next step',
    'midweekPlanning.nextStepBody':
      'The workbook import now scrapes and stores one cycle directly. The next step is to add week browsing and assignment screens for each meeting item.',
    'deepLink.validating': 'Validating access link...',
    'deepLink.error.noToken': 'No authentication token provided',
    'deepLink.error.invalid': 'Invalid or expired link',
    'deepLink.error.expired': 'This access link has expired. Please request a new one.',
    'deepLink.error.notFound': 'Invalid access link. Please check the link and try again.',
    'deepLink.error.invalidResponse': 'Invalid response from server',
    'deepLink.error.authFailed': 'Authentication failed',
    'deepLink.invalidTitle': 'Access Link Invalid',
    'deepLink.contactAdmin':
      'Please contact your congregation administrator for a new access link.',
    'deepLink.welcome': 'Welcome, {{name}}',
    'deepLink.portalTitle': 'Publisher Portal',
    'deepLink.manageAbsences': 'Manage Your Absences',
    'deepLink.manageSubtitle': 'Add and manage your upcoming absences',
    'deepLink.addAbsence': 'Add Absence',
    'deepLink.signOut': 'Sign Out',
    'deepLink.howToUse': 'How to use this portal',
    'deepLink.howToUse.1': 'Use "Add Absence" to notify about upcoming unavailability',
    'deepLink.howToUse.2': 'You can edit or delete upcoming absences',
    'deepLink.howToUse.3': 'Past absences cannot be modified',
    'deepLink.howToUse.4': 'Your absences will automatically sync with the congregation calendar',
    'publisherAbsenceForm.addTitle': 'Add New Absence',
    'publisherAbsenceForm.editTitle': 'Edit Absence',
    'publisherAbsenceForm.endDateOptional': 'End Date (Optional)',
    'publisherAbsenceForm.endDateHint': 'Leave empty for single-day absence',
    'publisherAbsenceForm.adding': 'Adding...',
    'publisherAbsenceForm.updating': 'Updating...',
    'publisherAbsenceForm.update': 'Update Absence',
    'publisherAbsenceForm.add': 'Add Absence',
    'publisherAbsenceForm.validation.startRequired': 'Start date is required',
    'publisherAbsenceForm.validation.endAfter': 'End date must be after start date',
    'publisherAbsenceForm.validation.pastLimit':
      'Start date cannot be more than 1 week in the past',
    'publisherAbsenceForm.validation.futureLimit':
      'Start date cannot be more than 6 months in the future',
    'publisherHistory.title': 'Your Absences',
    'publisherHistory.filter': 'Filter',
    'publisherHistory.all': 'All',
    'publisherHistory.upcoming': 'Upcoming',
    'publisherHistory.past': 'Past',
    'publisherHistory.loading': 'Loading your absences...',
    'publisherHistory.noMatch': 'No absences match your filter',
    'publisherHistory.empty': 'No absences found',
    'publisherHistory.emptyHint': 'Use the "Add Absence" button to create your first absence.',
    'publisherHistory.createdAt': 'Created {{date}}',
    'publisherHistory.cannotEditPast': 'Cannot edit past absences',
    'notFound.title': 'Page not found',
    'notFound.subtitle': "Sorry, we couldn't find the page you're looking for.",
    'notFound.goHome': 'Go back home',
  },
  de: {
    'publisherCalendar.subscribe': 'Abonnieren',
    'publisherCalendar.title': 'Persönlicher Kalender',
    'publisherCalendar.link': 'Abonnement-Link',
    'publisherCalendar.loading': 'Kalender-Link wird geladen…',
    'publisherCalendar.create': 'Abonnement-Link erstellen',
    'publisherCalendar.copy': 'Link kopieren',
    'publisherCalendar.copied': 'Link kopiert',
    'publisherCalendar.copyFailed':
      'Der Link konnte nicht kopiert werden. Markiere und kopiere ihn unten.',
    'publisherCalendar.rotate': 'Link neu erstellen',
    'publisherCalendar.revoke': 'Link widerrufen',
    'publisherCalendar.confirmRotate':
      'Diesen Link neu erstellen? Bestehende Abonnements werden nicht mehr aktualisiert. Teile den neuen Link, um den Kalender erneut zu abonnieren.',
    'publisherCalendar.confirmRevoke':
      'Diesen Link widerrufen? Bestehende Abonnements werden nicht mehr aktualisiert.',
    'publisherCalendar.confirm': 'Bestätigen',
    'publisherCalendar.cancel': 'Abbrechen',
    'publisherCalendar.empty': 'Es wurde noch kein Abonnement-Link erstellt.',
    'publisherCalendar.revoked': 'Dieser Abonnement-Link wurde widerrufen.',
    'publisherCalendar.failed':
      'Der Kalender-Link konnte nicht geladen oder geändert werden. Lade ihn erneut und versuche es noch einmal.',
    'publisherCalendar.retry': 'Link erneut laden',
    'publisherCalendar.guidance':
      'Enthält veröffentlichte LuDZ-Aufgaben, Dienste und Leitungen von Zusammenkünften für den Predigtdienst. Jeder mit diesem Link kann den Kalender lesen. Deine Kalender-App bestimmt, wann Änderungen angezeigt werden.',
    'fieldService.meetingType': 'Art des Treffpunkts',
    'fieldService.ordinary': 'Versammlungstreffpunkt',
    'fieldService.groups': 'Gruppentreffpunkte',
    'fieldService.notes': 'Notizen (optional)',
    'fieldService.addNotes': 'Notizen',
    'fieldService.notesPresent': 'Notiz vorhanden',
    'fieldService.invalidMeeting':
      'Gib für jeden normalen Treffpunkt eine gültige Beginnzeit ein und begrenze Notizen auf 500 Zeichen.',

    'absenceConflicts.title': 'Überschneidungen',
    'absenceConflicts.countOne': '1 betroffene Person',
    'absenceConflicts.count': '{{count}} betroffene Personen',
    'absenceConflicts.loading': 'Überschneidungen werden geladen…',
    'absenceConflicts.empty': 'Keine sich überschneidenden Abwesenheiten.',
    'absenceConflicts.loadError':
      'Überschneidungen konnten nicht geladen werden. Bitte erneut versuchen.',
    'absenceConflicts.edit': 'Abwesenheit für {{name}}, {{dates}} bearbeiten',
    'absenceConflicts.delete': 'Abwesenheit für {{name}}, {{dates}} löschen',
    'absenceConflicts.deleteConfirm':
      'Abwesenheit für {{name}} ({{dates}}) löschen? Dies kann nicht rückgängig gemacht werden.',
    'fieldService.assignmentRemoved': 'Diese Leitereinteilung wird nicht mehr aktiv sein.',
    'fieldService.weekCountOne': '1 Treffpunkt · {{assigned}} eingeteilt',
    'fieldService.title': 'Zusammenkünfte für den Predigtdienst',
    'fieldService.reviewPeriod': 'Zeitraum prüfen',
    'fieldService.releasePeriod': 'Zeitraum freigeben',
    'fieldService.unlockPeriod': 'Freigabe aufheben',
    'fieldService.releasedPeriod': 'Zeitraum freigegeben',
    'fieldService.draftPeriod': 'Zeitraum im Entwurf',
    'fieldService.revision': 'Revision {{revision}}',
    'fieldService.saveBeforeRelease': 'Änderungen vor der Prüfung speichern oder verwerfen.',
    'fieldService.releaseFailed':
      'Die Freigabe konnte nicht abgeschlossen werden. Bitte erneut versuchen.',
    'fieldService.releaseStale': 'Der Zeitraum wurde geändert. Bitte neu laden und erneut prüfen.',
    'fieldService.releaseOverlap':
      'Eine gemeinsame Woche weicht von einem anderen freigegebenen Zeitraum ab. Dessen Freigabe zuerst aufheben.',
    'fieldService.releaseLocked':
      'Diese Woche gehört zu einem freigegebenen Zeitraum. Vor Änderungen dessen Freigabe aufheben.',
    'fieldService.unlockConfirm':
      'Die Freigabe für diesen Zeitraum aufheben? Seine öffentlichen Wochen werden zurückgezogen, sofern kein anderer freigegebener Zeitraum sie weiterhin enthält.',
    'fieldService.reviewCount': '{{count}} Wochen in diesem Zeitraum',
    'fieldService.saveWeekFirst': 'Diese Woche vor der Freigabe speichern.',
    'fieldService.reviewInvalidWeek': 'Die gespeicherten Zuteilungen dieser Woche prüfen.',
    'fieldService.missingConductor': 'Kein Leiter ausgewählt',
    'fieldService.missingPlace': 'Kein Ort ausgewählt',
    'fieldService.releaseConfirm':
      'Diesen geprüften Zeitraum einschließlich nicht zugeteilter Treffpunkte freigeben?',
    'fieldService.help': 'Treffpunkte wochenweise planen und die Leiter manuell auswählen.',
    'fieldService.eligible': 'Als Leiter für Zusammenkünfte für den Predigtdienst geeignet',
    'fieldService.blueprint': 'Wöchentliche Vorlage für den Predigtdienst',
    'fieldService.placeNotSelected': 'Ort nicht ausgewählt',
    'fieldService.blueprintHelp':
      'Hier Wochentage und Uhrzeiten festlegen. Ort und Leiter werden für jeden Termin im Tab Predigtdienst ausgewählt. Geplante Wochen behalten ihre gespeicherten Angaben.',
    'fieldService.noBlueprint': 'Keine regelmäßigen Treffpunkte eingerichtet.',
    'fieldService.place': 'Ort',
    'fieldService.searchPlaces': 'Ort suchen',
    'fieldService.selectPlace': 'Ort auswählen',
    'fieldService.selectedPlace': 'Ausgewählter Ort',
    'fieldService.addPlace': 'Ort hinzufügen',
    'fieldService.noMatches': 'Keine passenden Orte.',
    'fieldService.noRecentPlaces': 'Keine kürzlich genutzten Orte.',
    'fieldService.noPlaces': 'Keine gespeicherten Orte.',
    'fieldService.name': 'Name des Orts',
    'fieldService.address': 'Adresse (optional)',
    'fieldService.directions': 'Hinweis (optional)',
    'fieldService.latitude': 'Breitengrad (optional)',
    'fieldService.longitude': 'Längengrad (optional)',
    'fieldService.optionalPlaceData':
      'Nur der Name ist erforderlich. Koordinaten bitte als vollständiges Paar eingeben.',
    'fieldService.reusePlace': '{{name}} verwenden',
    'fieldService.duplicatePlace': 'Ort bereits gespeichert. Bestehenden Ort auswählen.',
    'fieldService.coordinatesInvalid':
      'Breiten- und Längengrad gemeinsam eingeben oder beide leer lassen.',
    'fieldService.loadFailed':
      'Predigtdienstdaten konnten nicht geladen werden. Bitte erneut versuchen.',
    'fieldService.saveFailed':
      'Speichern nicht möglich. Die Eingaben bleiben erhalten. Bitte prüfen und erneut versuchen.',
    'fieldService.stale':
      'Woche oder Vorlage wurde geändert. Bitte aktualisieren, die gespeicherte Woche ausdrücklich laden und Änderungen erneut prüfen.',
    'fieldService.unavailable':
      'Der Leiter ist an diesem Datum nicht mehr geeignet oder verfügbar. Bitte Auswahl prüfen oder leeren.',
    'fieldService.saved': 'Woche gespeichert.',
    'fieldService.noMeetings': 'Keine Zusammenkünfte für den Predigtdienst in dieser Woche.',
    'fieldService.weekCount': '{{count}} Treffpunkte · {{assigned}} eingeteilt',
    'fieldService.conductor': 'Leiter',
    'fieldService.chooseConductor': 'Leiter auswählen',
    'fieldService.selectedConductor': 'Ausgewählter Leiter',
    'fieldService.unavailableShort': 'Nicht verfügbar',
    'fieldService.history': 'Einteilungshistorie',
    'fieldService.placesFailed': 'Orte nicht verfügbar.',
    'fieldService.useConductor': 'Ausgewählten Leiter übernehmen',
    'fieldService.clear': 'Leiter entfernen',
    'fieldService.date': 'Datum',
    'fieldService.addMeeting': 'Treffpunkt hinzufügen',
    'fieldService.removeMeeting': 'Treffpunkt entfernen',
    'fieldService.cancelMeeting': 'Treffpunkt absagen',
    'fieldService.restoreMeeting': 'Treffpunkt wiederherstellen',
    'fieldService.cancelled': 'Abgesagt',
    'fieldService.configureWeek': 'Wochenkonfiguration',
    'fieldService.saveWeek': 'Woche speichern',
    'fieldService.applyBlueprint': 'Aktuelle Vorlage anwenden',
    'fieldService.previewHelp':
      'Änderungen vor dem Speichern prüfen. Zusätzliche Treffpunkte und Absagen bleiben erhalten. Leiter werden immer manuell ausgewählt.',
    'fieldService.historyFailed': 'Einteilungshistorie nicht verfügbar.',
    'fieldService.periodCount': '{{count}} Einteilungen in diesem Zeitraum',
    'fieldService.conditional': 'Unter Vorbehalt · vorher Kontakt aufnehmen.',
    'fieldService.noCandidates': 'Keine geeigneten und verfügbaren Leiter an diesem Datum.',
    'fieldService.noHistory': 'Keine weiteren Einteilungen.',
    'fieldService.before': 'Vorher',
    'fieldService.after': 'Nachher',
    'fieldService.keepMeeting': 'Diesen Treffpunkt beibehalten',
    'fieldService.acknowledgeLoss':
      'Ich bestätige, dass durch diese Absagen die aktiven Leitereinteilungen entfallen.',
    'fieldService.usePreview': 'Vorschau in den Wochenentwurf übernehmen',
    'fieldService.useRefreshedWeek': 'Aktuelle gespeicherte Woche laden (Entwurf verwerfen)',
    'fieldService.timezone': 'Ortszeiten: {{timezone}}',
    'nav.fieldService': 'Predigtdienst',
    'appRole.read_only': 'Nur Lesen',
    'weeklyCopy.settings': 'Kopiereinstellungen',
    'weeklyCopy.format': 'Format der Wochenübersicht',
    'weeklyCopy.rtf': 'RTF (formatierter Text)',
    'weeklyCopy.markdown': 'Markdown',
    'weeklyCopy.whatsapp': 'WhatsApp',
    'weeklyCopy.formatHelp':
      'Gilt für die Kopierknöpfe bei LuDZ und Dienste. Wird für dein Benutzerkonto geräteübergreifend gespeichert.',
    'weeklyCopy.richHelp':
      'Fette Bezeichnungen bleiben erhalten, wenn dein Browser und die Zielanwendung formatierten Text unterstützen.',
    'weeklyCopy.loadFailed':
      'Dein Kopierformat konnte nicht geladen werden. Bitte versuche es erneut.',
    'weeklyCopy.saveFailed':
      'Dein Kopierformat konnte nicht gespeichert werden. Deine Auswahl bleibt erhalten. Bitte versuche es erneut.',
    'weeklyCopy.copy': 'Zusammenfassung für {{section}} kopieren',
    'weeklyCopy.button': 'Kopieren',
    'weeklyCopy.copied': 'Kopiert',
    'weeklyCopy.failed':
      'Kopieren fehlgeschlagen. Markiere die Zusammenfassung unten und kopiere sie manuell.',
    'weeklyCopy.plainFallback': 'Dies ist einfacher Text. Fettdruck ist hier nicht verfügbar.',
    'weeklyCopy.summary': 'Zusammenfassung zum Kopieren',

    'message.settings': 'Nachrichtenvorlage',
    'message.language': 'Sprache der Nachricht',
    'message.german': 'Deutsch',
    'message.english': 'Englisch',
    'message.languageHelp':
      'Für Datum und Bezeichnungen der Aufgaben. Dein Text bleibt unverändert.',
    'message.template': 'Deine Nachricht',
    'message.variables': 'Variable einfügen',
    'message.variable.firstname': 'Vorname',
    'message.variable.assignment': 'Aufgabe',
    'message.variable.date': 'Datum',
    'message.preview': 'Beispielvorschau',
    'message.previewUnavailable': 'Gib eine gültige Vorlage für die Vorschau ein.',
    'message.invalid':
      'Gib 1–2000 Zeichen ein. Erlaubte Variablen: {{firstname}}, {{assignment}}, {{date}}. Prüfe unvollständige oder unbekannte Variablen.',
    'message.saveFailed':
      'Die Vorlage konnte nicht gespeichert werden. Deine Änderungen bleiben erhalten. Bitte versuche es erneut.',
    'message.reset': 'Standard wiederherstellen',
    'message.cancel': 'Abbrechen',
    'message.save': 'Speichern',
    'message.saving': 'Wird gespeichert…',
    'message.loading': 'Deine Nachrichtenvorlage wird geladen…',
    'message.loadFailed':
      'Deine Nachrichtenvorlage konnte nicht geladen werden. Versuche es vor dem Vorbereiten einer Nachricht erneut.',
    'message.retry': 'Erneut versuchen',
    'message.prepare': 'Nachricht vorbereiten',
    'message.recipient': 'Nachricht an',
    'message.choosePerson': 'Person auswählen',
    'message.share': 'Nachricht teilen',
    'message.selectFor': '{{name}} auswählen',
    'message.copyFor': 'Nachricht für {{name}} kopieren',
    'message.shareFor': 'Nachricht für {{name}} teilen',
    'message.copy': 'Nachricht kopieren',
    'message.copied': 'Kopiert',
    'message.shareFailed':
      'Teilen war nicht möglich. Du kannst die Nachricht stattdessen kopieren.',
    'message.copyFailed':
      'Kopieren war nicht möglich. Markiere und kopiere diese Nachricht manuell.',
    'message.cannotPrepare':
      'Die Nachricht kann nicht vorbereitet werden. Prüfe die Vorlage und den Vornamen der Person.',
    'message.default':
      'Hallo {{firstname}},\nkönntest du am {{date}} {{assignment}} übernehmen?\nLiebe Grüße\n{{sender}}',
    'loading.refreshing': 'Wird aktualisiert…',
    'loading.stale':
      'Die angezeigten Informationen sind nicht aktuell. Vor dem Bearbeiten erneut versuchen.',
    'loading.codeFailed':
      'Dieser Teil der Anwendung konnte nicht geladen werden. Versuche es erneut, ohne deine Arbeit zu verlassen.',

    'assignment.open': 'Person zuweisen',
    'assignment.title': 'Person zuweisen',
    'assignment.confirm': 'Zuweisen: {{name}}',
    'assignment.undo': 'Zuweisung rückgängig machen',
    'assignment.empty': 'Für diese Aufgabe wurden keine geeigneten, verfügbaren Personen gefunden.',
    'assignment.saveFailed':
      'Die Zuweisung konnte nicht gespeichert werden. Lade den Vergleich neu, um die aktuelle Zuteilung vor einem weiteren Versuch zu prüfen.',
    'assignment.messageDefault':
      'Hallo {{firstname}},\nkönntest du am {{date}} die Aufgabe {{assignment}} übernehmen?\nLiebe Grüße\n{{sender}}',
    'replacement.confirm': 'Vertretung bestätigen: {{name}}',
    'replacement.undo': 'Letzte Vertretung rückgängig machen',
    'replacement.saving': 'Wird gespeichert…',
    'replacement.saveFailed':
      'Die Vertretung konnte nicht bestätigt werden. Lade den Vergleich neu, um die aktuelle Zuteilung vor einem weiteren Versuch zu prüfen.',
    'replacement.conditionalAvailability':
      '{{name}} ist unter Vorbehalt verfügbar. Bitte vor der Zuteilung persönlich kontaktieren.',
    'replacement.replacedOnDate':
      '{{name}} wurde an diesem Tag bei einer anderen Aufgabe vertreten. Für diese Aufgabe ist die Person möglicherweise weiterhin verfügbar.',
    'replacement.undoBeforeUnlock':
      'Mache die eingetragenen Vertretungen rückgängig, bevor du diesen Plan entsperrst.',
    'replacement.name': 'Name',
    'replacement.score': 'Punkte',
    'replacement.scoreValue': '{{score}} von 100 Punkten',
    'replacement.caution': 'Wichtigster Hinweis',
    'replacement.candidates': 'Verfügbare Personen',
    'replacement.caution.sameDay': 'Am selben Tag: {{task}}',
    'replacement.caution.sameTask.past': 'Gleiche Aufgabe {{days}} Tage davor',
    'replacement.caution.sameTask.future': 'Gleiche Aufgabe {{days}} Tage danach',
    'replacement.caution.otherTask.past': 'Zuteilung {{days}} Tage davor',
    'replacement.caution.otherTask.future': 'Zuteilung {{days}} Tage danach',
    'replacement.caution.workload': '{{count}} Zuteilungen · ±90 Tage',
    'replacement.caution.noHistory': 'Keine gespeicherten Zuteilungen',
    'replacement.caution.noNearby': 'Keine zeitnahen Zuteilungen',
    'replacement.caution.sameTask.past.one': 'Gleiche Aufgabe 1 Tag davor',
    'replacement.caution.sameTask.future.one': 'Gleiche Aufgabe 1 Tag danach',
    'replacement.caution.otherTask.past.one': 'Zuteilung 1 Tag davor',
    'replacement.caution.otherTask.future.one': 'Zuteilung 1 Tag danach',
    'replacement.caution.workload.one': '1 Zuteilung · ±90 Tage',
    'replacement.open': 'Vertretungen für {{name}} vergleichen',
    'replacement.title': 'Vertretungen vergleichen',
    'replacement.forPerson': 'Für {{name}}',
    'replacement.loadingTarget': 'Freigegebene Zuteilung wird geladen…',
    'replacement.loading': 'Verfügbare Personen und Zuteilungen werden ermittelt…',
    'replacement.search': 'Name suchen',
    'replacement.searchPlaceholder': 'Suchen',
    'replacement.count': '{{count}} verfügbar',
    'replacement.empty':
      'Für diese Aufgabe wurden keine geeigneten, verfügbaren Vertretungen gefunden.',
    'replacement.noMatches': 'Keine Namen passen zur Suche.',
    'replacement.refresh': 'Aktualisieren',
    'replacement.close': 'Schließen',
    'replacement.reload': 'Plan neu laden',
    'replacement.failed':
      'Der Vergleich konnte nicht vollständig geladen werden. Bitte erneut versuchen.',
    'replacement.conflicting':
      'Überlappende freigegebene Pläne widersprechen sich. Bitte die Pläne prüfen und den Vergleich erneut laden.',
    'replacement.unavailable':
      'Für diese freigegebene Zuteilung fehlen gespeicherte Angaben für den Vergleich. Vorhandene Planinformationen bleiben verfügbar.',
    'replacement.stale':
      'Der Plan wurde entsperrt oder eine neue Version wurde freigegeben. Bitte den Plan neu laden.',
    'replacement.forbidden':
      'Du hast keine Berechtigung, Vertretungen für diesen Plan zu vergleichen.',
    'replacement.domain.service': 'Dienstzuteilung',
    'replacement.domain.lamm': 'LuDZ-Zuteilung',
    'replacement.source.draft': 'Entwurf',
    'planning.cycle.period1': 'Jan/Feb',
    'planning.cycle.period2': 'Mär/Apr',
    'planning.cycle.period3': 'Mai/Jun',
    'planning.cycle.period4': 'Jul/Aug',
    'planning.cycle.period5': 'Sep/Okt',
    'planning.cycle.period6': 'Nov/Dez',
    'planning.cycle.label': 'Planungszyklus',
    'planning.cycle.select': 'Planungszyklus auswählen',
    'planning.cycle.open': 'Planungszyklus öffnen',
    'planning.cycle.frozen': 'Eingefroren · Version {{revision}}',
    'planning.cycle.error': 'Planungszyklen konnten nicht geladen werden.',
    'planning.cycle.empty': 'Keine aktiven Planungszyklen.',

    'midweekPlanning.releasedPrayer.opening': 'Anfangsgebet',
    'midweekPlanning.releasedPrayer.closing': 'Schlussgebet',
    'midweekPlanning.releasedAssignments': 'Freigegebene Zuteilungen',
    'midweekPlanning.releasedEmpty': 'Keine zuteilbaren Programmpunkte.',
    'midweekPlanning.releasedCancelled': 'Keine Zusammenkunft unter der Woche',
    'midweekPlanning.releasedPartner': 'Partner',
    'midweekPlanning.releasedPartnerUnassigned': 'Partner nicht zugeteilt',
    'midweekPlanning.releasedUnavailable':
      'Für diese ältere Freigabe ist keine Zuteilungsübersicht gespeichert. Vorhandene Plandateien sind weiterhin unter Planzyklen verfügbar.',
    'midweekPlanning.releasedFailed':
      'Die freigegebenen Zuteilungen konnten nicht geladen werden. Erneut versuchen, um diese Version zu laden.',
    'absenceForm.addPerson': 'Person hinzufügen',
    'absenceForm.removePerson': 'Person {{count}} entfernen',
    'absenceForm.additionalPerson': 'Person {{count}}',
    'absenceForm.peopleLimit': 'Bis zu 20 Personen können gemeinsam gespeichert werden.',
    'absenceForm.appliesToPeople': 'Gilt für alle {{count}} Personen',
    'absenceForm.savePeople': 'Für {{count}} Personen speichern',
    'absenceForm.saveMoreDates': 'Speichern und weitere Daten eingeben',
    'absenceForm.savedPeople':
      'Abwesenheiten für {{count}} Personen gespeichert. Weitere Daten für dieselben Personen eingeben.',
    'absenceForm.saveUncertain':
      'Das Speichern konnte nicht bestätigt werden. Die Eingaben sind gesperrt, um Duplikate zu vermeiden. Erneut speichern, um diesen Vorgang zu prüfen und abzuschließen.',
    'absenceForm.overlappingDates': 'Überschneidende Daten',
    'absenceResponses.title': 'Rückmeldungen',
    'absenceResponses.cycle': 'Planungszyklus',
    'absenceResponses.loading': 'Rückmeldungen werden geladen…',
    'absenceResponses.loadError':
      'Rückmeldungen konnten nicht geladen werden. Bitte erneut versuchen.',
    'absenceResponses.retry': 'Erneut versuchen',
    'absenceResponses.noCycles': 'Es sind keine importierten Planungszyklen verfügbar.',
    'absenceResponses.unavailable':
      'Die tatsächlichen Daten des vorherigen Zyklus fehlen. Importiere dessen Arbeitsheft, um diese Übersicht zu verwenden.',
    'absenceResponses.readOnly': 'Diese Übersicht ist schreibgeschützt.',
    'absenceResponses.noPublishers': 'Keine aktiven LuDZ-Teilnehmer.',
    'absenceResponses.progress': '{{completed}}/{{total}} erhalten · {{outstanding}} offen',
    'absenceResponses.complete': 'Alle Rückmeldungen erhalten.',
    'absenceResponses.completed': 'Erhaltene Rückmeldungen',
    'absenceResponses.absence_recorded': 'Abwesenheit erfasst',
    'absenceResponses.no_absences': 'Keine Abwesenheiten bestätigt',
    'absenceResponses.confirm': 'Keine Abwesenheiten',
    'absenceResponses.confirmFor': 'Keine Abwesenheiten für {{name}} bestätigen',
    'absenceResponses.undo': 'Bestätigung zurücknehmen',
    'absenceResponses.undoFor': 'Bestätigung für {{name}} zurücknehmen',
    'absenceResponses.saving': 'Wird gespeichert…',
    'absenceResponses.saveError':
      'Die Rückmeldung konnte nicht gespeichert werden. Bitte erneut versuchen.',

    'cycles.lifecycle.current': 'Aktuellen Planungszyklus öffnen',
    'cycles.lifecycle.loading': 'Zyklusstatus wird geladen…',
    'cycles.lifecycle.error':
      'Der Zyklusstatus konnte nicht geladen werden. Bearbeitung ist nicht verfügbar.',
    'cycles.lifecycle.retry': 'Erneut versuchen',
    'cycles.lifecycle.archived':
      'Archivierter Zyklus: schreibgeschützt. Vorhandene Dateien bleiben bis zur Aufbewahrungsbereinigung verfügbar.',
    'cycles.lifecycle.unavailable':
      'Dieser Zyklus ist gemäß der Aufbewahrungsregel nicht mehr verfügbar.',
    'cycles.lifecycle.empty': 'Keine aktiven Zyklen in diesem Jahr',

    'participation.progress': '{{assigned}} von {{total}} Aufgaben zugeteilt',
    'participation.remaining': '{{count}} offene Aufgaben',
    'participation.complete': 'Alle Aufgaben zugeteilt',
    'participation.noParts': 'Keine Aufgaben in diesem Zyklus zuzuteilen.',
    'participation.progressUnavailable': 'Der Zuteilungsfortschritt konnte nicht geladen werden.',
    'participation.cycle': 'Arbeitsheftzyklus {{year}} · {{workbook}}',
    'midweekPlanning.decisionContext.partnerHistory': 'Letzte Aufgaben als Partner',
    'midweekPlanning.decisionContext.noPartnerHistory': 'Keine bisherigen Aufgaben als Partner.',
    'midweekPlanning.decisionContext.partnerHistoryUnavailable':
      'Die Partnerhistorie ist nicht verfügbar.',
    'nav.congregation': 'Versammlung',
    'publisherForm.paused': 'Pausiert',
    'publisherForm.weight.inactive': 'Nicht aktiv',
    'publisherForm.weight.stronglyReduced': 'Stark reduziert',
    'publisherForm.weight.reduced': 'Reduziert',
    'publisherForm.weight.slightlyReduced': 'Leicht reduziert',
    'publisherForm.weight.standard': 'Standard',
    'publisherForm.group.opening': 'Beginn der Zusammenkunft',
    'publisherForm.group.treasures': 'Schätze aus Gottes Wort',
    'publisherForm.group.ministry': 'Uns im Dienst verbessern',
    'publisherForm.group.living': 'Unser Leben als Christ',
    'publisherForm.gender': 'Bruder / Schwester',
    'publisherForm.gender.unknown': 'Nicht angegeben',
    'publisherForm.gender.male': 'Bruder',
    'publisherForm.gender.female': 'Schwester',
    'participation.title': 'Zuteilungen in diesem Zyklus',
    'participation.explanation':
      'Die Farbe vergleicht die Anzahl innerhalb jeder Gruppe. Partner, Vorsitz und Gebete zählen mit.',
    'participation.elder': 'Älteste',
    'participation.ministerial_servant': 'Dienstamtgehilfen',
    'participation.brothers': 'Andere Brüder',
    'participation.sisters': 'Schwestern',
    'participation.unknown': 'Einordnung erforderlich',
    'participation.classify': 'Bruder / Schwester in der Verkündigerverwaltung festlegen.',
    'participation.sort': 'Teilnehmer sortieren',
    'participation.alphabetical': 'Alphabetisch',
    'participation.fewest': 'Wenigste zuerst',
    'participation.range': '0–{{max}} Zuteilungen',
    'participation.emptyGroup': 'Keine Teilnehmer',
    'participation.empty': 'Keine aktiven LuDZ-Teilnehmer.',
    'participation.loading': 'Zuteilungen des Zyklus werden geladen…',
    'participation.error': 'Die Zuteilungen des Zyklus konnten nicht geladen werden.',
    'participation.tile': '{{name}}: {{count}} Zuteilungen. Zuteilungen anzeigen.',
    'participation.details': '{{name}} · {{count}} Zuteilungen',
    'participation.none': 'Keine Zuteilungen in diesem Zyklus.',
    'participation.assignee': 'Programmteil',
    'participation.partner': 'Partner',
    'participation.close': 'Details schließen',

    'settings.location': 'Ort der Zusammenkünfte',
    'settings.locationHelp':
      'Gilt für neue Freigaben von LaMM- und Diensteplänen und deren Kalender. Bereits freigegebene Pläne behalten ihren Ort.',
    'settings.invalidLocation': 'Gib einen Ort in einer Zeile mit höchstens 500 Zeichen ein.',
    'settings.timezone': 'Zeitzone der Zusammenkünfte',
    'timezone.city.Pacific/Honolulu': 'Honolulu',
    'timezone.city.America/Anchorage': 'Anchorage',
    'timezone.city.America/Los_Angeles': 'Los Angeles',
    'timezone.city.America/Denver': 'Denver',
    'timezone.city.America/Phoenix': 'Phoenix',
    'timezone.city.America/Chicago': 'Chicago',
    'timezone.city.America/New_York': 'New York',
    'timezone.city.America/Halifax': 'Halifax',
    'timezone.city.America/St_Johns': 'St. John’s',
    'timezone.city.America/Sao_Paulo': 'São Paulo',
    'timezone.city.America/Argentina/Buenos_Aires': 'Buenos Aires',
    'timezone.city.Atlantic/Azores': 'Azoren',
    'timezone.city.UTC': 'UTC',
    'timezone.city.Europe/London': 'London',
    'timezone.city.Europe/Berlin': 'Berlin',
    'timezone.city.Europe/Helsinki': 'Helsinki',
    'timezone.city.Europe/Moscow': 'Moskau',
    'timezone.city.Africa/Cairo': 'Kairo',
    'timezone.city.Africa/Johannesburg': 'Johannesburg',
    'timezone.city.Asia/Dubai': 'Dubai',
    'timezone.city.Asia/Karachi': 'Karatschi',
    'timezone.city.Asia/Kolkata': 'Kolkata',
    'timezone.city.Asia/Kathmandu': 'Kathmandu',
    'timezone.city.Asia/Dhaka': 'Dhaka',
    'timezone.city.Asia/Bangkok': 'Bangkok',
    'timezone.city.Asia/Shanghai': 'Shanghai',
    'timezone.city.Asia/Tokyo': 'Tokio',
    'timezone.city.Australia/Perth': 'Perth',
    'timezone.city.Australia/Adelaide': 'Adelaide',
    'timezone.city.Australia/Sydney': 'Sydney',
    'timezone.city.Pacific/Auckland': 'Auckland',
    'settings.timezoneHelp':
      'Wähle eine Zeitzone. Örtliche Zusammenkunftstermine und Anfangszeiten bleiben gleich; eingefrorene Pläne behalten ihre gespeicherte Zeitzone.',
    'settings.invalidTimezone': 'Wähle eine andere Zeitzone und versuche es erneut.',
    'nav.congregationSettings': 'Einstellungen',
    'planAccess.title': 'Passwort für den Wochenplan',
    'planAccess.loading': 'Planzugriff wird geladen…',
    'planAccess.configured': 'Ein Passwort ist eingerichtet.',
    'planAccess.unconfigured': 'Noch kein Passwort eingerichtet. Der Wochenplan bleibt gesperrt.',
    'planAccess.password': 'Neues Passwort',
    'planAccess.confirmation': 'Passwort bestätigen',
    'planAccess.help': '8–128 Zeichen. Eine Änderung meldet alle Besucher des Wochenplans ab.',
    'planAccess.save': 'Planpasswort speichern',
    'planAccess.saved': 'Passwort gespeichert. Bisherige Plansitzungen wurden abgemeldet.',
    'planAccess.mismatch': 'Die Passwörter stimmen nicht überein.',
    'planAccess.invalid': 'Verwende 8–128 Zeichen mit mindestens einem Zeichen außer Leerzeichen.',
    'planAccess.stale':
      'Das Passwort wurde inzwischen geändert. Lade die Daten vor dem Speichern neu.',
    'planAccess.loadFailed':
      'Der Planzugriff konnte nicht geladen werden. Bitte lade die Daten erneut.',
    'settings.title': 'Versammlungseinstellungen',
    'settings.help':
      'Lege Vorgaben für neue Wochen fest. Bestehende Termine bleiben erhalten. Offizielle Zusammenkünfte werden in Planungszyklen bearbeitet, Predigtdiensttreffpunkte im eigenen Tab.',
    'settings.adminOnly': 'Nur Administratoren haben Zugriff.',
    'settings.loadFailed':
      'Versammlungseinstellungen konnten nicht geladen werden. Bitte erneut versuchen.',
    'settings.saveFailed': 'Speichern fehlgeschlagen. Prüfe die Werte und versuche es erneut.',
    'settings.stale':
      'Die Vorgaben wurden geändert. Lade die gespeicherten Werte, prüfe deinen Entwurf und speichere erneut.',
    'settings.reload': 'Gespeicherte Werte neu laden',
    'settings.save': 'Vorgaben speichern',
    'settings.saving': 'Wird gespeichert…',
    'settings.saved': 'Vorgaben gespeichert.',
    'settings.unsaved': 'Ungespeicherte Änderungen',
    'settings.unavailableConductor':
      'Der gespeicherte Leiter ist kein aktiver Ältester mehr. Behalte den Wert oder wähle einen Ersatz.',
    'settings.useDefault': 'Versammlungsvorgabe verwenden',
    'settings.resetPreview': '{{before}} → {{after}}. Speichere, um die Änderung anzuwenden.',

    'watchtower.defaultsTitle': 'Standardwerte für die Wochenendzusammenkunft',
    'watchtower.defaultHelp':
      'Wochen ohne Ausnahme verwenden den Wachtturm-Studium Leiter oder, wenn er nicht verfügbar ist, seinen Vertreter.',
    'watchtower.defaultConductor': 'Wachtturm-Studium Leiter',
    'watchtower.deputyConductor': 'Vertreter des Wachtturm-Studium-Leiters',
    'watchtower.reason.BOTH_UNAVAILABLE':
      'Weder der Wachtturm-Studium Leiter noch sein Vertreter ist an diesem Datum verfügbar. Bitte einen Leiter für diese Woche auswählen.',
    'watchtower.conductor': 'Wachtturm-Studium Leiter',
    'watchtower.part': 'Wachtturm-Studium',
    'watchtower.useDefaultName': 'Standard verwenden ({{name}})',
    'watchtower.useDefault': 'Standard verwenden',
    'watchtower.saveDefault': 'Standard speichern',
    'watchtower.saveConductor': 'Leiter speichern',
    'watchtower.clear': 'Zuteilung löschen',
    'watchtower.loadFailed':
      'Die Konfiguration des Wachtturm-Studiums konnte nicht geladen werden. Bitte erneut laden.',
    'watchtower.saveFailed':
      'Der Leiter konnte nicht gespeichert werden. Bitte Verfügbarkeit prüfen und erneut versuchen.',
    'watchtower.stale': 'Der Standard wurde geändert. Bitte vor dem Speichern erneut laden.',
    'watchtower.noElders': 'Keine aktiven Ältesten stehen zur Auswahl.',
    'watchtower.noAvailableElders':
      'Für diesen Zusammenkunftstermin sind keine aktiven Ältesten verfügbar.',
    'watchtower.unavailableChoice': '{{name}} (nicht verfügbar)',
    'watchtower.setupRequired':
      'Den Planungsumfang speichern, bevor ein Leiter für diese Woche ausgewählt wird.',
    'watchtower.source.default': 'Standard',
    'watchtower.source.override': 'Ausnahme',
    'watchtower.source.unassigned': 'Nicht zugeteilt',
    'watchtower.reason.NO_DEFAULT':
      'Kein Wachtturm-Studium Leiter festgelegt. Bitte einen Leiter für diese Woche auswählen.',
    'watchtower.reason.INELIGIBLE':
      'Der Leiter ist kein aktiver Ältester mehr. Bitte eine Vertretung auswählen.',
    'watchtower.reason.ABSENT':
      'Der Leiter ist an diesem Datum abwesend. Bitte eine Vertretung für diese Woche auswählen.',
    'publisherForm.service.WatchtowerStudy': 'Wachtturm-Studium',
    'cycles.unifiedDescription':
      'Arbeitsheft laden, Wochen vorbereiten, Zuteilungen prüfen, Dateien freigeben und per E-Mail veröffentlichen.',
    'cycles.release': 'Freigabe des Planungszyklus',
    'cycles.verify': 'Prüfen',
    'cycles.publish': 'Veröffentlichen',
    'cycles.publishEmails': 'E-Mails vorbereiten',
    'cycles.verifiedFrozen': 'Prüfungen sind vor der Freigabe des Zyklus verfügbar.',
    'cycles.releaseHelp':
      'Pläne prüfen und bestätigen, dann den Zyklus freigeben, um Dateien zu erstellen und herunterzuladen.',
    'cycles.publishHelp':
      'Den Zyklus freigeben und die Dateien erstellen, bevor die E-Mails vorbereitet werden.',
    'cycles.weekOverview': 'Wochenübersicht',
    'cycles.checkAll': 'Alle Pläne prüfen',
    'cycles.review.lamm': '1. LuDZ prüfen',
    'cycles.review.service': '2. Dienste prüfen',
    'cycles.reviewed': 'Geprüft',
    'cycles.freeze': 'Planungszyklus festschreiben',
    'cycles.frozen': 'Planungszyklus festgeschrieben',
    'cycles.unlock': 'Planungszyklus entsperren',
    'cycles.freezeConfirm':
      'LuDZ und Dienste gemeinsam festschreiben? Dateien und E-Mail-Versand bleiben separate Aktionen.',
    'cycles.unlockConfirm':
      'Beide Pläne zur Bearbeitung entsperren? Veröffentlichte Versionen bleiben erhalten.',
    'cycles.weekType': 'Konfiguration für {{date}}',
    'cycles.editWeek': 'Woche {{date}} bearbeiten',
    'cycles.saveWeek': 'Woche {{date}} speichern',
    'cycles.removeService': '{{role}} am {{date}} entfernen',
    'cycles.assignmentProgress': '{{assigned}} von {{total}} Programmteilen zugeteilt',
    'cycles.serviceAssignmentProgress': '{{assigned}} von {{total}} Diensten zugeteilt',
    'cycles.missingParts': 'Noch nicht zugeteilt',
    'cycles.allPartsAssigned': 'Alle Programmteile sind zugeteilt.',
    'cycles.noAssignableParts': 'Für diese Woche sind keine zuteilbaren Programmteile vorhanden.',
    'cycles.noWorkbookWeeks': 'Für diesen Zyklus sind noch keine Arbeitsheft-Wochen importiert.',
    'cycles.serviceOverview': 'Dienste',
    'cycles.weekdayMeeting': 'Wochentagszusammenkunft',
    'cycles.weekendMeeting': 'Wochenendzusammenkunft',
    'cycles.otherMeeting': 'Weiterer Termin',
    'cycles.additionalDates': 'Zusätzliche Termine außerhalb der Arbeitsheft-Wochen',
    'cycles.meetingDisabled': 'Diese Zusammenkunft findet nicht statt.',
    'cycles.noMeetingServices': 'Für diese Zusammenkunft sind keine Dienste angelegt.',
    'cycles.configureServices': 'Dienste einrichten',
    'cycles.closeServiceSetup': 'Dienste-Einrichtung schließen',
    'planning.files.plan.lamm': 'LuDZ-Plan',
    'planning.files.createLamm': 'LuDZ-Plan und S-89-Zettel erstellen',
    'planning.files.createService': 'Dienste-Plan erstellen',
    'planning.files.calendarStep': 'Persönlicher Kalender',
    'planning.files.createCalendar': 'Persönlichen Kalender erstellen',
    'planning.files.calendarReadyNotice':
      'Die gemeinsamen Kalender sind unten unter Persönliche Dateien verfügbar.',
    'planning.files.calendarNeedsPlans':
      'Zuerst beide Pläne erstellen, dann die persönlichen Kalender',
    'planning.files.plan.service': 'Dienste-Plan',
    'planning.files.identity': 'Zyklus {{scope}} · Version {{revision}}',
    'planning.files.personalTitle': 'Persönliche Dateien',
    'planning.files.personalHelp':
      'Personen mit Zuteilungen in diesen festgeschriebenen Plänen. Nach beiden Plänen wird ein gemeinsamer Kalender pro Person erstellt. S-89-Zettel gibt es nur für passende LuDZ-Programmteile.',
    'planning.files.unknownPerson': 'Teilnehmername nicht verfügbar',
    'planning.files.inventoryUnknown':
      'Die vollständige Übersicht persönlicher Dateien konnte nicht geprüft werden. Dateien oder Personen können noch fehlen.',
    'planning.files.noPeople': 'Für diese Pläne sind keine persönlichen Dateien erforderlich.',
    'planning.files.peopleCount': '{{count}} Personen',
    'planning.files.personName': 'Name',
    'planning.files.calendarHeader': 'ICS',
    'planning.files.s89Header': 'S-89',
    'planning.files.pendingPlans': 'Dateien noch nicht bereit: {{plans}}',
    'planning.files.downloadIcs': 'ICS für {{name}} herunterladen',
    'planning.files.downloadS89': 'S-89 für {{name}} herunterladen',
    'planning.files.notReady': 'Die erforderliche Datei ist noch nicht bereit.',
    'planning.files.notRequired': 'Für diese Person ist keine Datei dieses Typs erforderlich.',
    'planning.files.expectationUnknown': 'Die S-89-Zuordnung konnte nicht geprüft werden.',
    'planning.files.downloadHtml': '{{plan}} als HTML herunterladen',
    'planning.files.downloadPdf': '{{plan}} als PDF herunterladen',
    'planning.files.preview': '{{plan}} ansehen',
    'planning.files.failedStage': 'Fehlgeschlagene Phase: {{stage}}',
    'planning.files.job': 'Erstellungsreferenz',
    'planning.files.inputIssue': 'Prüfung des gespeicherten Plans',
    'planning.files.stage.prepare': 'Planvorbereitung und HTML',
    'planning.files.stage.pdf': 'Plan-PDF',
    'planning.files.stage.personal': 'Persönliche Kalender und S-89-Zettel',
    'planning.files.stage.commit': 'Abschließende Dateiprüfung',
    'planning.files.stage.unknown': 'Für diesen älteren Versuch nicht gespeichert',
    'planning.files.error.ARTIFACT_GENERATION_FAILED':
      'Die Erstellung ist fehlgeschlagen. Wiederholen oder den betroffenen Plan anhand der Referenz prüfen.',
    'planning.files.error.ARTIFACT_GENERATION_TIMEOUT':
      'Die Erstellung hat die Zeitgrenze überschritten.',
    'planning.files.error.S89_MAPPING_MISSING':
      'Ein LuDZ-Programmteil hat keine unterstützte S-89-Zuordnung. Den Aufgabentyp prüfen und eine neue Version festschreiben.',
    'planning.files.error.S89_PART_NUMBER_MISSING':
      'Bei einem S-89-Programmteil fehlt die Nummer im festgeschriebenen Plan.',
    'planning.files.error.CAPTURE_PARTICIPANT_MISSING':
      'Ein Teilnehmer fehlt im gespeicherten Plankontext.',
    'planning.files.error.S89_TEMPLATE_MISSING': 'Die S-89-Vorlage ist nicht verfügbar.',
    'planning.files.error.S89_TEMPLATE_FIELDS_MISSING':
      'Die erforderlichen Felder fehlen in der S-89-Vorlage.',
    'planning.files.error.INVALID_RENDER_CONTEXT':
      'Der gespeicherte Kontext für die Erstellung ist unvollständig oder ungültig.',
    'planning.files.error.ARTIFACT_REVISION_MISMATCH':
      'Der Erstellungsauftrag passt nicht zur festgeschriebenen Version.',
    'planning.files.error.ARTIFACT_TEMPLATE_UNAVAILABLE':
      'Die angeforderte Planvorlage ist nicht verfügbar.',
    'planning.files.error.ARTIFACT_SET_INCOMPLETE':
      'Es wurden nicht alle erforderlichen Dateien erstellt.',
    'planning.files.error.ARTIFACT_CHECKSUM_MISMATCH':
      'Eine Datei hat die Integritätsprüfung nicht bestanden.',
    'planning.files.error.CAPTURE_MEETING_DATE_INVALID':
      'Ein gespeichertes Zusammenkunftsdatum ist ungültig.',
    'cycles.title': 'Planungszyklen',
    'cycles.description':
      'Planungsbereiche vorbereiten, gemeinsame Wochen konfigurieren, Zuteilungen prüfen und eingefrorene Pläne verwalten.',
    'cycles.domains': 'Planungsbereiche',
    'cycles.serviceSetup': 'Dienstzeitraum und Plätze',
    'cycles.slots': 'Dienste',
    'cycles.addSlot': 'Platz hinzufügen',
    'cycles.removeSlot': 'Platz entfernen',
    'cycles.saveSlots': 'Plätze speichern',
    'cycles.saved': 'Plätze gespeichert.',
    'cycles.filled': 'Besetzt',
    'cycles.legacyPreview':
      '{{count}} frühere Zuteilung(en) werden zur Übernahme vorgeschlagen. Beim Speichern bleiben die Verkündiger unverändert.',
    'cycles.noServiceSlots': 'In diesem Zeitraum finden keine Zusammenkünfte statt.',
    'cycles.invalidSlot': 'Ein Datum im Zeitraum und eine gültige Platzkennung auswählen.',
    'cycles.duplicateSlot': 'Diese Kombination aus Datum, Dienst und Platz ist bereits vorhanden.',
    'cycles.staleScope': 'Der Zeitraum wurde anderswo geändert. Vor dem Speichern erneut laden.',
    'cycles.saveFailed': 'Die Plätze konnten nicht gespeichert werden.',
    'cycles.loadFailed': 'Der ausgewählte Planungsbereich konnte nicht geladen werden.',
    'cycles.loadScope': 'Zeitraum laden',
    'cycles.retry': 'Erneut versuchen',
    'cycles.overlapNotice':
      'Überlappende Zeiträume teilen sich Zuteilungen mit gleichem Datum, Dienst und Platz.',
    'cycles.sharedWeeks': 'Wochenkonfiguration',
    'cycles.openSetup': 'In Planungszyklen öffnen',
    'cycles.fillService': 'Dienstzuteilungen ausfüllen',
    'cycles.fillLamm': 'LuDZ-Zuteilungen ausfüllen',
    'planning.issue.SERVICE_SCOPE_MISSING': 'Vor der Prüfung die Dienstplätze speichern.',
    'planning.issue.MEETING_DATE_UNCLASSIFIED':
      'Für dieses Datum ist keine Zusammenkunftszeit konfiguriert.',
    'planning.distribution.lamm': 'Leben und Dienst',
    'planning.distribution.service': 'Dienstplan',
    'planning.distribution.title': 'Geprüfte Pläne senden',
    'planning.distribution.instructions':
      'Wähle fertige Pläne und Empfänger und prüfe alle Anhänge vor dem Senden.',
    'planning.distribution.domain': 'Planart',
    'planning.distribution.scope': 'Zeitraum',
    'planning.distribution.revision': 'Version',
    'planning.distribution.find': 'Fertige Dateien suchen',
    'planning.distribution.add': '{{locale}}-Dateien für Version {{revision}} hinzufügen',
    'planning.distribution.remove': 'Entfernen',
    'planning.distribution.recipients': 'Empfänger',
    'planning.distribution.recipientCount':
      'Empfänger hinzufügen oder ändern ({{selected}} von {{total}} ausgewählt)',
    'planning.distribution.defaultExplanation':
      'Empfohlen sind aktive LuDZ-Teilnehmer und aktive Verkündiger mit einer Aufgabe in einem der ausgewählten Pläne. Weitere aktive Verkündiger kannst du unten hinzufügen.',
    'planning.distribution.restoreRecommended': 'Empfohlene Empfänger auswählen',
    'planning.distribution.emptyDefault':
      'Keine empfohlenen Empfänger für diese Pläne. Du kannst weitere aktive Verkündiger auswählen.',
    'planning.distribution.manualRecipients':
      'Für diese älteren Pläne ist keine automatische Empfängerauswahl verfügbar. Wähle die Empfänger manuell aus.',
    'planning.distribution.peopleError':
      'Die vollständige Empfängerliste konnte nicht geladen werden. Versuche es vor der Versandprüfung erneut.',
    'planning.distribution.retryPeople': 'Empfänger erneut laden',
    'planning.distribution.selectAll': 'Alle auswählen',
    'planning.distribution.clearAll': 'Alle abwählen',
    'planning.distribution.noRecipients': 'Keine aktiven Verkündiger verfügbar.',
    'planning.distribution.emailLocale': 'E-Mail-Sprache',
    'planning.distribution.review': 'Sendung prüfen',
    'planning.distribution.sender': 'Absender',
    'planning.distribution.expires': 'Vorschau gültig bis',
    'planning.distribution.confirm': 'Ich habe Empfänger, Pläne und Anhänge geprüft.',
    'planning.distribution.send': 'Geprüfte E-Mail senden',
    'planning.distribution.batch': 'Versandstatus',
    'planning.distribution.batchSummary': '{{sent}} von {{total}} vom E-Mail-Anbieter angenommen',
    'planning.distribution.queued': 'In Warteschlange',
    'planning.distribution.sending': 'Wird gesendet',
    'planning.distribution.sent': 'Vom E-Mail-Anbieter angenommen',
    'planning.distribution.failed': 'Fehlgeschlagen',
    'planning.distribution.unknown': 'Annahme unklar — nicht ohne Prüfung erneut senden',
    'planning.distribution.selectRetry': 'Für Wiederholung auswählen',
    'planning.distribution.retry': 'Ausgewählte fehlgeschlagene Empfänger erneut senden',
    'planning.distribution.sendUncertain':
      'Das Versandergebnis ist unklar. Bitte nicht erneut senden, bis die Sendung geprüft wurde.',
    'planning.distribution.disabled': 'Der E-Mail-Versand ist deaktiviert.',
    'planning.distribution.stale':
      'Die Vorschau ist abgelaufen oder hat sich geändert. Bitte erneut prüfen.',
    'planning.distribution.denied': 'Du hast keine Berechtigung, diese Pläne zu senden.',
    'planning.distribution.error':
      'Prüfung oder Versand fehlgeschlagen. Bitte Auswahl prüfen und erneut versuchen.',
    'planning.artifacts.title': 'Plandateien',
    'planning.artifacts.locale': 'Dokumentsprache',
    'planning.artifacts.generate': 'Dateien erstellen',
    'planning.artifacts.submitting': 'Dateien werden zur Erstellung angemeldet…',
    'planning.artifacts.accepted':
      'Erstellungsanfrage angenommen. Der Status erscheint gleich hier.',
    'planning.artifacts.readyNotice': 'Die Dateien sind unten verfügbar.',
    'planning.artifacts.regenerate': 'Dateien neu erstellen',
    'planning.artifacts.regenerateHelp':
      'Neue Dateien aus dieser festgeschriebenen Version erstellen. Bisherige Dateien bleiben verfügbar.',
    'planning.artifacts.fileSet': 'Dateisatz',
    'planning.artifacts.empty': 'Für diese Version wurden noch keine Dateien erstellt.',
    'planning.artifacts.legacy':
      'Fixiere eine neue Version, um die erforderlichen Daten zu erfassen.',
    'planning.artifacts.queued': 'In der Warteschlange',
    'planning.artifacts.running': 'Wird erstellt',
    'planning.artifacts.ready': 'Bereit',
    'planning.artifacts.failed': 'Erstellung fehlgeschlagen',
    'planning.artifacts.retry': 'Erstellung wiederholen',
    'planning.artifacts.download': 'Herunterladen',
    'planning.artifacts.preview': 'Planvorschau',
    'planning.artifacts.finalPlanActions': 'Formate des fertigen Plans',
    'planning.artifacts.viewHtml': 'Fertigen Plan ansehen (HTML)',
    'planning.artifacts.downloadPdf': 'Fertigen Plan herunterladen (PDF)',
    'planning.artifacts.closePreview': 'Vorschau schließen',
    'planning.artifacts.error':
      'Dateien konnten nicht geladen oder erstellt werden. Prüfe die Konfiguration und versuche es erneut.',
    'planning.artifacts.denied': 'Du hast keine Berechtigung für diese Dateien.',
    'planning.artifacts.disabled': 'Die Dateierstellung ist derzeit deaktiviert.',
    'planning.artifacts.kind.html': 'Plan als HTML',
    'planning.artifacts.kind.pdf': 'Plan als PDF',
    'planning.artifacts.kind.s89': 'Persönliche S-89',
    'planning.artifacts.kind.ics': 'Persönlicher Kalender',
    'common.appName': 'CongPlanner',
    'common.loading': 'Wird geladen...',
    'common.cancel': 'Abbrechen',
    'common.retry': 'Erneut versuchen',
    'common.closeDialog': 'Dialog schließen',
    'common.confirm': 'Bestätigen',
    'common.processing': 'Wird verarbeitet...',
    'common.error': 'Fehler',
    'common.dismiss': 'Schließen',
    'common.save': 'Speichern',
    'common.language': 'Sprache',
    'common.english': 'Englisch',
    'common.german': 'Deutsch',
    'common.refresh': 'Aktualisieren',
    'common.from': 'Von',
    'common.to': 'Bis',
    'common.type': 'Typ',
    'common.status': 'Status',
    'common.active': 'Aktiv',
    'common.inactive': 'Inaktiv',
    'common.created': 'Erstellt',
    'common.completed': 'Abgeschlossen',
    'common.filters': 'Filter',
    'common.noFilters': 'Keine Filter aktiv',
    'common.row': 'Zeile',
    'nav.dashboard': 'Dashboard',
    'nav.planCycles': 'Planungszyklen',
    'nav.servicePlanning': 'Dienste',
    'nav.midweekMeetingPlanning': 'LuDZ',
    'nav.publishers': 'Verkündiger',
    'nav.absences': 'Abwesenheiten',
    'nav.export': 'Export',
    'nav.userManagement': 'Benutzer',
    'nav.accountMenu': 'Benutzermenü',
    'nav.account': 'Benutzerkonto',
    'nav.logout': 'Abmelden',
    'nav.openMenu': 'Navigationsmenü öffnen',
    'nav.closeMenu': 'Navigationsmenü schließen',
    'login.title': 'Bei CongPlanner anmelden',
    'login.subtitle': 'Planungssystem für die Versammlung',
    'login.mockAuthEnabled': 'Mock-Authentifizierung ist für die lokale Entwicklung aktiviert.',
    'login.mockAuthUse': 'Verwende {{email}} / {{password}}',
    'login.email': 'E-Mail-Adresse',
    'login.emailPlaceholder': 'E-Mail-Adresse eingeben',
    'login.password': 'Passwort',
    'login.passwordPlaceholder': 'Passwort eingeben',
    'login.submit': 'Anmelden',
    'login.submitting': 'Anmeldung läuft...',
    'login.support':
      'Bei technischem Support wende dich an den IT-Administrator deiner Versammlung',
    'login.error.default': 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.',
    'login.error.invalidCredentials':
      'Ungültige E-Mail-Adresse oder ungültiges Passwort. Bitte prüfe deine Angaben und versuche es erneut.',
    'login.error.tooManyAttempts':
      'Zu viele Anmeldeversuche. Bitte warte einige Minuten und versuche es erneut.',
    'login.error.server': 'Serverfehler. Bitte versuche es in Kürze erneut.',
    'login.error.network': 'Netzwerkfehler. Bitte prüfe deine Verbindung und versuche es erneut.',
    'login.validation.emailRequired': 'E-Mail-Adresse ist erforderlich',
    'login.validation.emailInvalid': 'Bitte gib eine gültige E-Mail-Adresse ein',
    'login.validation.passwordRequired': 'Passwort ist erforderlich',
    'login.validation.passwordLength': 'Das Passwort muss mindestens 6 Zeichen lang sein',
    'login.firstLogin.title': 'Dauerhaftes Passwort festlegen',
    'login.firstLogin.subtitle': 'Erste Anmeldung abschließen',
    'login.firstLogin.instructions':
      'Ersetze das vorläufige Passwort durch ein dauerhaftes Passwort. Verwende mindestens 12 Zeichen mit Groß- und Kleinbuchstaben, einer Zahl und einem Sonderzeichen.',
    'login.firstLogin.newPassword': 'Neues Passwort',
    'login.firstLogin.confirmPassword': 'Neues Passwort bestätigen',
    'login.firstLogin.submit': 'Passwort festlegen und anmelden',
    'login.firstLogin.back': 'Zurück zur Anmeldung',
    'login.firstLogin.error':
      'Das Passwort konnte nicht geändert werden. Fordere eine neue Einladung an, falls das vorläufige Passwort abgelaufen ist.',
    'login.firstLogin.validation.policy':
      'Verwende mindestens 12 Zeichen mit Groß- und Kleinbuchstaben, einer Zahl und einem Sonderzeichen.',
    'login.firstLogin.validation.mismatch': 'Die Passwörter stimmen nicht überein.',
    'login.recovery.forgotPassword': 'Passwort vergessen?',
    'login.recovery.title': 'Passwort zurücksetzen',
    'login.recovery.subtitle': 'Stelle den Zugriff auf dein Konto wieder her',
    'login.recovery.requestInstructions':
      'Gib deine E-Mail-Adresse ein. Wenn sie für die Kontowiederherstellung verwendet werden kann, sendet Cognito einen Code. Falls kein Code ankommt, wende dich an deinen Administrator.',
    'login.recovery.sendCode': 'Code senden',
    'login.recovery.codeSent':
      'Falls das Konto wiederhergestellt werden kann, wurde ein Code an die bestätigte E-Mail-Adresse gesendet. Prüfe auch den Spam-Ordner.',
    'login.recovery.code': 'Bestätigungscode',
    'login.recovery.codeRequired': 'Gib den Code aus der E-Mail ein.',
    'login.recovery.resend': 'Neuen Code senden',
    'login.recovery.resetPassword': 'Neues Passwort festlegen',
    'login.recovery.success':
      'Dein Passwort wurde zurückgesetzt. Du kannst dich jetzt mit dem neuen Passwort anmelden.',
    'login.recovery.backToSignIn': 'Zurück zur Anmeldung',
    'login.recovery.invalidCode':
      'Der Code ist ungültig oder abgelaufen. Fordere einen neuen Code an und versuche es erneut.',
    'login.recovery.requestError':
      'Die Passwortwiederherstellung konnte nicht gestartet werden. Versuche es später erneut oder wende dich an deinen Administrator.',
    'login.recovery.confirmError':
      'Das Passwort konnte nicht zurückgesetzt werden. Prüfe Code und Passwort und versuche es erneut.',
    'login.firstLogin.profileError': 'Erforderliche Profilangaben fehlen oder sind ungültig.',
    'login.firstLogin.givenNameRequired': 'Vorname ist erforderlich.',
    'login.firstLogin.familyNameRequired': 'Nachname ist erforderlich.',
    'protected.refreshing': 'Sitzung wird aktualisiert...',
    'protected.sessionUnavailable': 'Sitzung kann nicht aktualisiert werden',
    'protected.sessionRetryDescription':
      'Deine gespeicherte Anmeldung ist weiterhin vorhanden. Prüfe deine Verbindung und versuche es erneut.',
    'protected.retrySession': 'Erneut versuchen',
    'protected.accessDenied': 'Zugriff verweigert',
    'protected.permissionDenied': 'Deine aktuelle Rolle darf diese Seite nicht aufrufen.',
    'protected.requiresRole':
      'Für diese Seite ist {{role}}-Zugriff erforderlich. Deine aktuelle Rolle ist {{currentRole}}.',
    'protected.goBack': 'Zurück',
    'role.admin': 'Administrator',
    'role.publisher': 'Verkündiger',
    'appRole.admin': 'Admin',
    'appRole.absence_management': 'Abwesenheitsverwaltung',
    'appRole.service_management': 'Dienstverwaltung',
    'appRole.lamm_overseer': 'LuDZ-Aufseher',
    'appRole.planner': 'Predigtdienstplaner',
    'appRole.overseer': 'Aufseher',
    'dashboard.title': 'Dashboard',
    'dashboard.totalPublishers': 'Verkündiger gesamt',
    'dashboard.upcomingAbsences': 'Bevorstehende Abwesenheiten',
    'dashboard.recentActivity': 'Letzte Aktivitäten',
    'dashboard.shortcuts': 'Schnellzugriff',
    'home.thisWeek': 'Diese Woche',
    'home.openPublicPlan': 'Öffentlichen Wochenplan öffnen (neuer Tab)',
    'home.loadFailed': 'Diese Woche konnte nicht geladen werden.',
    'home.namesFailed': 'Die Namen der Verkündiger konnten nicht geladen werden.',
    'home.notReleased':
      'Für diese Woche ist kein Plan freigegeben. Öffne das Modul, um die Planung anzusehen.',
    'home.noAssignments': 'Keine Zuteilungen in dieser Woche.',
    'home.noAbsences': 'Keine Abwesenheiten in dieser Woche.',
    'dashboard.shortcutsEmpty': 'Für dein Konto sind keine Module verfügbar.',
    'dashboard.openShortcut': 'Modul öffnen',
    'dashboard.servicePlanning': 'Dienstplanung',
    'dashboard.servicePlanningDesc':
      'Die vorhandene Planungs-Lambda und den bestehenden Workflow für Dienstplanungszyklen auslösen.',
    'dashboard.midweekMeetingPlanning': 'LuDZ-Planung',
    'dashboard.midweekMeetingPlanningDesc':
      'Einen Arbeitsbuch-Zyklus für die LuDZ laden und spätere Zuteilungen vorbereiten.',
    'dashboard.activity.absenceUpdated': 'Abwesenheit für {{name}} aktualisiert',
    'dashboard.activity.publisherUpdated': 'Verkündiger {{name}} aktualisiert',
    'dashboard.activity.exportGenerated': 'Datenexport für {{period}} erstellt',
    'dashboard.activity.empty': 'Noch keine aktuellen Aktivitäten.',
    'userManagement.title': 'Benutzerverwaltung',
    'userManagement.subtitle':
      'Benutzer erstellen, Zugänge aktivieren oder deaktivieren und Anwendungsrollen zuweisen.',
    'userManagement.addUser': 'Benutzer hinzufügen',
    'userManagement.createTitle': 'Benutzer erstellen',
    'userManagement.usersTitle': 'Benutzer',
    'userManagement.email': 'E-Mail',
    'userManagement.givenName': 'Vorname',
    'userManagement.familyName': 'Nachname',
    'userManagement.congregation': 'Versammlung',
    'userManagement.role': 'Rolle',
    'userManagement.status': 'Status',
    'userManagement.accountSetup': 'Kontoeinrichtung',
    'userManagement.setupRequired': 'Vorläufiges Passwort: Ersteinrichtung erforderlich',
    'userManagement.setupComplete': 'Ersteinrichtung abgeschlossen',
    'userManagement.setupUnknown': 'Einrichtungsstatus nicht verfügbar',
    'userManagement.actions': 'Aktionen',
    'userManagement.user': 'Benutzer',
    'userManagement.create': 'Erstellen',
    'userManagement.enable': 'Aktivieren',
    'userManagement.disable': 'Deaktivieren',
    'userManagement.enabled': 'Aktiviert',
    'userManagement.disabled': 'Deaktiviert',
    'userManagement.loadFailed': 'Benutzer konnten nicht geladen werden.',
    'userManagement.createFailed': 'Benutzer konnte nicht erstellt werden.',
    'userManagement.updateFailed': 'Benutzer konnte nicht aktualisiert werden.',
    'userManagement.roleUpdateFailed': 'Benutzerrolle konnte nicht aktualisiert werden.',
    'userManagement.empty': 'Keine Benutzer gefunden.',
    'publishers.title': 'Verkündiger',
    'publishers.subtitle':
      'Verkündigerverzeichnis und Informationen verwalten ({{count}} Verkündiger)',
    'publishers.subtitleIdle': 'Verkündigerverzeichnis und Informationen verwalten',
    'publishers.dashboard.total': 'Verkündiger gesamt',
    'publishers.dashboard.active': 'Aktive Verkündiger',
    'publishers.dashboard.elders': 'Älteste',
    'publishers.dashboard.ministerialServants': 'Dienstamtgehilfen',
    'publishers.add': 'Verkündiger hinzufügen',
    'publishers.import': 'CSV importieren',
    'publishers.importing': 'Import läuft...',
    'publishers.search': 'Verkündiger suchen',
    'publishers.noMatches': 'Keine Verkündiger entsprechen deiner Suche.',
    'publishers.searchPlaceholder': 'Verkündiger nach Name oder E-Mail suchen...',
    'publishers.clear': 'Zurücksetzen',
    'publishers.noResults': 'Keine Verkündiger entsprechen deinen Kriterien.',
    'publishers.empty': 'Keine Verkündiger gefunden. Füge deinen ersten Verkündiger hinzu.',
    'publishers.unloaded': 'Verkündigerdaten werden nur auf deine Anforderung geladen.',
    'publishers.load': 'Aktive Verkündiger laden',
    'publishers.loading': 'Aktive Verkündiger werden geladen...',
    'publishers.retry': 'Verkündiger erneut laden',
    'publishers.registry': 'Verkündiger',
    'publishers.contact': 'Kontakt',
    'publishers.typeRole': 'Typ und Rolle',
    'publishers.baptismDate': 'Taufdatum',
    'publishers.actions': 'Aktionen',
    'publishers.edit': 'Verkündiger bearbeiten',
    'publishers.delete': 'Verkündiger löschen',
    'publishers.deleteTitle': 'Verkündiger löschen',
    'publishers.deleteMessage':
      'Möchtest du {{name}} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'publishers.filters.publisherType': 'Verkündigertyp',
    'publishers.filters.congregationRole': 'Aufgabe in der Versammlung',
    'publishers.filters.state': 'Status',
    'publishers.filters.allTypes': 'Alle Typen',
    'publishers.filters.allRoles': 'Alle Rollen',
    'publishers.filters.all': 'Alle',
    'publishers.importFormatTitle': 'CSV-Importformat',
    'publishers.importFormatRequired': 'Erforderliche Spalten: {{columns}}',
    'publishers.importFormatOptional':
      'Optionale Spalten: {{columns}}. Verwende | oder ;, um mehrere Listenwerte zu trennen.',
    'publishers.importLastResult': 'Ergebnis des letzten Imports',
    'publishers.importSummary':
      '{{created}} erstellt, {{updated}} aktualisiert, {{unchanged}} unverändert, {{failed}} fehlgeschlagen ({{total}} gesamt).',
    'publishers.importSelectJson': 'Bitte wähle eine JSON-Datei aus.',
    'publishers.importMalformed': 'Die ausgewählte Datei enthält kein gültiges JSON.',
    'publishers.importEmpty': 'Das Dokument enthält keine Verkündiger.',
    'publishers.importUnsupported': 'Nur Version 1 des Verkündiger-Imports wird unterstützt.',
    'publishers.importExample': 'JSON-Beispiel',
    'publishers.importErrors': 'Zeilen mit Fehlern',
    'publishers.importErrorsShowing': 'Es werden die ersten 10 von {{count}} Fehlern angezeigt.',
    'publishers.importSelectCsv': 'Bitte wähle eine CSV-Datei aus.',
    'publishers.importFailed': 'Verkündiger konnten nicht importiert werden.',
    'publishers.loadFailed': 'Verkündiger konnten nicht geladen werden',
    'publishers.deleteFailed': 'Verkündiger konnte nicht gelöscht werden',
    'publishers.queryPrompt': 'Verwende Suche oder Filter, um Verkündiger zu finden.',
    'publisherType.publisher': 'Verkündiger',
    'publisherType.pioneer': 'Pionier',
    'congregationRole.none': 'Keine',
    'congregationRole.ministerial_servant': 'Dienstamtgehilfe',
    'congregationRole.elder': 'Ältester',
    'publisherForm.addTitle': 'Neuen Verkündiger hinzufügen',
    'publisherForm.editTitle': 'Verkündiger bearbeiten',
    'publisherForm.firstName': 'Vorname',
    'publisherForm.lastName': 'Nachname',
    'publisherForm.birthDate': 'Geburtsdatum',
    'publisherForm.baptismDate': 'Taufdatum',
    'publisherForm.address': 'Adresse',
    'publisherForm.street': 'Straße und Hausnummer',
    'publisherForm.city': 'Ort',
    'publisherForm.state': 'Bundesland',
    'publisherForm.zipCode': 'Postleitzahl',
    'publisherForm.phoneNumber': 'Telefonnummer',
    'publisherForm.emailAddress': 'E-Mail-Adresse',
    'publisherForm.appointedRole': 'Ernannte Aufgabe',
    'publisherForm.serviceEligibility': 'Diensteignung',
    'publisherForm.frequency': 'Zuteilungsgewicht',
    'publisherForm.frequencyHelp':
      '0 schließt diesen Dienst aus. Positive Werte steuern den Punkteanstieg: 0,5 bedeutet die halbe Standardrate, 1 die Standardrate. Der Wert ist kein Anteil an Zusammenkünften.',
    'publisherForm.weekdays': 'Wochentags',
    'publisherForm.weekends': 'Wochenende',
    'publisherForm.qualified': 'Ja',
    'publisherForm.notQualified': 'Nein',
    'publisherForm.temporarilyUnavailable': 'Vorübergehend pausiert',
    'publisherForm.part.chairman': 'Vorsitz',
    'publisherForm.part.prayer': 'Gebet',
    'publisherForm.part.treasuresTalk': 'Schätze aus Gottes Wort',
    'publisherForm.part.spiritualGems': 'Nach geistigen Schätzen graben',
    'publisherForm.part.bibleReading': 'Bibellesung',
    'publisherForm.part.initialCall': 'Gespräche beginnen',
    'publisherForm.part.returnVisit': 'Interesse fördern',
    'publisherForm.part.makeDisciples': 'Menschen zu Jüngern machen',
    'publisherForm.part.ayttfmTalk': 'Vortrag',
    'publisherForm.part.partner': 'Partner',
    'publisherForm.part.livingAsChristians': 'Unser Leben als Christ',
    'publisherForm.part.congregationBibleStudy': 'Versammlungsbibelstudium',
    'publisherForm.service.ChairmanWEM': 'Vorsitz',
    'publisherForm.service.Reader': 'Leser',
    'publisherForm.service.Video': 'Video',
    'publisherForm.service.Audio': 'Audio',
    'publisherForm.service.AttendantHall': 'Saalordner',
    'publisherForm.service.AttendantEntrance': 'Eingangsordner',
    'publisherForm.service.AttendantZoom': 'Zoom Ordner',
    'publisherForm.service.Stage': 'Bühne',
    'publisherForm.service.Microphone': 'Mikrofon',
    'publisherForm.publisherType': 'Verkündigertyp',
    'publisherForm.congregationRole': 'Aufgabe in der Versammlung',
    'publisherForm.lammEligibility': 'LuDZ-Befähigung',
    'publisherForm.lammEligibilityDescription':
      'Teilnahme und Befähigung für jede LuDZ-Aufgabe pflegen.',
    'publisherForm.lammParticipant': 'Teilnehmer',
    'publisherForm.privileges': 'Vorrechte',
    'publisherForm.assignments': 'Zuteilungen',
    'publisherForm.privilegePlaceholder': 'Vorrecht hinzufügen...',
    'publisherForm.assignmentPlaceholder': 'Zuteilung hinzufügen...',
    'publisherForm.addItem': 'Hinzufügen',
    'publisherForm.create': 'Verkündiger erstellen',
    'publisherForm.update': 'Verkündiger aktualisieren',
    'publisherForm.saving': 'Wird gespeichert...',
    'publisherForm.saveFailed':
      'Der Verkündiger konnte nicht gespeichert werden. Deine Änderungen bleiben erhalten. Bitte versuche es erneut.',
    'publisherForm.privilege.sound': 'Ton',
    'publisherForm.privilege.platform': 'Bühne',
    'publisherForm.privilege.microphones': 'Mikrofone',
    'publisherForm.privilege.literature': 'Literatur',
    'publisherForm.privilege.accounts': 'Konten',
    'publisherForm.privilege.territories': 'Gebiete',
    'publisherForm.privilege.schoolOverseer': 'Schuldiener',
    'publisherForm.privilege.serviceOverseer': 'Dienstaufseher',
    'publisherForm.privilege.secretary': 'Sekretär',
    'publisherForm.privilege.coordinator': 'Koordinator',
    'publisherForm.assignment.watchtowerStudy': 'Wachtturm-Studium',
    'publisherForm.assignment.theocraticMinistrySchool': 'LuDZ',
    'publisherForm.assignment.serviceMeeting': 'Dienstzusammenkunft',
    'publisherForm.assignment.publicTalk': 'Öffentlicher Vortrag',
    'publisherForm.assignment.circuitAssembly': 'Kongress in der Rundreise',
    'publisherForm.assignment.regionalConvention': 'Regionalkongress',
    'publisherForm.validation.firstName': 'Vorname ist erforderlich',
    'publisherForm.validation.lastName': 'Nachname ist erforderlich',
    'publisherForm.validation.birthDate': 'Geburtsdatum ist erforderlich',
    'publisherForm.validation.baptismDate': 'Taufdatum ist erforderlich',
    'publisherForm.validation.street': 'Straße ist erforderlich',
    'publisherForm.validation.city': 'Ort ist erforderlich',
    'publisherForm.validation.state': 'Bundesland ist erforderlich',
    'publisherForm.validation.zipCode': 'Postleitzahl ist erforderlich',
    'publisherForm.validation.phoneRequired': 'Telefonnummer ist erforderlich',
    'publisherForm.validation.phoneInvalid': 'Bitte gib eine gültige Telefonnummer ein',
    'publisherForm.validation.emailRequired': 'E-Mail-Adresse ist erforderlich',
    'publisherForm.validation.emailInvalid': 'Bitte gib eine gültige E-Mail-Adresse ein',
    'absences.title': 'Abwesenheiten',
    'absences.add': 'Abwesenheit hinzufügen',
    'absences.import': 'CSV importieren',
    'absences.importing': 'Import läuft...',
    'absences.total': 'Abwesenheiten gesamt',
    'absences.absentPublishers': 'Abwesende Verkündiger',
    'absences.absentTypeCount': 'Abwesend',
    'absences.conditionalCount': 'Unter Vorbehalt',
    'absences.importFormatTitle': 'CSV-Importformat',
    'absences.importFormatRequired': 'Erforderliche Spalten: {{columns}}',
    'absences.importFormatIdentifier':
      'Mindestens eine Spalte zur Verkündigerzuordnung angeben: {{columns}}',
    'absences.importFormatOptional': 'Optionale Spalten: {{columns}}',
    'absences.importLastResult': 'Ergebnis des letzten Imports',
    'absences.importSummary':
      '{{imported}} von {{total}} Zeilen importiert. {{failed}} fehlgeschlagen.',
    'absences.importErrors': 'Zeilen mit Fehlern',
    'absences.importErrorsShowing': 'Es werden die ersten 10 von {{count}} Fehlern angezeigt.',
    'absences.importSelectCsv': 'Bitte wähle eine CSV-Datei aus.',
    'absences.importFailed': 'Abwesenheiten konnten nicht importiert werden.',
    'absences.loadFailed': 'Daten konnten nicht geladen werden',
    'absences.loadAbsencesFailed': 'Abwesenheiten konnten nicht geladen werden',
    'absences.createFailed': 'Abwesenheit konnte nicht erstellt werden',
    'absences.createOverlapWarning':
      'In diesem Zeitraum besteht bereits eine Abwesenheit. Bitte die Einträge klären, auflösen oder manuell zusammenführen.',
    'absences.updateFailed': 'Abwesenheit konnte nicht aktualisiert werden',
    'absences.deleteFailed': 'Abwesenheit konnte nicht gelöscht werden',
    'absences.queryPrompt': 'Verwende Filter, um Abwesenheiten zu finden.',
    'absenceCalendar.title': 'Abwesenheitskalender',
    'absenceCalendar.month': 'Monat',
    'absenceCalendar.empty': 'Keine Abwesenheiten in diesem Monat.',
    'absenceCalendar.loadFailed': 'Abwesenheiten konnten nicht geladen werden.',
    'absenceCalendar.retry': 'Erneut versuchen',
    'absenceCalendar.barDescription': '{{name}}: {{start}} – {{end}}',
    'absenceType.selectOptional': 'Typ auswählen (optional)',
    'absenceSubscriptions.title': 'Kalender abonnieren',
    'absenceSubscriptions.guidance':
      'Jeder mit dem Link kann den Kalender lesen. Zum Abonnieren den HTTPS-Link in die Kalender-App kopieren. Änderungen werden etwa eine Stunde gesammelt und dann veröffentlicht. Die App bestimmt, wann sie Aktualisierungen abruft.',
    'absenceSubscriptions.appointed': 'Älteste und Dienstamtgehilfen',
    'absenceSubscriptions.all': 'Alle',
    'absenceSubscriptions.elders': 'Älteste',
    'absenceSubscriptions.ministerialServants': 'Dienstamtgehilfen',
    'absenceSubscriptions.otherBrothers': 'Andere Brüder',
    'absenceSubscriptions.sisters': 'Schwestern',
    'absenceSubscriptions.copy': 'Link kopieren',
    'absenceSubscriptions.subscribe': 'Abonnieren',
    'absenceSubscriptions.copied': 'Link kopiert.',
    'absenceSubscriptions.copyFailed':
      'Kopieren fehlgeschlagen. Den Link oben markieren und kopieren.',
    'absenceSubscriptions.unavailable': 'Kalenderabonnements sind noch nicht verfügbar.',
    'absenceType.talk': 'Vortrag',
    'absenceType.absent': 'Abwesend',
    'absenceType.conditional': 'Unter Vorbehalt',
    'absenceType.do_not_schedule': 'Nicht einplanen',
    'absenceForm.addTitle': 'Neue Abwesenheit hinzufügen',
    'absenceForm.editTitle': 'Abwesenheit bearbeiten',
    'absenceForm.publisher': 'Verkündiger',
    'absenceForm.selectPublisher': 'Verkündiger auswählen',
    'absenceForm.absenceType': 'Abwesenheitstyp',
    'absenceForm.startDate': 'Startdatum',
    'absenceForm.endDate': 'Enddatum',
    'absenceForm.endDateHint': 'Für eine eintägige Abwesenheit leer lassen',
    'absenceForm.create': 'Speichern',
    'absenceForm.saveAnother': 'Speichern und weitere hinzufügen',
    'absenceForm.savedOverlapWarning':
      'Die Abwesenheit wurde gespeichert, aber für diesen Zeitraum besteht bereits eine Abwesenheit. Bitte die Einträge klären, bereinigen oder manuell zusammenführen.',
    'absenceForm.savedAnother':
      'Abwesenheit gespeichert. Weitere für denselben Verkündiger hinzufügen.',
    'absenceForm.saveFailed':
      'Die Abwesenheit konnte nicht gespeichert werden. Bitte erneut versuchen.',
    'absenceForm.previousMonth': 'Vorheriger Monat',
    'absenceForm.nextMonth': 'Nächster Monat',
    'absenceForm.chooseDate': 'Auswählen',
    'absenceForm.unavailableDate': 'Nicht verfügbar',
    'absenceForm.update': 'Abwesenheit aktualisieren',
    'absenceForm.saving': 'Wird gespeichert...',
    'absenceForm.validation.publisher': 'Verkündiger ist erforderlich',
    'absenceForm.validation.startDate': 'Startdatum ist erforderlich',
    'absenceForm.validation.invalidDate': 'Bitte ein gültiges Datum eingeben.',
    'absenceForm.validation.endDateAfter': 'Das Enddatum muss nach dem Startdatum liegen',
    'absenceForm.validation.pastLimit':
      'Das Startdatum darf nicht mehr als 2 Jahre in der Vergangenheit liegen',
    'absenceForm.validation.futureLimit':
      'Das Startdatum darf nicht mehr als 1 Jahr in der Zukunft liegen',
    'absenceForm.upcomingTitle': 'Kommende Abwesenheiten',
    'absenceForm.upcomingLoading': 'Kommende Abwesenheiten werden geladen…',
    'absenceForm.upcomingEmpty': 'Keine kommenden Abwesenheiten.',
    'absenceForm.upcomingError':
      'Kommende Abwesenheiten konnten nicht geladen werden. Diese Abwesenheit kann trotzdem erstellt werden.',
    'absenceForm.overlapWarning':
      'In diesem Zeitraum besteht bereits eine Abwesenheit. Bitte die Einträge klären, auflösen oder manuell zusammenführen.',
    'absenceList.filters': 'Filter',
    'absenceList.clearFilters': 'Filter zurücksetzen',
    'absenceList.publisher': 'Verkündiger',
    'absenceList.publisherGroups': 'Verkündigergruppen',
    'absenceList.allGroups': 'Alle Gruppen',
    'absenceGroup.elder': 'Älteste',
    'absenceGroup.ministerial_servant': 'Dienstamtgehilfen',
    'absenceGroup.brothers': 'Brüder',
    'absenceGroup.sisters': 'Schwestern',
    'absenceList.allPublishers': 'Alle Verkündiger',
    'absenceList.allTypes': 'Alle Typen',
    'absenceList.dateRange': 'Datumsbereich',
    'absenceList.startDate': 'Startdatum',
    'absenceList.endDate': 'Enddatum',
    'absenceList.loading': 'Abwesenheiten werden geladen...',
    'absenceList.noMatch': 'Keine Abwesenheiten entsprechen deinen Filtern',
    'absenceList.empty': 'Keine Abwesenheiten gefunden',
    'absenceList.unknownPublisher': 'Unbekannter Verkündiger',
    'absenceList.edit': 'Abwesenheit bearbeiten',
    'absenceList.delete': 'Abwesenheit löschen',
    'absenceList.deleteTitle': 'Abwesenheit löschen',
    'absenceList.deleteMessage':
      'Möchtest du diese Abwesenheit für {{name}} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'absenceList.pageIndicator': 'Seite {{page}}',
    'absenceList.previousPage': 'Zurück',
    'absenceList.nextPage': 'Weiter',
    'export.title': 'Datenexport',
    'export.subtitle': 'Abwesenheitsdaten in verschiedenen Formaten exportieren',
    'export.new': 'Neuer Export',
    'export.loadFailed': 'Exportverlauf konnte nicht geladen werden',
    'export.failed': 'Export fehlgeschlagen. Bitte versuche es erneut.',
    'export.downloadFailed': 'Download fehlgeschlagen. Bitte versuche es erneut.',
    'export.dialog.title': 'Daten exportieren',
    'export.dialog.subtitle': 'Einstellungen für den Datenexport konfigurieren',
    'export.dialog.format': 'Exportformat',
    'export.dialog.filters': 'Filter',
    'export.dialog.startDate': 'Startdatum',
    'export.dialog.endDate': 'Enddatum',
    'export.dialog.publisher': 'Verkündiger',
    'export.dialog.allPublishers': 'Alle Verkündiger',
    'export.dialog.absenceType': 'Abwesenheitstyp',
    'export.dialog.cancel': 'Abbrechen',
    'export.dialog.generate': 'Export erstellen',
    'export.dialog.generating': 'Export wird erstellt...',
    'export.format.csvDesc': 'Kommagetrennte Werte für Tabellenkalkulationen',
    'export.format.jsonDesc': 'JavaScript Object Notation zur programmatischen Nutzung',
    'export.format.excelDesc': 'Microsoft-Excel-Format mit formatierten Tabellen',
    'export.history.title': 'Exportverlauf',
    'export.history.loading': 'Exportverlauf wird geladen...',
    'export.history.emptyTitle': 'Noch keine Exporte',
    'export.history.emptySubtitle': 'Erstelle deinen ersten Datenexport, um ihn hier zu sehen.',
    'export.history.pending': 'Ausstehend',
    'export.history.processing': 'In Bearbeitung',
    'export.history.completed': 'Abgeschlossen',
    'export.history.failed': 'Fehlgeschlagen',
    'export.history.unknown': 'Unbekannt',
    'export.history.unknownSize': 'Unbekannte Größe',
    'export.history.format': 'Format',
    'export.history.size': 'Größe',
    'export.history.created': 'Erstellt',
    'export.history.completedAt': 'Abgeschlossen',
    'export.history.filters': 'Filter',
    'export.history.error': 'Fehler',
    'export.history.download': 'Herunterladen',
    'servicePlanning.edit': 'Zuteilung bearbeiten',
    'serviceAuto.undoDescription':
      'Zurücksetzen entfernt automatische und manuelle Dienstzuteilungen. Die Ausgangspunkte bleiben erhalten.',
    'serviceAuto.undo': 'Dienstzuteilungen zurücksetzen',
    'serviceAuto.recoverUndo': 'Rückgängig machen erneut versuchen',
    'servicePlanning.weekCounts': '{{assigned}} zugeteilt · {{open}} offen',
    'servicePlanning.title': 'Dienste-Planung',
    'servicePlanning.subtitle':
      'Den bestehenden Workflow für die Dienstplanung aus dem Web-Tool starten.',
    'servicePlanning.description':
      'Den Zyklus automatisch zuteilen und manuelle Änderungen prüfen.',
    'servicePlanning.blueprint':
      'Jede Zusammenkunft übernimmt die üblichen Dienste. Termine oder Absagen werden in Planungszyklen → Wochenkonfiguration geändert.',
    'servicePlanning.noConfigurationWeeks':
      'Keine Wochenkonfigurationen verfügbar. Diesen Zeitraum erneut laden.',
    'servicePlanning.noMeetings': 'In diesem Zeitraum finden keine Zusammenkünfte statt.',
    'servicePlanning.trigger': 'Dienstplanung starten',
    'servicePlanning.triggering': 'Wird gestartet...',
    'servicePlanning.triggered':
      'Der Auslöser für den Dienstplanungs-Workflow ist vorbereitet. Als Nächstes kann die Backend-Anbindung an die bestehende Lambda erfolgen.',
    'servicePlanning.nextStepTitle': 'Aktueller Umfang',
    'servicePlanning.nextStepBody':
      'Die Kategorie und die Auslöseaktion sind jetzt vorhanden. Als Nächstes wird dieser Button mit dem bestehenden Workflow-Endpunkt verbunden.',
    'planning.progressLabel': 'Planungsfortschritt',
    'planning.step.scope': 'Umfang wählen',
    'planning.step.assign': 'Aufgaben zuteilen',
    'planning.step.review': 'Zuteilungen prüfen',
    'planning.step.publish': 'Plan einfrieren',
    'planning.step.complete': 'Fertigen Plan ansehen',
    'planning.step.import': 'Arbeitsbuch importieren',
    'planning.step.manualAssignments': 'Manuelle Zuteilungen vornehmen',
    'planning.step.configureWeeks': 'Wochen konfigurieren',
    'planning.step.autoAssign': 'Automatisch zuteilen',
    'serviceAuto.description':
      'Diesen Zyklus mit seinen gespeicherten Ausgangspunkten zuteilen. Manuelle Entscheidungen bleiben erhalten.',
    'serviceAuto.recover': 'Unterbrochene Zuteilung wiederherstellen',
    'serviceAuto.pendingAssignments':
      'Ausstehende Zuteilungsänderungen zuerst speichern oder verwerfen, bevor automatisch zugeteilt wird.',
    'serviceAuto.planningNotReady':
      'Aktuelle Planungsdaten laden lassen, bevor automatisch zugeteilt wird.',
    'serviceAuto.run': 'Dienste automatisch zuteilen',
    'serviceAuto.running': 'Dienstzuteilungen werden aktualisiert…',
    'serviceAuto.refresh': 'Korrigierte Punkte des vorherigen Zyklus übernehmen',
    'serviceAuto.refreshConfirm':
      'Die Eingangsreferenz wird ersetzt. Manuelle Entscheidungen bleiben erhalten; automatische Zuteilung und Prüfung müssen erneut erfolgen.',
    'serviceAuto.input': 'Ausgangspunkte: {{cycle}}',
    'serviceAuto.completed':
      'Zugeteilt: {{assigned}}. Offene Dienste: {{gaps}}. Dienste vor dem Einfrieren prüfen.',
    'serviceAuto.loadFailed':
      'Der Zuteilungsstand konnte nicht geladen werden. Erneut versuchen, um die aktuellen Punkte zu laden.',
    'serviceAuto.frozen':
      'Dieser Zyklus ist eingefroren. Zum Bearbeiten unter Planungszyklen entsperren.',
    'serviceAuto.error.SCORE_INPUT_MISSING':
      'Ausgangspunkte fehlen. Den vorherigen Zyklus abschließen oder den geprüften Ausgangsstand importieren.',
    'serviceAuto.error.SCORE_PREDECESSOR_STALE':
      'Der vorherige Zyklus wurde geändert. Diesen abschließen und danach die Ausgangspunkte hier ausdrücklich aktualisieren.',
    'serviceAuto.error.UNSUPPORTED_AUTO_SCOPE':
      'Automatische Zuteilung verwendet reguläre Zweimonatszyklen. Dieser Zeitraum kann weiterhin manuell geplant werden.',
    'serviceAuto.error.SERVICE_SCOPE_MISSING':
      'Zuerst die Dienstpositionen hier unter Dienste einrichten und speichern.',
    'serviceAuto.error.EMPTY_AUTO_SCOPE':
      'Es gibt keine aktivierten Dienstpositionen zum Zuteilen.',
    'serviceAuto.error.AUTO_CONTEXT_STALE':
      'Die Planungsdaten wurden geändert. Den aktuellen Stand neu laden und vor einem erneuten Versuch prüfen.',
    'serviceAuto.error.AUTO_ASSIGNMENT_PENDING':
      'Ein vorheriger automatischer Durchlauf muss wiederhergestellt werden. Denselben Durchlauf vor weiteren Änderungen erneut versuchen.',
    'serviceAuto.error.SCORE_RUN_REQUIRED':
      'Vor der Prüfung dieses Zyklus erneut automatisch zuteilen.',
    'serviceAuto.error.AUTO_ASSIGNMENT_FOREIGN_OWNER':
      'Automatische Zuteilungen gehören zu einem überlappenden Zeitraum. Diesen vor einem neuen Durchlauf klären.',
    'serviceAuto.context': 'Zuteilungskontext anzeigen',
    'serviceAuto.score': 'Punkte',
    'serviceAuto.weight': 'Gewicht',
    'serviceAuto.manualEmpty': 'Manuell freigehalten',
    'serviceAuto.return': 'Wieder automatisch zuteilen lassen',
    'serviceAuto.conflict':
      'Diese Person hat an diesem Datum bereits eine Aufgabe. Nur bestätigen, wenn eine Doppelbelegung beabsichtigt ist.',
    'serviceAuto.confirmConflict': 'Zuteilung am selben Tag bestätigen',
    'serviceAuto.contextFailed':
      'Der Kandidatenkontext konnte nicht geladen werden. Vor einer Entscheidung erneut versuchen.',
    'serviceAuto.reason.INACTIVE': 'Inaktiv',
    'serviceAuto.reason.NOT_ELIGIBLE': 'Nicht geeignet',
    'serviceAuto.reason.DAY_INELIGIBLE': 'Für diesen Zusammenkunftstag nicht geeignet',
    'serviceAuto.reason.ABSENT': 'Abwesend oder nicht einzuplanen',
    'serviceAuto.reason.CONDITIONAL': 'Unter Vorbehalt; persönliche Rücksprache erforderlich',
    'serviceAuto.reason.SAME_DATE_CONFLICT': 'An diesem Datum bereits zugeteilt',
    'serviceAuto.available': 'Verfügbar',
    'serviceAuto.noCandidates': 'Keine Verkündiger als Kandidaten verfügbar.',
    'planning.step.manualGaps': 'Manuelle Lücken füllen',
    'planning.readiness.loading': 'Planbereitschaft wird geprüft…',
    'planning.readiness.notReviewed':
      'Prüfe die Bereitschaft, sobald die Zuteilungen abgeschlossen sind.',
    'planning.readiness.ready': 'Bereit zur Veröffentlichung',
    'planning.readiness.readyWithUnassigned': 'Bereit zum Einfrieren mit nicht zugeteilten Teilen',
    'planning.readiness.blocked': 'Dieser Plan enthält noch blockierende Zuteilungen',
    'planning.readiness.summary': '{{complete}} von {{total}} Zuteilungen vollständig.',
    'planning.issue.EMPTY_SCOPE': 'In diesem Umfang sind keine Planungszuteilungen gespeichert.',
    'planning.issue.SCORE_PREDECESSOR_STALE':
      'Punkte des vorherigen Zyklus wurden geändert. Ausgangspunkte aktualisieren, erneut automatisch zuteilen und neu prüfen.',
    'planning.issue.SCORE_RUN_REQUIRED':
      'Nach der Aktualisierung der Ausgangspunkte erneut automatisch zuteilen.',
    'planning.issue.SCORE_ASSIGNMENT_INVALID':
      'Eine gespeicherte Dienstzuteilung ist nicht mehr geeignet oder verfügbar. Vor dem Einfrieren klären.',
    'planning.issue.AUTO_ASSIGNMENT_PENDING':
      'Die automatische Zuteilung muss wiederhergestellt werden. Vor der Prüfung denselben Durchlauf erneut versuchen.',
    'planning.issue.MISSING_ASSIGNEE': 'Für eine Zuteilung fehlt noch ein Verkündiger.',
    'planning.issue.INVALID_STATUS': 'Eine Zuteilung hat einen nicht veröffentlichbaren Status.',
    'planning.issue.open': 'Öffnen',
    'planning.publication.none': 'Für diesen Umfang wurde noch keine Version veröffentlicht.',
    'planning.publication.revision': 'Veröffentlichte Version {{revision}}',
    'planning.publication.unpublishedChanges': 'Unveröffentlichte Änderungen',
    'planning.publication.review': 'Zuteilungen prüfen',
    'planning.publication.publish': 'Zuteilungsplan einfrieren',
    'planning.publication.publishing': 'Wird veröffentlicht…',
    'planning.publication.confirmTitle': 'Diesen Zuteilungsplan einfrieren?',
    'planning.publication.confirmBody':
      'Dadurch entsteht ein unveränderlicher Stand aller Zuteilungen im vollständigen Umfang. Zum späteren Ändern muss er entsperrt werden.',
    'planning.publication.success': 'Version {{revision}} wurde eingefroren.',
    'planning.publication.stale':
      'Der Entwurf wurde nach der Prüfung geändert. Prüfe die Bereitschaft vor der Veröffentlichung erneut.',
    'planning.publication.error':
      'Der Plan konnte nicht veröffentlicht werden. Dein Entwurf blieb unverändert; bitte versuche es erneut.',
    'planning.frozen.title': 'Plan eingefroren',
    'planning.unlock.action': 'Plan entsperren',
    'planning.unlock.confirmTitle': 'Diesen Plan entsperren?',
    'planning.unlock.confirmBody':
      'Die eingefrorene Version bleibt unverändert. Der aktuelle Entwurf kann wieder bearbeitet werden.',
    'planning.autoAssign.unavailable':
      'Automatische Zuteilung ist noch nicht verfügbar. Fülle verbleibende manuelle Lücken.',
    'servicePlanning.scopeTitle': 'Planungszeitraum auswählen',
    'servicePlanning.year': 'Jahr',
    'servicePlanning.period': 'Planungszeitraum',
    'servicePlanning.selectPeriod': 'Planungszeitraum auswählen',
    'servicePlanning.invalidPeriod':
      'Bitte ein gültiges Jahr und einen Planungszeitraum auswählen.',
    'servicePlanning.period1': 'Jan/Feb',
    'servicePlanning.period2': 'Mär/Apr',
    'servicePlanning.period3': 'Mai/Jun',
    'servicePlanning.period4': 'Jul/Aug',
    'servicePlanning.period5': 'Sep/Okt',
    'servicePlanning.period6': 'Nov/Dez',
    'servicePlanning.startDate': 'Startdatum',
    'servicePlanning.endDate': 'Enddatum',
    'servicePlanning.loadRange': 'Zuteilungen laden',
    'servicePlanning.invalidRange': 'Wähle ein Enddatum am oder nach dem Startdatum.',
    'servicePlanning.emptyTitle': 'Keine Dienstzuteilungen in diesem Bereich',
    'servicePlanning.emptyBody':
      'Füge das erste Datum und die erste Aufgabe hinzu, um diesen Bereich zu planen.',
    'servicePlanning.addRow': 'Zuteilungszeile hinzufügen',
    'servicePlanning.date': 'Datum',
    'servicePlanning.role': 'Dienstaufgabe',
    'servicePlanning.slot': 'Platz',
    'servicePlanning.publisher': 'Verkündiger',
    'servicePlanning.selectPublisher': 'Berechtigten Verkündiger wählen',
    'servicePlanning.save': 'Zuteilung speichern',
    'servicePlanning.unassign': 'Zuteilung aufheben',
    'servicePlanning.readOnly': 'Du hast Lesezugriff auf diesen Plan.',
    'servicePlanning.loadFailed': 'Die Dienstzuteilungen konnten nicht geladen werden.',
    'servicePlanning.noSlotsForMeeting': 'Für diese Zusammenkunft sind keine Dienste eingerichtet.',
    'servicePlanning.saveShort': 'Speichern',
    'servicePlanning.saveFailed': 'Die Zuteilung konnte nicht gespeichert werden.',
    'servicePlanning.selectionChanged':
      'Diese Person ist für den Dienst nicht mehr verfügbar. Bitte lade die Liste neu.',
    'servicePlanning.optionsLoading': 'Verfügbare Personen werden geladen…',
    'servicePlanning.optionsFailed':
      'Verfügbare Personen konnten nicht geladen werden. Erneut versuchen',
    'servicePlanning.noOptions': 'Für diesen Dienst sind keine Personen verfügbar.',
    'servicePlanning.lammTask': 'LuDZ',
    'servicePlanning.assistant': 'Partner',
    'servicePlanning.automaticSection': 'Automatische Zuteilung',
    'servicePlanning.sourceBusy':
      'Die Planung ist gerade beschäftigt. Bitte versuche erneut, diese Zuteilung zu speichern.',
    'midweekPlanning.title': 'LuDZ-Planung',
    'midweekPlanning.subtitle': 'LuDZ-Planung für einen Arbeitsheft-Zyklus.',
    'midweekPlanning.description':
      'Diese Kategorie lädt die Arbeitsheft-Daten für einen Planungszyklus. Die Zuweisung von Brüdern und Teilnehmern kann im nächsten Schritt ergänzt werden.',
    'midweekPlanning.loadCycle': 'Arbeitsheftdaten abrufen',
    'midweekPlanning.loadingCycle': 'Arbeitsheft wird geladen...',
    'midweekPlanning.contextTitle': 'Arbeitsheft',
    'midweekPlanning.importOptionsTitle': 'Importoptionen',
    'midweekPlanning.year': 'Jahr',
    'midweekPlanning.workbook': 'Arbeitsheft',
    'midweekPlanning.meetingWeekday': 'Wochentag',
    'midweekPlanning.weekendWeekday': 'Wochenende',
    'midweekPlanning.workbookOption1': '1 - Jan/Feb',
    'midweekPlanning.workbookOption2': '2 - Mär/Apr',
    'midweekPlanning.workbookOption3': '3 - Mai/Jun',
    'midweekPlanning.workbookOption4': '4 - Jul/Aug',
    'midweekPlanning.workbookOption5': '5 - Sep/Okt',
    'midweekPlanning.workbookOption6': '6 - Nov/Dez',
    'midweekPlanning.weekday.monday': 'Montag',
    'midweekPlanning.weekday.tuesday': 'Dienstag',
    'midweekPlanning.weekday.wednesday': 'Mittwoch',
    'midweekPlanning.weekday.thursday': 'Donnerstag',
    'midweekPlanning.weekday.friday': 'Freitag',
    'midweekPlanning.weekday.saturday': 'Samstag',
    'midweekPlanning.weekday.sunday': 'Sonntag',
    'midweekPlanning.loaded':
      'Arbeitsheft-Zyklus erfolgreich importiert. {{count}} Wochen wurden gespeichert.',
    'midweekPlanning.loadedAt': 'Zuletzt vorbereitete Aktion: {{time}}',
    'workbookReview.type.explainingBeliefs': 'Unsere Glaubensansichten erklären',
    'workbookReview.type.unknown': 'Unbekannte Art des Programmpunkts',
    'workbookReview.refreshSummary': 'Programmstruktur in {{count}} Wochen aktualisieren',
    'workbookReview.refreshIntro':
      'In diesem Zyklus ist noch niemand zugeteilt. Mit der Bestätigung wird die geprüfte Programmstruktur ersetzt. Wocheneinstellungen und veröffentlichte Pläne bleiben erhalten.',
    'workbookReview.refresh': 'Unzugeteiltes Programm aktualisieren',
    'workbookReview.structure.added': 'Programmpunkt hinzugefügt',
    'workbookReview.structure.removed': 'Programmpunkt entfernt',
    'workbookReview.structure.replaced': 'Programmpunkt ersetzt',
    'workbookReview.field.programOrder': 'Position im Programm',
    'workbookReview.field.sectionCode': 'Zusammenkunftsteil',
    'workbookReview.capacity':
      'Dieses Arbeitsheft ist für eine sichere Aktualisierung zu groß. Es wurden keine Änderungen übernommen.',
    'workbookReview.refreshFailed':
      'Die Aktualisierung konnte nicht abgeschlossen werden. Einzelne vollständige Wochen können bereits aktualisiert sein. Bitte die Arbeitsheftdaten erneut abrufen und die verbleibenden Änderungen prüfen.',
    'workbookReview.title': 'Arbeitsheft-Änderungen prüfen',
    'workbookReview.summary':
      '{{weeks}} Wochen geprüft; {{count}} Einträge enthalten Arbeitsheft-Änderungen.',
    'workbookReview.noChanges':
      'Keine Arbeitsheft-Änderungen. Gespeicherte Zuteilungen bleiben unverändert.',
    'workbookReview.preserve':
      'Zugeteilte Personen und Partner bleiben zugeteilt. Erst nach deiner Bestätigung werden diese Änderungen übernommen.',
    'workbookReview.blocked':
      'Diese Änderungen können nicht übernommen werden. Bestehende Einträge und Zuteilungen bleiben erhalten.',
    'workbookReview.conflict.added': 'Die Quelle enthält einen zusätzlichen Programmpunkt.',
    'workbookReview.conflict.removed': 'Ein bestehender Programmpunkt fehlt in der Quelle.',
    'workbookReview.conflict.identity':
      'Die Identität oder Reihenfolge des Programmpunkts hat sich geändert.',
    'workbookReview.conflict.assignment':
      'Eine gespeicherte Zuteilung ist mit dem geänderten Programmpunkt nicht vereinbar.',
    'workbookReview.empty': 'Keine Angabe',
    'workbookReview.week': 'Wochendetails',
    'workbookReview.before': 'Bisher',
    'workbookReview.after': 'Neu',
    'workbookReview.apply': 'Geprüfte Änderungen übernehmen',
    'workbookReview.applying': 'Änderungen werden übernommen…',
    'workbookReview.cancel': 'Prüfung schließen',
    'workbookReview.stale':
      'Das Arbeitsheft oder der gespeicherte Plan hat sich geändert. Lade das Arbeitsheft erneut, um die aktuellen Änderungen zu prüfen.',
    'workbookReview.updateConflict':
      'Die Änderung ist gesperrt. Prüfe den Zyklusstatus und lade das Arbeitsheft erneut.',
    'workbookReview.applied':
      'Arbeitsheft-Änderungen für {{count}} Einträge übernommen. Gespeicherte Zuteilungen wurden beibehalten.',
    'workbookReview.song.opening': 'Anfang',
    'workbookReview.song.middle': 'Mitte',
    'workbookReview.song.closing': 'Schluss',
    'workbookReview.field.weekStartDate': 'Wochenbeginn',
    'workbookReview.field.weekEndDate': 'Wochenende',
    'workbookReview.field.weekendMeetingDate': 'Zusammenkunft am Wochenende',
    'workbookReview.field.workbookWeekNumber': 'Arbeitsheftwoche',
    'workbookReview.field.language': 'Sprache der Quelle',
    'workbookReview.field.sourceUrl': 'Quelle',
    'workbookReview.field.jwLibraryUrl': 'JW-Library-Verweis',
    'workbookReview.field.songs': 'Lieder',
    'workbookReview.field.readingProgram': 'Bibelleseprogramm',
    'workbookReview.field.itemCount': 'Programmpunkte',
    'workbookReview.field.title': 'Titel',
    'workbookReview.field.titleClean': 'Titel des Programmpunkts',
    'workbookReview.field.number': 'Nummer des Programmpunkts',
    'workbookReview.field.durationMinutes': 'Dauer (Minuten)',
    'workbookReview.field.lesson': 'Lektionsverweis',
    'workbookReview.field.assignmentTypeCode': 'Art des Programmpunkts',
    'workbookReview.field.songNumber': 'Liednummer',
    'midweekPlanning.importSummaryTitle': 'Importierter Arbeitsheft-Zyklus',
    'midweekPlanning.importSummaryWeeks': '{{count}} Zusammenkunftswochen gespeichert.',
    'midweekPlanning.importSummaryRange': 'Datumsbereich: {{from}} bis {{to}}',
    'midweekPlanning.importedTitle': 'Arbeitsheft-Import abgeschlossen',
    'midweekPlanning.importedBody':
      'Arbeitsheft {{workbook}} für {{year}} ist jetzt mit {{count}} Wochen im Planungsspeicher verfügbar.',
    'midweekPlanning.errorDefault': 'Der Arbeitsheft-Import ist fehlgeschlagen.',
    'midweekPlanning.sourceError':
      'Die Arbeitsheft-Quelle konnte nicht geladen werden. Bitte versuche es später erneut.',
    'midweekPlanning.weeksTitle': 'Arbeitsheft-Wochen',
    'midweekPlanning.activeWorkbookLabel': 'Aktives Arbeitsheft: {{workbook}} {{year}}',
    'midweekPlanning.loadingWeeks': 'Gespeicherte Wochen werden geladen...',
    'midweekPlanning.weeksEmpty': 'Für dieses Arbeitsheft sind noch keine Wochen gespeichert.',
    'midweekPlanning.weekLabel': 'Woche {{number}}',
    'midweekPlanning.jwLibraryReference': 'JW Library',
    'midweekPlanning.jwLibraryReferenceLabel':
      'JW-Library-Referenz für Woche {{number}} (öffnet JW Library)',
    'midweekPlanning.jwWebsiteReference': 'jw.org',
    'midweekPlanning.jwWebsiteReferenceLabel':
      'jw.org-Referenz für Woche {{number}} (öffnet in einem neuen Tab)',
    'midweekPlanning.weekRange': '{{from}}-{{to}}',
    'midweekPlanning.weekendMeetingDateLabel': 'Wochenendzusammenkunft: {{date}}',
    'schedule.day': 'Tag',
    'schedule.time': 'Beginn',
    'schedule.duration': 'Dauer: {{minutes}} Minuten',
    'schedule.timezone':
      'Zeiten in {{timezone}}. Bestehende Dienste werden mit der Zusammenkunft verschoben.',
    'schedule.error.SCHEDULE_INVALID_DATE': 'Wähle ein gültiges Datum in dieser Woche.',
    'schedule.error.SCHEDULE_INVALID_DAY':
      'Wähle Montag–Freitag für die Wochentagszusammenkunft oder Samstag–Sonntag für die Wochenendzusammenkunft.',
    'schedule.error.SCHEDULE_INVALID_TIME':
      'Wähle eine gültige Ortszeit. Mehrdeutige oder fehlende Zeiten beim Zeitwechsel sind nicht verfügbar.',
    'schedule.error.SCHEDULE_INVALID_CYCLE':
      'Lade diesen Zyklus neu, bevor du seine Zeiten änderst.',
    'schedule.error.SCHEDULE_INVALID_REQUEST':
      'Lade die Woche neu, bevor du ihre Zeiten speicherst.',
    'schedule.error.SCHEDULE_STALE':
      'Die Woche oder ihre Dienste wurden geändert. Lade sie vor dem Speichern neu; dein Entwurf bleibt erhalten.',
    'schedule.error.SCHEDULE_FROZEN':
      'Entsperre den ausgewählten Plan und betroffene Dienstpläne vor dem Verschieben.',
    'schedule.error.SCHEDULE_SERVICE_PERMISSION':
      'Zum Verschieben bestehender Dienste brauchst du Schreibrechte für Dienste.',
    'schedule.error.SCHEDULE_ASSIGNEE_UNAVAILABLE':
      'Eine zugeteilte Person ist am neuen Datum nicht verfügbar. Passe ihre Zuteilung vor dem Verschieben an.',
    'schedule.error.SCHEDULE_SLOT_CONFLICT':
      'Am neuen Datum besteht bereits ein kollidierender Dienst. Löse den Konflikt vor dem Verschieben.',
    'schedule.error.SCHEDULE_RANGE_CONFLICT':
      'Das neue Datum liegt außerhalb eines gespeicherten Dienstzeitraums. Passe den Zeitraum vor dem Verschieben an.',
    'schedule.error.SCHEDULE_TOO_LARGE':
      'Diese Zusammenkunft hat zu viele verknüpfte Dienste für einen Speichervorgang. Wende dich an einen Administrator.',
    'schedule.error.SCHEDULE_DEFAULTS_UNAVAILABLE':
      'Die Standardzeiten konnten nicht geladen werden. Lade die Woche erneut.',
    'midweekPlanning.weekConfiguration.title': 'Wochenkonfiguration',
    'midweekPlanning.weekConfiguration.description':
      'Diese Versammlungswoche wird von LuDZ und Diensten gemeinsam genutzt. Änderungen erfolgen in den Planungszyklen.',
    'midweekPlanning.weekConfiguration.typeLabel': 'Konfiguration',
    'midweekPlanning.weekConfiguration.notesLabel': 'Notiz',
    'midweekPlanning.weekConfiguration.notesPlaceholder': 'Optionale Notiz für diese Woche',
    'midweekPlanning.weekConfiguration.sharedOwnerHint':
      'Gemeinsame Planungsdaten für LuDZ und Dienste.',
    'midweekPlanning.weekConfiguration.save': 'Wochenkonfiguration speichern',
    'midweekPlanning.weekConfiguration.saving': 'Wird gespeichert...',
    'midweekPlanning.weekConfiguration.saved': 'Wochenkonfiguration gespeichert: {{type}}',
    'midweekPlanning.weekConfiguration.loadFailed':
      'Wochenkonfiguration konnte nicht geladen werden',
    'midweekPlanning.weekConfiguration.saveFailed':
      'Wochenkonfiguration konnte nicht gespeichert werden',
    'midweekPlanning.weekConfiguration.weekdayEnabled': 'Wochentagszusammenkunft aktiv',
    'midweekPlanning.weekConfiguration.weekdayDisabled': 'Keine Wochentagszusammenkunft',
    'midweekPlanning.weekConfiguration.weekendEnabled': 'Wochenendzusammenkunft aktiv',
    'midweekPlanning.weekConfiguration.weekendDisabled': 'Keine Wochenendzusammenkunft',
    'midweekPlanning.weekConfiguration.noWeekdayMeetingHint':
      'Diese Woche ist als Woche ohne Wochentagszusammenkunft konfiguriert. LuDZ-Zuteilungen sind für diese Woche deaktiviert.',
    'midweekPlanning.section.tfgw': 'Schätze aus Gottes Wort',
    'midweekPlanning.section.ayttfm': 'Uns im Dienst verbessern',
    'midweekPlanning.section.lac': 'Unser Leben als Christ',
    'midweekPlanning.weekConfiguration.type.standard': 'Normale Woche',
    'midweekPlanning.weekConfiguration.type.circuit_overseer_visit': 'Besuch des Kreisaufsehers',
    'midweekPlanning.weekConfiguration.type.no_weekday_meeting': 'Keine Wochentagszusammenkunft',
    'midweekPlanning.weekConfiguration.type.no_weekend_meeting': 'Keine Wochenendzusammenkunft',
    'midweekPlanning.weekConfiguration.type.no_meetings': 'Keine Zusammenkünfte',
    'midweekPlanning.itemsStored': '{{count}} Programmpunkte',
    'midweekPlanning.loadingWeekDetail': 'Zusammenkunftsdetails werden geladen...',
    'midweekPlanning.loadWeeksFailed':
      'Die gespeicherten Arbeitsheft-Wochen konnten nicht geladen werden.',
    'midweekPlanning.loadWeekFailed':
      'Die ausgewählte Zusammenkunftswoche konnte nicht geladen werden.',
    'midweekPlanning.itemType.song': 'Lied',
    'midweekPlanning.itemType.prayer': 'Gebet',
    'midweekPlanning.itemType.chairman': 'Vorsitz',
    'midweekPlanning.durationMinutes': '{{count}} Min.',
    'midweekPlanning.lessonLabel': 'Lektion: {{lesson}}',
    'midweekPlanning.assigned': 'Zugewiesen',
    'midweekPlanning.unassigned': 'Nicht zugewiesen',
    'midweekPlanning.notAssignable': 'N/A',
    'midweekPlanning.assignmentLabel': 'Zuteilung',
    'midweekPlanning.mainAssigneeLabel': 'Zuteilung',
    'midweekPlanning.candidateWithCycleCount': '{{name}} ({{count}})',
    'midweekPlanning.conditionalAvailability.title': 'Unter Vorbehalt',
    'midweekPlanning.conditionalAvailability.contact':
      'Bitte vor der Einteilung Rücksprache halten.',
    'midweekPlanning.partnerLabel': 'Partner',
    'midweekPlanning.assistantLabel': 'Partner',
    'midweekPlanning.noAssigneeOption': 'Nicht zugewiesen',
    'midweekPlanning.noAssistantOption': 'Kein Partner',
    'midweekPlanning.noPartnerOption': 'Kein Partner',
    'midweekPlanning.noAssignmentSelected': 'Noch keine Zuteilung ausgewählt.',
    'midweekPlanning.noEligibleCandidates': 'Keine verfügbaren geeigneten Personen.',
    'midweekPlanning.naOption': 'N/A',
    'midweekPlanning.savedAssignment': 'Gespeicherte Zuteilung: {{assignee}}',
    'midweekPlanning.savedAssignmentWithPartner':
      'Gespeicherte Zuteilung: {{assignee}} · Partner: {{assistant}}',
    'midweekPlanning.unknownPublisher': 'Unbekannter Verkündiger',
    'midweekPlanning.saveAssignment': 'Zuteilung speichern',
    'midweekPlanning.savingAssignment': 'Wird gespeichert...',
    'midweekPlanning.saveAssignmentFailed':
      'Die Zuteilung für den Programmpunkt konnte nicht gespeichert werden.',
    'midweekPlanning.loadingPublishers': 'Verkündiger werden geladen...',
    'midweekPlanning.loadPublishersFailed': 'Die aktiven Verkündiger konnten nicht geladen werden.',
    'midweekPlanning.loadingCandidates': 'Kandidaten werden geladen...',
    'midweekPlanning.loadCandidatesFailed': 'Die Kandidatenliste konnte nicht geladen werden.',
    'midweekPlanning.decisionContext.title': 'Zuteilungskontext für {{name}}',
    'midweekPlanning.decisionContext.previousAssignments': 'Letzte LuDZ-Zuteilungen',
    'midweekPlanning.decisionContext.thisWeek': 'Diese Woche',
    'midweekPlanning.decisionContext.assignmentDescription': '{{date}} · {{description}}',
    'midweekPlanning.decisionContext.loading': 'Zuteilungskontext wird geladen...',
    'midweekPlanning.decisionContext.noPreviousAssignments': 'Keine Zuteilung erfasst.',
    'midweekPlanning.decisionContext.loadFailed': 'Zuteilungskontext konnte nicht geladen werden.',
    'midweekPlanning.nextStepTitle': 'Geplanter nächster Schritt',
    'midweekPlanning.nextStepBody':
      'Der Arbeitsbuch-Import lädt und speichert jetzt direkt einen kompletten Zyklus. Als Nächstes kommen Wochenansicht und Zuteilungsschirme für jeden Programmpunkt.',
    'deepLink.validating': 'Zugriffslink wird geprüft...',
    'deepLink.error.noToken': 'Kein Authentifizierungs-Token übergeben',
    'deepLink.error.invalid': 'Ungültiger oder abgelaufener Link',
    'deepLink.error.expired': 'Dieser Zugriffslink ist abgelaufen. Bitte fordere einen neuen an.',
    'deepLink.error.notFound':
      'Ungültiger Zugriffslink. Bitte prüfe den Link und versuche es erneut.',
    'deepLink.error.invalidResponse': 'Ungültige Antwort vom Server',
    'deepLink.error.authFailed': 'Authentifizierung fehlgeschlagen',
    'deepLink.invalidTitle': 'Zugriffslink ungültig',
    'deepLink.contactAdmin':
      'Bitte kontaktiere deinen Versammlungsadministrator für einen neuen Zugriffslink.',
    'deepLink.welcome': 'Willkommen, {{name}}',
    'deepLink.portalTitle': 'Verkündiger-Portal',
    'deepLink.manageAbsences': 'Deine Abwesenheiten verwalten',
    'deepLink.manageSubtitle': 'Füge bevorstehende Abwesenheiten hinzu und verwalte sie',
    'deepLink.addAbsence': 'Abwesenheit hinzufügen',
    'deepLink.signOut': 'Abmelden',
    'deepLink.howToUse': 'So verwendest du dieses Portal',
    'deepLink.howToUse.1':
      'Verwende „Abwesenheit hinzufügen“, um bevorstehende Nichtverfügbarkeit mitzuteilen',
    'deepLink.howToUse.2': 'Du kannst bevorstehende Abwesenheiten bearbeiten oder löschen',
    'deepLink.howToUse.3': 'Vergangene Abwesenheiten können nicht geändert werden',
    'deepLink.howToUse.4':
      'Deine Abwesenheiten werden automatisch mit dem Versammlungskalender synchronisiert',
    'publisherAbsenceForm.addTitle': 'Neue Abwesenheit hinzufügen',
    'publisherAbsenceForm.editTitle': 'Abwesenheit bearbeiten',
    'publisherAbsenceForm.endDateOptional': 'Enddatum (optional)',
    'publisherAbsenceForm.endDateHint': 'Für eine eintägige Abwesenheit leer lassen',
    'publisherAbsenceForm.adding': 'Wird hinzugefügt...',
    'publisherAbsenceForm.updating': 'Wird aktualisiert...',
    'publisherAbsenceForm.update': 'Abwesenheit aktualisieren',
    'publisherAbsenceForm.add': 'Abwesenheit hinzufügen',
    'publisherAbsenceForm.validation.startRequired': 'Startdatum ist erforderlich',
    'publisherAbsenceForm.validation.endAfter': 'Das Enddatum muss nach dem Startdatum liegen',
    'publisherAbsenceForm.validation.pastLimit':
      'Das Startdatum darf nicht mehr als 1 Woche in der Vergangenheit liegen',
    'publisherAbsenceForm.validation.futureLimit':
      'Das Startdatum darf nicht mehr als 6 Monate in der Zukunft liegen',
    'publisherHistory.title': 'Deine Abwesenheiten',
    'publisherHistory.filter': 'Filter',
    'publisherHistory.all': 'Alle',
    'publisherHistory.upcoming': 'Bevorstehend',
    'publisherHistory.past': 'Vergangen',
    'publisherHistory.loading': 'Deine Abwesenheiten werden geladen...',
    'publisherHistory.noMatch': 'Keine Abwesenheiten entsprechen deinem Filter',
    'publisherHistory.empty': 'Keine Abwesenheiten gefunden',
    'publisherHistory.emptyHint':
      'Verwende „Abwesenheit hinzufügen“, um deine erste Abwesenheit zu erstellen.',
    'publisherHistory.createdAt': 'Erstellt {{date}}',
    'publisherHistory.cannotEditPast': 'Vergangene Abwesenheiten können nicht bearbeitet werden',
    'notFound.title': 'Seite nicht gefunden',
    'notFound.subtitle': 'Entschuldigung, wir konnten die gesuchte Seite nicht finden.',
    'notFound.goHome': 'Zur Startseite',
  },
};
